import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useContext,
  useRef,
  useMemo
} from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { CardList } from "../Card";
import Table from "../Table";
import Modal from "../Modal";
import EnableFeatureModal from "../EnableFeatureModal";
import { COMPETITIVE_EXAM_LIBRARY_FEATURE } from "../../../config/featurePromotions";
import Loader from "../Loader";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../utils/swalAlert";
import { checkUserPromptScoreMatch } from "../../../utils/userPromptUtils";
import certificateService from "../../../services/certificateService";
import MobileCapture from "../MobileCapture";
import ModalTableInput from "../ModalTableInput";
import FileUploadSection from "../FileUploadSection";
import ReactMarkdown from "react-markdown";
import QuestionDiagramResizeModal from "../QuestionDiagramResizeModal";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import stringSimilarity from "string-similarity";

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (
    !processed.includes("$") &&
    !processed.includes("\\(") &&
    !processed.includes("\\[")
  ) {
    const words = [];
    let current = "";
    let depth = 0;
    for (let i = 0; i < processed.length; i++) {
      const c = processed[i];
      if (c === "{" || c === "(") depth++;
      else if (c === "}" || c === ")") depth = Math.max(0, depth - 1);

      if (c === " " && depth === 0) {
        if (current) words.push(current);
        current = "";
      } else {
        current += c;
      }
    }
    if (current) words.push(current);

    processed = words
      .map((word) => {
        if (/(\\[a-zA-Z]+|\^|_)/.test(word)) {
          return `$${word}$`;
        }
        return word;
      })
      .join(" ");
  }
  processed = processed.replace(
    /(^|[^\\])\$([^$]+)\$/g,
    (match, before, math) => {
      if (math.includes("\\displaystyle")) return match;
      return `${before}$\\displaystyle ${math}$`;
    }
  );
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const roundTo2 = (val) => {
  if (val == null || val === "") return "";
  const num = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(num)) return val;
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const SPAN_COMPONENTS = { p: "span" };
const REMARK_PLUGINS = [remarkMath, remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [
  [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
];

const LazyMarkdown = React.memo(({ children, components }) => (
  <ReactMarkdown
    children={children}
    remarkPlugins={REMARK_PLUGINS}
    rehypePlugins={REHYPE_PLUGINS}
    components={components}
  />
));

const ManageTestUI = forwardRef(
  ({ classId, role = "instructor", defaultTab, classDetails }, ref) => {
  const { user } = useContext(AuthContext);
  const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

  const [selectedClass, setSelectedClass] = useState(null);

  const fetchSelectedClass = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get/${classId}`,
        { credentials: "include" },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch class");
      const data = await response.json();
      setSelectedClass(data);
    } catch (err) {
      swalError(err.message);
    }
  }, [classId, serverUrl]);

  useEffect(() => {
    if (classId) {
      fetchSelectedClass();
    }
  }, [classId, fetchSelectedClass]);

  const [tests, setTests] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGradingTypeModalOpen, setIsGradingTypeModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isHomeworkPath =
    defaultTab === "homeworks" ||
    location.pathname.endsWith("/homeworks") ||
    location.pathname.endsWith("/homework") ||
    searchParams.get("tab") === "homeworks";

  const [activeTab, setActiveTab] = useState(
    isHomeworkPath ? "homeworks" : defaultTab || "tests"
  );

  useEffect(() => {
    const isHw =
      defaultTab === "homeworks" ||
      location.pathname.endsWith("/homeworks") ||
      location.pathname.endsWith("/homework") ||
      searchParams.get("tab") === "homeworks";
    setActiveTab(isHw ? "homeworks" : defaultTab || "tests");
  }, [defaultTab, location.pathname, searchParams]);

  useEffect(() => {
    if (
      role === "instructor" &&
      classId &&
      searchParams.get("tab") === "homeworks" &&
      !location.pathname.includes("/homeworks")
    ) {
      navigate(`/instructor/manage-class/${classId}/homeworks`, { replace: true });
    }
  }, [role, classId, searchParams, location.pathname, navigate]);
  const [isCreateHomeworkModalOpen, setIsCreateHomeworkModalOpen] = useState(false);
  const [newHomework, setNewHomework] = useState({
    homeworkName: "",
    dueDate: "",
    status: "Ready",
    testPaperURL: null,
    capturedFile: null,
    answerKeyFile: null,
    answerKeyCapturedFile: null,
  });
  const [isEditHomeworkModalOpen, setIsEditHomeworkModalOpen] = useState(false);
  const [editHomework, setEditHomework] = useState({
    id: "",
    homeworkName: "",
    dueDate: "",
    initialDueDate: "",
    status: "Ready",
    initialStatus: "Ready",
    testPaperURL: null,
    capturedFile: null,
    removeTestPaper: false,
    originalTestPaperURL: null,
    originalTestPaperFileName: null,
    answerKeyFile: null,
    answerKeyCapturedFile: null,
    removeAnswerKey: false,
    originalAnswerKeysFileURL: null,
    originalAnswerKeysFileName: null,
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "homeworks") {
          next.set("tab", "homeworks");
        } else {
          next.delete("tab");
        }
        return next;
      },
      { replace: true }
    );
  };

  const [newTest, setNewTest] = useState({
    testType: null,
    testName: "",
    testPaperURL: null,
    maxScore: "",
    rubricURL: null,
    scheduledDate: "",
    dueDate: "",
    duration: "",
    capturedFile: null,
    isExternal: false,
    isManualEntryQuestions: false,
    manualEntryQuestions: [],
    isOnlineExamination: false,
    onlineExamQuestions: [],
    omrTemplateId: null,
    gradingPolicy: null,
    answerKeyFile: null,
    answerKeyCapturedFile: null,
    resultsAvailableImmediately: false,
    isCalculatorAllowed: false,
    verifyAnswerPaperOnUpload: false,
    standardizedTestId: null,
    certificateTemplateId: null,
    annotationPrintingOffsetConfigId: null,
    isAdvancedOnlineExam: false,
    advancedOnlineExamOptions: {
      videoProctoring: false,
      navigationBackToQuestion: true,
      shuffleQuestions: false,
      shuffleOptions: false,
      isTimedExam: false,
    },
  });

  // Update editTest state to include answerKeyFile (around line 51)
  const [editTest, setEditTest] = useState({
    id: "",
    testType: null,
    testName: "",
    maxScore: "",
    testPaperURL: null,
    rubricId: null,
    scheduledDate: "",
    dueDate: "",
    duration: "",
    removeTestPaper: false,
    capturedFile: null,
    isExternal: false,
    isManualEntryQuestions: false,
    manualEntryQuestions: [],
    isOnlineExamination: false,
    onlineExamQuestions: [],
    omrTemplateId: null,
    gradingPolicy: null,
    original: {},
    answerKeyFile: null,
    answerKeyCapturedFile: null,
    removeAnswerKey: false,
    resultsAvailableImmediately: false,
    isCalculatorAllowed: false,
    verifyAnswerPaperOnUpload: false,
    standardizedTestId: null,
    certificateTemplateId: null,
    annotationPrintingOffsetConfigId: null,
    isAdvancedOnlineExam: false,
    advancedOnlineExamOptions: {
      videoProctoring: false,
      navigationBackToQuestion: true,
      shuffleQuestions: false,
      shuffleOptions: false,
      isTimedExam: false,
    },
  });

  const [initialEditTest, setInitialEditTest] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingTestId, setEditingTestId] = useState(null);
  const [deletingTests, setDeletingTests] = useState(new Set());
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rubrics, setRubrics] = useState([]);
  const [previewText, setPreviewText] = useState("");
  const [isPreviewTextOpen, setIsPreviewTextOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureFor, setCaptureFor] = useState(null);
  const [syncingTests, setSyncingTests] = useState(new Set());
  const [testHasProgram, setTestHasProgram] = useState(false);
  const [courseOutcomes, setCourseOutcomes] = useState([]);
  const [bloomsLevelOptions, setBloomsLevelOptions] = useState([]);
  const [omrTemplateOptions, setOmrTemplateOptions] = useState([]);
  const [standardizedTestOptions, setStandardizedTestOptions] = useState([]);
  const [certificateTemplateOptions, setCertificateTemplateOptions] = useState([]);
  // Entries for standardized-test-mode question generation (one row per subject)
  const [stdTestEntries, setStdTestEntries] = useState([
    {
      subject: "",
      topic: "",
      difficulty: "",
      questionType: "MCQ",
      questionCount: 5,
    },
  ]);

  const [generatedTestPaperBlob, setGeneratedTestPaperBlob] = useState(null);
  const [generatedAnswerKeyBlob, setGeneratedAnswerKeyBlob] = useState(null);
  const [generatedSolutionsBlob, setGeneratedSolutionsBlob] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadModalTestId, setDownloadModalTestId] = useState(null);
  const [downloadModalTestName, setDownloadModalTestName] = useState("");
  const [downloadModalAction, setDownloadModalAction] = useState("navigate");

  // Online Exam states
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [availableQuestionBanks, setAvailableQuestionBanks] = useState([]);
  const [isMcqPickerOpen, setIsMcqPickerOpen] = useState(false);
  const [mcqSearchTerm, setMcqSearchTerm] = useState("");
  const [mcqFilterQB, setMcqFilterQB] = useState("");
  const [mcqFilterTopic, setMcqFilterTopic] = useState("");
  const [mcqFilterDifficulty, setMcqFilterDifficulty] = useState("");
  const [mcqSelectedIds, setMcqSelectedIds] = useState(new Set());
  const [isMcqLoading, setIsMcqLoading] = useState(false);
  const [isCustomQuestionOpen, setIsCustomQuestionOpen] = useState(false);
  const [cropQuestion, setCropQuestion] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const handleDiagramSaveSuccess = useCallback(
    (questionId, newDiagramFileURL, newBoundingBox) => {
      if (cropQuestion && cropQuestion.isEditMode) {
        setEditTest((prev) => {
          const updatedQuestions = (prev.onlineExamQuestions || []).map((q) => {
            if ((q._id || q.questionId || q._sourceId) === questionId) {
              return {
                ...q,
                diagramFileURL: newDiagramFileURL,
                diagramBoundingBox: newBoundingBox,
              };
            }
            return q;
          });
          return { ...prev, onlineExamQuestions: updatedQuestions };
        });
      } else {
        setNewTest((prev) => {
          const updatedQuestions = (prev.onlineExamQuestions || []).map((q) => {
            if ((q._id || q.questionId || q._sourceId) === questionId) {
              return {
                ...q,
                diagramFileURL: newDiagramFileURL,
                diagramBoundingBox: newBoundingBox,
              };
            }
            return q;
          });
          return { ...prev, onlineExamQuestions: updatedQuestions };
        });
      }
    },
    [cropQuestion],
  );
  const [customQuestion, setCustomQuestion] = useState({
    questionText: "",
    options: ["", "", "", ""],
    correctOptions: "",
    maxScore: 1,
    courseOutcome: "",
    bloomsLevel: "",
  });
  const [mcqPickerFor, setMcqPickerFor] = useState("create"); // "create" or "edit"

  // Advanced Online Exam: subject tab for question grouping
  const [activeSubjectTab, setActiveSubjectTab] = useState("All");

  // Generate Questions modal states
  const [isGenerateQuestionsOpen, setIsGenerateQuestionsOpen] = useState(false);
  const [generateQForm, setGenerateQForm] = useState({
    topic: "",
    difficulty: [],
    questionCount: 5,
  });
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [generateQProgress, setGenerateQProgress] = useState("");
  const [generateQFor, setGenerateQFor] = useState("create"); // "create" or "edit"

  // Extract from PDF states
  const [isExtractPdfOpen, setIsExtractPdfOpen] = useState(false);
  const [extractPdfForm, setExtractPdfForm] = useState({
    questionBankName: "",
    sourceTestPaper: null,
    answerKeysFile: null,
    customInstructions: "",
    numberOfQs: "",
  });
  const [isExtractAnswerKeyModalOpen, setIsExtractAnswerKeyModalOpen] = useState(false);
  const [extractedAnswerKeys, setExtractedAnswerKeys] = useState([]);
  const [isExtractingAnswerKey, setIsExtractingAnswerKey] = useState(false);
  const [answerKeyPage, setAnswerKeyPage] = useState(1);
  const [isAnswerKeyTransposed, setIsAnswerKeyTransposed] = useState(false);
  const [answerKeyPreviewUrl, setAnswerKeyPreviewUrl] = useState(null);
  const [isExtractingPdf, setIsExtractingPdf] = useState(false);
  const [extractPdfProgress, setExtractPdfProgress] = useState("");
  const [extractPdfFor, setExtractPdfFor] = useState("create"); // "create" or "edit"

  // ── Library Bank states (Online Exam) ──
  const [, setClassData] = useState(null);
  const [isLibraryBankModalOpen, setIsLibraryBankModalOpen] = useState(false);
  const [isEnableFeatureModalOpen, setIsEnableFeatureModalOpen] = useState(false);
  const [isFetchingLibraryQuestions, setIsFetchingLibraryQuestions] =
    useState(false);
  const [libraryExamFor, setLibraryExamFor] = useState("create"); // "create" or "edit"
  const [libraryQuestionsLoaded, setLibraryQuestionsLoaded] = useState(false);
  const [modalStandardizedTestId, setModalStandardizedTestId] = useState(null);
  // New: Subject-tab + selection-based states
  const [libraryActiveSubject, setLibraryActiveSubject] = useState(null);
  const [libraryFilters, setLibraryFilters] = useState({
    topic: [],
    subTopic: [],
    type: [],
    difficulty: [],
  });
  const [librarySelectedQuestions, setLibrarySelectedQuestions] = useState(
    new Map(),
  );
  const [librarySearchTerm, setLibrarySearchTerm] = useState("");
  const [libraryDebouncedSearchTerm, setLibraryDebouncedSearchTerm] =
    useState("");
  const [libraryTopicPage, setLibraryTopicPage] = useState(1);
  const [libraryTopicTotalPages, setLibraryTopicTotalPages] = useState(1);
  const [libraryTopicTotalCount, setLibraryTopicTotalCount] = useState(0);
  const [libraryTopicQuestions, setLibraryTopicQuestions] = useState([]);
  const [isFetchingTopicQuestions, setIsFetchingTopicQuestions] =
    useState(false);
  const [libraryRandomCount, setLibraryRandomCount] = useState("");
  const [lastPickedRandomCount, setLastPickedRandomCount] = useState(null);
  const [isRandomSelecting, setIsRandomSelecting] = useState(false);
  const [libraryRandomSortedPool, setLibraryRandomSortedPool] = useState(null);
  const [librarySolutionModalData, setLibrarySolutionModalData] = useState(null);

  // New states for revamped Question Selection & Auto-Pick
  const [isAutoPickPanelOpen, setIsAutoPickPanelOpen] = useState(false);
  const [libraryAutoPickedIds, setLibraryAutoPickedIds] = useState(new Set());
  const [isSelectingAllTopic, setIsSelectingAllTopic] = useState(false);

  // Caching states to prevent re-fetching and retain state when switching subjects
  const [librarySubjectStates, setLibrarySubjectStates] = useState({});
  const [subjectQuestionsCache, setSubjectQuestionsCache] = useState({});

  useEffect(() => {
    const handler = setTimeout(() => {
      setLibraryDebouncedSearchTerm(librarySearchTerm);
      setLibraryTopicPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [librarySearchTerm]);

  useEffect(() => {
    setLibraryRandomCount("");
    setLastPickedRandomCount(null);
    setIsRandomSelecting(false);
    setLibraryRandomSortedPool(null);
    setIsAutoPickPanelOpen(false);
    setLibraryAutoPickedIds(new Set());
  }, [isLibraryBankModalOpen]);

  useEffect(() => {
    setLibraryRandomCount("");
    setLastPickedRandomCount(null);
    setLibraryRandomSortedPool(null);
    setLibraryAutoPickedIds(new Set());
  }, [
    libraryActiveSubject,
    libraryFilters.topic,
    libraryFilters.subTopic,
    libraryFilters.type,
    libraryFilters.difficulty,
    libraryDebouncedSearchTerm,
  ]);

  const [allLibraries, setAllLibraries] = useState([]);
  const [libraryMetadataCache, setLibraryMetadataCache] = useState({});
  const [fetchingMetadataIds, setFetchingMetadataIds] = useState(new Set());

  const [userPromptProgress, setUserPromptProgress] = useState("");
  const [userPromptPhase, setUserPromptPhase] = useState("");
  const [testPaperChanged, setTestPaperChanged] = useState(false);
  const [generateAIPrompt, setGenerateAIPrompt] = useState(true);

  const [showNewRubricForm, setShowNewRubricForm] = useState(false);
  const [newRubricData, setNewRubricData] = useState({
    name: "",
    file: null,
    text: "",
    capturedFile: null,
  });
  const [instituteData, setInstituteData] = useState(null);
  const isK12School = instituteData?.category === "K12_SCHOOL";
  const isLanguageInst =
    instituteData?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  const isAnnotationMode = useMemo(() => {
    const feedbackMode =
      instituteData?.feedbackMode ||
      user?.institute?.feedbackMode ||
      user?.feedbackMode;
    const category =
      instituteData?.category ||
      user?.institute?.category ||
      user?.category;

    const isMatch = (val) =>
      val === "ANNOTATION_ONLY" ||
      val === "ANNOTATION_AND_APP_FEEDBACK";

    return Boolean(isMatch(feedbackMode) || isMatch(category));
  }, [instituteData, user]);
  const [selectedQuizSkills, setSelectedQuizSkills] = useState([]);
  const [selectedQuizFormats, setSelectedQuizFormats] = useState([]);

  const isHomeworkGradingAllowed = Boolean(
    instituteData?.allowHomeworkGrading ||
    user?.institute?.allowHomeworkGrading ||
    user?.allowHomeworkGrading
  );

  const checkIsHomeworkTest = useCallback((test) => {
    return Boolean(
      test?.isHomeworkTest === true ||
      test?.isHomeworkTest === "true"
    );
  }, []);

  const displayedTests = useMemo(() => {
    return tests.filter((test) => {
      const isHomework = checkIsHomeworkTest(test);
      return activeTab === "homeworks" ? isHomework : !isHomework;
    });
  }, [tests, activeTab, checkIsHomeworkTest]);


  const progressIndicatorRef = useRef(null);

  useEffect(() => {
    if (userPromptProgress && progressIndicatorRef.current) {
      progressIndicatorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [userPromptProgress, userPromptPhase]);

  // Utility functions
  const convertToUTC = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return new Date(date.getTime()).toISOString();
  }, []);

  const convertToLocal = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString();
  }, []);

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return new Date(date.getTime()).toLocaleString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  }, []);

  // Expose openModal method to parent via ref
  const handleOpenCreateFlow = useCallback(() => {
    const hasMultipleTypes =
      instituteData?.isManualGradingAllowed ||
      instituteData?.isOnlineExamAllowed;
    if (hasMultipleTypes) {
      setIsGradingTypeModalOpen(true);
    } else {
      // Only AI Grading available, open create modal directly
      setNewTest((prev) => ({
        ...prev,
        isManualEntryQuestions: false,
        isOnlineExamination: false,
      }));
      setIsCreateModalOpen(true);
    }
  }, [instituteData]);

  useImperativeHandle(ref, () => ({
    openModal: handleOpenCreateFlow,
  }));

  // Fetch Test Type options from LOV API
  const [testTypeOptions, setTestTypeOptions] = useState([]);
  const [difficultyOptions, setDifficultyOptions] = useState([]);

  const fetchTestTypes = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${user.instituteId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch Institute Data");

      const instituteData = await response.json();
      setInstituteData(instituteData);

      if (!response.ok)
        throw new Error(
          instituteData.message || "Failed to fetch institute data",
        );
      if (instituteData.testTypes && instituteData.testTypes.length > 0) {
        setTestTypeOptions(
          instituteData.testTypes.map((item) => ({
            value: item,
            label: item,
          })),
        );
      } else if (
        instituteData?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
        user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
        user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE"
      ) {
        setTestTypeOptions([
          { value: "Mid-Term", label: "Mid-Term Exam" },
          { value: "End-Term", label: "End-Term Exam" },
          { value: "Quiz", label: "Quiz" },
        ]);
      }

      const bloomsOptions =
        instituteData?.bloomsLevels?.map((item) => ({
          value: item,
          label: item,
        })) || [];
      setBloomsLevelOptions(bloomsOptions);
    } catch (err) {
      swalError(err.message);
    }
  }, [serverUrl, user]);

  // Fetch class data for Test: returns true when a program exists for the course
  const fetchTestPrograms = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get/${classId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return false;
      if (!response.ok) throw new Error("Failed to fetch class data");
      const classData = await response.json();
      const { courseId } = classData;

      // Only call course API when courseId is present and not the string 'undefined'
      if (courseId && courseId !== "undefined") {
        const courseResponse = await fetchWithAuth(
          `${serverUrl}/api/course/get/${courseId}`,
          {
            credentials: "include",
          },
        );
        if (!courseResponse) return false;
        if (!courseResponse.ok) throw new Error("Failed to fetch course data");
        const courseData = await courseResponse.json();
        const { programId } = courseData;
        // check programId exists or not
        if (programId) {
          setTestHasProgram(true);
          return true;
        } else {
          setTestHasProgram(false);
          return false;
        }
      } else {
        // No valid courseId
        setTestHasProgram(false);
        return false;
      }
    } catch (err) {
      swalError(err.message);
      return false;
    }
  }, [serverUrl, classId]);

  const fetchQBDifficultyLOVs = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/QB-difficulty`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch difficulty options");
      }
      const optionsWithLabels = data.map((item) => {
        return { ...item, label: item.value };
      });
      setDifficultyOptions(optionsWithLabels);
    } catch (err) {
      swalError(err.message);
    }
  }, [serverUrl]);

  const fetchOmrTemplateLOVs = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/omr-template/get-all`,
        { credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch OMR templates");
      }
      const options = (data.data || data).map((item) => ({
        value: item._id,
        label: item.templateName,
        templateURL: item.templateURL,
        templateId: item.templateId,
        standardizedTestId:
          typeof item.standardizedTestId === "object"
            ? item.standardizedTestId?._id
            : item.standardizedTestId || null,
      }));
      setOmrTemplateOptions(options);
    } catch (err) {
      console.error("Error fetching OMR templates:", err);
    }
  }, [serverUrl]);

  const fetchStandardizedTestOptions = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/standardized-test/get-all`,
        { credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch Exam Template");
      }
      const options = (data.data || data).map((item) => ({
        value: item._id,
        label: item.templateName,
        templateId: item.templateId,
        subjects: item.subjects || [],
      }));
      setStandardizedTestOptions(options);
    } catch (err) {
      console.error("Error fetching Exam Templates:", err);
    }
  }, [serverUrl]);

  const hasStandardizedTemplates = useMemo(() => {
    const templateIds =
      instituteData?.standardizedTestTemplateIds ||
      instituteData?.standardizedTestTemplateId ||
      user?.institute?.standardizedTestTemplateIds ||
      user?.institute?.standardizedTestTemplateId ||
      [];
    return Array.isArray(templateIds)
      ? templateIds.filter(Boolean).length > 0
      : Boolean(templateIds);
  }, [instituteData, user]);

  const filteredStandardizedTestOptions = useMemo(() => {
    if (
      !instituteData?.standardizedTestTemplateIds ||
      instituteData.standardizedTestTemplateIds.length === 0
    ) {
      return standardizedTestOptions;
    }
    const allowedIds = new Set(
      instituteData.standardizedTestTemplateIds.map((t) =>
        typeof t === "object" ? t?._id?.toString() : t?.toString(),
      ),
    );
    return standardizedTestOptions.filter((opt) =>
      allowedIds.has(opt.value?.toString()),
    );
  }, [standardizedTestOptions, instituteData]);

  const filteredOmrTemplateOptions = useMemo(() => {
    return omrTemplateOptions;
  }, [omrTemplateOptions]);

  const annotationPrintingOffsetOptions = useMemo(() => {
    if (
      !isAnnotationMode ||
      !instituteData?.annotationPrintingOffsetConfig ||
      !Array.isArray(instituteData.annotationPrintingOffsetConfig)
    ) {
      return [];
    }
    return instituteData.annotationPrintingOffsetConfig
      .filter((cfg) => cfg && (cfg._id || cfg.name))
      .map((cfg) => ({
        value: cfg._id,
        label: `${cfg.name || "Default Config"} (Top: ${cfg.topOffset ?? 0} cm, Right: ${cfg.rightOffset ?? 0} cm)`,
      }));
  }, [instituteData, isAnnotationMode]);

  const fetchCertificateTemplates = useCallback(async () => {
    try {
      const data = await certificateService.getAllCertificateTemplates();
      const list = Array.isArray(data) ? data : data?.data || [];
      const options = list
        .filter((item) => item.isActive !== false)
        .map((item) => ({
          value: item._id,
          label: item.name,
          templateImageURL: item.templateImageURL,
        }));
      setCertificateTemplateOptions(options);
    } catch (err) {
      console.error("Error fetching certificate templates:", err);
    }
  }, []);

  useEffect(() => {
    fetchTestTypes();
    fetchTestPrograms();
    fetchQBDifficultyLOVs();
    fetchStandardizedTestOptions();
    fetchOmrTemplateLOVs();
    fetchCertificateTemplates();
  }, [
    fetchTestPrograms,
    fetchTestTypes,
    fetchQBDifficultyLOVs,
    fetchStandardizedTestOptions,
    fetchOmrTemplateLOVs,
    fetchCertificateTemplates,
  ]);

  // Fetch Tests from API
  const fetchTests = useCallback(
    async (isSilent = false) => {
      try {
        if (!isSilent) setLoading(true);
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get-all/${classId}`,
          {
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch tests");
        const data = await response.json();
        const nonPlacementData = Array.isArray(data)
          ? data.filter(
            (test) => !test.isLevelPlacementTest && test.testType !== "Level Test"
          )
          : [];
        const enhancedData = nonPlacementData.map((test) => ({
          ...test,
          formattedScheduledDate: userDate(test.scheduledDate),
          formattedDueDate: userDate(test.dueDate),
        }));
        setTests(enhancedData);
        setError(null);
      } catch (err) {
        if (!isSilent) {
          setError(err.message);
          swalError(err.message);
        }
      } finally {
        if (!isSilent) setLoading(false);
      }
    },
    [serverUrl, classId, userDate],
  );

  // Polling for background PDF extraction status updates
  useEffect(() => {
    const hasPendingExtraction = tests.some((test) => {
      const qbObj =
        typeof test.questionBankId === "object" && test.questionBankId !== null
          ? test.questionBankId
          : null;
      const status = qbObj?.status;
      return status === "Requested" || status === "Processing";
    });

    if (!hasPendingExtraction) return;

    const intervalId = setInterval(() => {
      fetchTests(true); // Silent background fetch without loader flicker
    }, 10000); // Poll every 10 seconds while any test extraction is pending

    return () => clearInterval(intervalId);
  }, [tests, fetchTests]);

  const fetchRubrics = useCallback(async () => {
    try {
      if (role === "admin" && !selectedClass) return;
      const targetInstructorId =
        role === "admin" ? selectedClass.instructorId : user._id;
      const response = await fetchWithAuth(
        `${serverUrl}/api/rubric/get-all/${targetInstructorId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch rubrics");

      const data = await response.json();
      const enhancedData = data.map((rubric) => ({
        ...rubric,
        formattedDate: userDate(rubric.createdAt),
      }));

      setRubrics(enhancedData);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    }
  }, [serverUrl, userDate, user._id, role, selectedClass]);

  const fetchClassDetails = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get/${classId}`,
        { credentials: "include" },
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        setClassData(data);
      }
    } catch (err) {
      console.error("Failed to fetch class details:", err);
    }
  }, [serverUrl, classId]);

  const fetchCourseOutcomes = useCallback(async () => {
    try {
      const classResponse = await fetchWithAuth(
        `${serverUrl}/api/class/get/${classId}`,
        { credentials: "include" },
      );
      if (!classResponse || !classResponse.ok) {
        throw new Error("Failed to fetch class data");
      }
      const classData = await classResponse.json();
      const courseId = classData.courseId;
      if (!courseId || courseId === "undefined") {
        setCourseOutcomes([]);
        return;
      }

      const courseResponse = await fetchWithAuth(
        `${serverUrl}/api/course/get/${courseId}`,
        { credentials: "include" },
      );
      if (!courseResponse || !courseResponse.ok) {
        throw new Error("Failed to fetch course data");
      }
      const courseData = await courseResponse.json();
      const outcomes =
        courseData.outcomes?.map((outcome) => ({
          value: outcome.code,
          label: outcome.code,
        })) || [];
      setCourseOutcomes(outcomes);
    } catch (err) {
      console.error("Error fetching course outcomes:", err);
      // Silently handle or set default
      setCourseOutcomes([]);
    }
  }, [serverUrl, classId]);

  // ── Online Exam helpers ──────────────────────────────────────────
  const fetchQuestionBanks = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/get-question-banks/${classId}`,
        { credentials: "include" },
      );
      if (!response) return [];
      const data = await response.json();
      setAvailableQuestionBanks(data || []);
      return data;
    } catch (err) {
      console.error("Error fetching question banks:", err);
      return [];
    }
  }, [serverUrl, classId]);

  const fetchMcqQuestions = useCallback(
    async (bankId = null) => {
      try {
        setIsMcqLoading(true);
        const url = bankId
          ? `${serverUrl}/api/question-bank/get-questions/${bankId}`
          : `${serverUrl}/api/question-bank/get-all-questions/${classId}`;
        const response = await fetchWithAuth(url, { credentials: "include" });
        if (!response) return [];
        const data = await response.json();
        const allowedTypes = ["MCQ", "FILL-IN-THE-BLANKS", "NUMERICAL"];
        const mcqs = data.filter(
          (q) => q.type && allowedTypes.includes(q.type.toUpperCase()),
        );
        setMcqQuestions(mcqs);
        return mcqs;
      } catch (err) {
        console.error("Error fetching MCQ questions:", err);
        swalError("Failed to load MCQ questions.");
        return [];
      } finally {
        setIsMcqLoading(false);
      }
    },
    [serverUrl, classId],
  );

  const convertMcqToOnlineExam = useCallback(
    (question, index) => {
      // Resolve library info if not present but libraryId is available
      let resolvedLibraryName = question.libraryName || "";
      let resolvedYear = question.year || "";
      if (!resolvedLibraryName && question.libraryId) {
        const lib = allLibraries.find((l) => l._id === question.libraryId);
        if (lib) {
          resolvedLibraryName = lib.libraryName || "";
          resolvedYear = lib.year || "";
        }
      }

      // Use structured options and correctOptions from backend directly
      const optionsArr = (question.options || []).filter(Boolean).map((o) => ({
        key: o.key || "",
        text: o.text || "",
      }));
      const correctOpts = Array.isArray(question.correctOptions)
        ? question.correctOptions
        : [];
      return {
        questionNumber: String(index + 1),
        sectionNumber: question.sourceSectionNumber || null,
        questionText: question.questionText || "",
        type: question.type || "MCQ",
        options: optionsArr,
        correctOptions: correctOpts,
        correctAnswer: question.correctAnswer || "",
        maxScore: 1,
        courseOutcome: question.courseOutcome || "",
        bloomsLevel: question.bloomsLevel || "",
        subject: question.subject || "",
        topic: question.topic || "",
        subTopic: question.subTopic || "",
        difficulty: question.difficulty || "",
        isPastExam: question.isPastExam || false,
        libraryName: resolvedLibraryName,
        year: resolvedYear,
        source: question.source || (question.libraryId ? "library" : ""),
        libraryId: question.libraryId || null,
        _sourceId: question._id,
        questionId: question._id,
        libraryQuestionId: (question.source === "library" || question.libraryId) ? question._id : null,
        _sourceSolution: question.solution || "",
        diagramFileURL: question.diagramFileURL || "",
        diagramFileSignedURL: question.diagramFileSignedURL || "",
        diagramPageFileURL: question.diagramPageFileURL || "",
        diagramBoundingBox: question.diagramBoundingBox || null,
        svgCode: question.svgCode || "",
      };
    },
    [allLibraries],
  );

  const handleAddSelectedMcqs = useCallback(
    (isEdit = false) => {
      const test = isEdit ? editTest : newTest;
      const setTest = isEdit ? setEditTest : setNewTest;
      const existingSourceIds = new Set(
        (test.onlineExamQuestions || [])
          .map((q) => q._sourceId)
          .filter(Boolean),
      );
      const questionsToAdd = mcqQuestions
        .filter(
          (q) => mcqSelectedIds.has(q._id) && !existingSourceIds.has(q._id),
        )
        .map((q, i) =>
          convertMcqToOnlineExam(
            q,
            (test.onlineExamQuestions || []).length + i,
          ),
        );
      if (questionsToAdd.length === 0) {
        swalWarning("Selected questions are already added.");
        setIsMcqPickerOpen(false);
        return;
      }
      setTest((prev) => {
        const updated = [
          ...(prev.onlineExamQuestions || []),
          ...questionsToAdd,
        ];
        const renumbered = updated.map((q, i) => ({
          ...q,
          questionNumber: String(i + 1),
        }));
        const totalMarks = roundTo2(
          renumbered.reduce(
            (sum, q) => sum + (parseFloat(q.maxScore) || 0),
            0,
          ),
        );
        const extra = {};
        if (
          prev.isAdvancedOnlineExam &&
          prev.advancedOnlineExamOptions?.isTimedExam
        ) {
          extra.duration = String(
            roundTo2(
              renumbered.reduce(
                (sum, q) => sum + (parseFloat(q.duration) || 0),
                0,
              ),
            ),
          );
        }
        return {
          ...prev,
          onlineExamQuestions: renumbered,
          maxScore: totalMarks,
          ...extra,
        };
      });
      if (isEdit) setIsEditChanged(true);
      setMcqSelectedIds(new Set());
      setIsMcqPickerOpen(false);
      swalSuccess(`${questionsToAdd.length} question(s) added.`);
    },
    [mcqQuestions, mcqSelectedIds, editTest, newTest, convertMcqToOnlineExam],
  );

  const handleOnlineExamQuestionRemove = useCallback(
    (index, isEdit = false) => {
      const setTest = isEdit ? setEditTest : setNewTest;
      setTest((prev) => {
        const updated = prev.onlineExamQuestions.filter((_, i) => i !== index);
        const renumbered = updated.map((q, i) => ({
          ...q,
          questionNumber: String(i + 1),
        }));
        const totalMarks = roundTo2(
          renumbered.reduce(
            (sum, q) => sum + (parseFloat(q.maxScore) || 0),
            0,
          ),
        );
        const extra = {};
        if (
          prev.isAdvancedOnlineExam &&
          prev.advancedOnlineExamOptions?.isTimedExam
        ) {
          extra.duration = String(
            roundTo2(
              renumbered.reduce(
                (sum, q) => sum + (parseFloat(q.duration) || 0),
                0,
              ),
            ),
          );
        }
        return {
          ...prev,
          onlineExamQuestions: renumbered,
          maxScore: totalMarks,
          ...extra,
        };
      });
      if (isEdit) setIsEditChanged(true);
    },
    [],
  );

  const handleOnlineExamQuestionChange = useCallback(
    (index, field, value, isEdit = false) => {
      const setTest = isEdit ? setEditTest : setNewTest;
      setTest((prev) => {
        const updated = [...prev.onlineExamQuestions];
        updated[index] = { ...updated[index], [field]: value };
        const extra = {};
        if (field === "maxScore") {
          extra.maxScore = roundTo2(
            updated.reduce(
              (sum, q) => sum + (parseFloat(q.maxScore) || 0),
              0,
            ),
          );
        }
        if (
          field === "duration" &&
          prev.isAdvancedOnlineExam &&
          prev.advancedOnlineExamOptions?.isTimedExam
        ) {
          extra.duration = String(
            roundTo2(
              updated.reduce(
                (sum, q) => sum + (parseFloat(q.duration) || 0),
                0,
              ),
            ),
          );
        }
        return { ...prev, onlineExamQuestions: updated, ...extra };
      });
      if (isEdit) setIsEditChanged(true);
    },
    [],
  );

  const handleCommonMaxScoreChange = useCallback((newScore, isEdit = false) => {
    const parsedScore = newScore === "" ? "" : parseFloat(newScore);
    if (newScore !== "" && (isNaN(parsedScore) || parsedScore < 0)) return;

    const setTest = isEdit ? setEditTest : setNewTest;
    setTest((prev) => {
      const updated = (prev.onlineExamQuestions || []).map((q) => ({
        ...q,
        maxScore: parsedScore,
      }));
      const totalMarks = roundTo2(
        updated.reduce(
          (sum, q) => sum + (parseFloat(q.maxScore) || 0),
          0,
        ),
      );
      return {
        ...prev,
        onlineExamQuestions: updated,
        maxScore: totalMarks,
      };
    });
    if (isEdit) setIsEditChanged(true);
  }, []);

  const handleCommonDurationChange = useCallback(
    (newDuration, isEdit = false, subjectFilter = null) => {
      const parsedDuration = newDuration === "" ? "" : parseFloat(newDuration);
      if (newDuration !== "" && (isNaN(parsedDuration) || parsedDuration < 0)) return;

      const setTest = isEdit ? setEditTest : setNewTest;
      setTest((prev) => {
        const updated = (prev.onlineExamQuestions || []).map((q) => {
          if (subjectFilter && (q.subject || "General") !== subjectFilter)
            return q;
          return { ...q, duration: parsedDuration };
        });
        const totalDuration = roundTo2(
          updated.reduce(
            (sum, q) => sum + (parseFloat(q.duration) || 0),
            0,
          ),
        );
        return {
          ...prev,
          onlineExamQuestions: updated,
          duration: String(totalDuration),
        };
      });
      if (isEdit) setIsEditChanged(true);
    },
    [],
  );

  const handleCommonMaxScoreChangeScoped = useCallback(
    (newScore, isEdit = false, subjectFilter = null) => {
      const parsedScore = newScore === "" ? "" : parseFloat(newScore);
      if (newScore !== "" && (isNaN(parsedScore) || parsedScore < 0)) return;

      const setTest = isEdit ? setEditTest : setNewTest;
      setTest((prev) => {
        const updated = (prev.onlineExamQuestions || []).map((q) => {
          if (subjectFilter && (q.subject || "General") !== subjectFilter)
            return q;
          return { ...q, maxScore: parsedScore };
        });
        const totalMarks = roundTo2(
          updated.reduce(
            (sum, q) => sum + (parseFloat(q.maxScore) || 0),
            0,
          ),
        );
        return {
          ...prev,
          onlineExamQuestions: updated,
          maxScore: totalMarks,
        };
      });
      if (isEdit) setIsEditChanged(true);
    },
    [],
  );

  const handleAddCustomQuestion = useCallback(
    (isEdit = false) => {
      if (!customQuestion.questionText.trim()) {
        swalError("Question text is required.");
        return;
      }
      if (customQuestion.options.some((o) => !o.trim())) {
        swalError("All 4 options are required.");
        return;
      }
      if (!customQuestion.correctOptions) {
        swalError("Please select the correct answer.");
        return;
      }
      const setTest = isEdit ? setEditTest : setNewTest;
      const test = isEdit ? editTest : newTest;
      const labels = ["A", "B", "C", "D"];
      const newQ = {
        questionNumber: String((test.onlineExamQuestions || []).length + 1),
        questionText: customQuestion.questionText,
        options: customQuestion.options.map((o, i) => ({
          key: labels[i],
          text: o,
        })),
        correctOptions: [customQuestion.correctOptions.toUpperCase()],
        maxScore: parseFloat(customQuestion.maxScore) || 1,
        courseOutcome: customQuestion.courseOutcome,
        bloomsLevel: customQuestion.bloomsLevel,
        _sourceId: null,
        questionId: null,
        _sourceSolution: "",
        _isCustom: true,
      };
      setTest((prev) => {
        const updated = [...(prev.onlineExamQuestions || []), newQ];
        const totalMarks = roundTo2(
          updated.reduce(
            (sum, q) => sum + (parseFloat(q.maxScore) || 0),
            0,
          ),
        );
        const extra = {};
        if (
          prev.isAdvancedOnlineExam &&
          prev.advancedOnlineExamOptions?.isTimedExam
        ) {
          extra.duration = String(
            roundTo2(
              updated.reduce(
                (sum, q) => sum + (parseFloat(q.duration) || 0),
                0,
              ),
            ),
          );
        }
        return { ...prev, onlineExamQuestions: updated, maxScore: totalMarks, ...extra };
      });
      if (isEdit) setIsEditChanged(true);
      setCustomQuestion({
        questionText: "",
        options: ["", "", "", ""],
        correctOptions: "",
        maxScore: 1,
        courseOutcome: "",
        bloomsLevel: "",
      });
      setIsCustomQuestionOpen(false);
      swalSuccess("Custom question added.");
    },
    [customQuestion, editTest, newTest],
  );

  // Generate Questions via AI (creates QB under the hood)
  const handleGenerateQuestions = useCallback(
    async (isEdit = false) => {
      // Determine which test has a Exam Template selected
      const currentTest = isEdit ? editTest : newTest;
      const selectedStdTestOpt = standardizedTestOptions.find(
        (opt) => opt.value === currentTest.standardizedTestId,
      );
      const isStdTestMode = !!selectedStdTestOpt;

      if (isStdTestMode) {
        // Validate entries
        const validEntries = stdTestEntries.filter(
          (e) => e.subject && e.questionType && e.questionCount >= 1,
        );
        if (validEntries.length === 0) {
          swalError(
            "Please add at least one valid entry with a subject, question type, and count.",
          );
          return;
        }

        setIsGeneratingQuestions(true);
        setGenerateQProgress("Generating questions for Exam Template...");

        try {
          const payload = {
            name: `${currentTest.testName?.trim()} ${new Date().toISOString().slice(0, 16).replace("T", " ")}`,
            standardizedTestTemplateId: selectedStdTestOpt.templateId,
            entries: validEntries.map((e) => ({
              subject: e.subject,
              topic: e.topic || undefined,
              difficulty: e.difficulty || undefined,
              questionType: e.questionType,
              questionCount: parseInt(e.questionCount),
            })),
          };

          const response = await fetchWithAuth(
            `${serverUrl}/api/question-bank/create-question-bank/${classId}`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            },
          );
          if (!response) {
            setIsGeneratingQuestions(false);
            return;
          }
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.message || "Failed to generate questions");
          }

          if (data.questionIds && data.questionIds.length > 0) {
            setGenerateQProgress("Generating solutions...");
            try {
              await fetchWithAuth(
                `${serverUrl}/api/question-bank/generate-question-solution`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ questionIds: data.questionIds }),
                },
              );
            } catch (solutionError) {
              console.error("Solution generation error:", solutionError);
            }
          }

          setGenerateQProgress("Fetching generated questions...");
          const questionsResponse = await fetchWithAuth(
            `${serverUrl}/api/question-bank/get-all-questions/${classId}`,
            { credentials: "include" },
          );
          if (!questionsResponse) {
            setIsGeneratingQuestions(false);
            return;
          }
          const allQuestions = await questionsResponse.json();
          const allowedTypes = ["MCQ", "FILL-IN-THE-BLANKS", "NUMERICAL"];
          const generatedQuestions = allQuestions.filter(
            (q) =>
              data.questionIds?.includes(q._id) &&
              allowedTypes.includes(q.type.toUpperCase()),
          );

          if (generatedQuestions.length === 0) {
            swalWarning(
              "No questions were generated. Try adjusting your entries.",
            );
            setIsGeneratingQuestions(false);
            setGenerateQProgress("");
            return;
          }

          const setTest = isEdit ? setEditTest : setNewTest;
          const test = isEdit ? editTest : newTest;
          const startIndex = (test.onlineExamQuestions || []).length;
          const convertedQuestions = generatedQuestions.map((q, i) =>
            convertMcqToOnlineExam(q, startIndex + i),
          );

          setTest((prev) => {
            const updated = [
              ...(prev.onlineExamQuestions || []),
              ...convertedQuestions,
            ];
            const renumbered = updated.map((q, i) => ({
              ...q,
              questionNumber: String(i + 1),
            }));
            const totalMarks = roundTo2(
              renumbered.reduce(
                (sum, q) => sum + (parseFloat(q.maxScore) || 0),
                0,
              ),
            );
            const extra = {};
            if (
              prev.isAdvancedOnlineExam &&
              prev.advancedOnlineExamOptions?.isTimedExam
            ) {
              extra.duration = String(
                roundTo2(
                  renumbered.reduce(
                    (sum, q) => sum + (parseFloat(q.duration) || 0),
                    0,
                  ),
                ),
              );
            }
            return {
              ...prev,
              onlineExamQuestions: renumbered,
              maxScore: totalMarks,
              ...extra,
            };
          });
          if (isEdit) setIsEditChanged(true);

          setMcqQuestions(
            allQuestions.filter((q) =>
              allowedTypes.includes(q.type.toUpperCase()),
            ),
          );

          setIsGenerateQuestionsOpen(false);
          setStdTestEntries([
            {
              subject: "",
              topic: "",
              difficulty: "",
              questionType: "MCQ",
              questionCount: 5,
            },
          ]);
          swalSuccess(
            `${convertedQuestions.length} question(s) generated and added!`,
          );
        } catch (error) {
          console.error("Error generating questions:", error);
          swalError(error.message || "Failed to generate questions.");
        } finally {
          setIsGeneratingQuestions(false);
          setGenerateQProgress("");
        }
        return;
      }

      // ── Standard (non-standardized-test) mode ────────────────────
      if (!generateQForm.topic.trim()) {
        swalError("Topic is required.");
        return;
      }
      if (generateQForm.questionCount < 1 || generateQForm.questionCount > 30) {
        swalError("Question count must be between 1 and 30.");
        return;
      }

      setIsGeneratingQuestions(true);
      setGenerateQProgress("Generating MCQ questions...");

      try {
        const payload = {
          name: `MCQ Exam - ${generateQForm.topic} - ${new Date().toISOString().slice(0, 16).replace("T", " ")}`,
          customInstructions: "",
          entries: [
            {
              topic: generateQForm.topic,
              subTopic: "",
              difficulty: Array.isArray(generateQForm.difficulty)
                ? generateQForm.difficulty.map((opt) => opt.value).join(", ")
                : generateQForm.difficulty,
              questionType: "MCQ",
              questionCount: parseInt(generateQForm.questionCount),
            },
          ],
        };

        const response = await fetchWithAuth(
          `${serverUrl}/api/question-bank/create-question-bank/${classId}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        if (!response) {
          setIsGeneratingQuestions(false);
          return;
        }
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Failed to generate questions");
        }

        if (data.questionIds && data.questionIds.length > 0) {
          setGenerateQProgress("Generating solutions...");
          try {
            await fetchWithAuth(
              `${serverUrl}/api/question-bank/generate-question-solution`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ questionIds: data.questionIds }),
              },
            );
          } catch (solutionError) {
            console.error("Solution generation error:", solutionError);
          }
        }

        setGenerateQProgress("Fetching generated questions...");

        // Fetch the generated questions to get full data
        const questionsResponse = await fetchWithAuth(
          `${serverUrl}/api/question-bank/get-all-questions/${classId}`,
          { credentials: "include" },
        );
        if (!questionsResponse) {
          setIsGeneratingQuestions(false);
          return;
        }
        const allQuestions = await questionsResponse.json();

        // Filter to only the newly generated questions
        const allowedTypes = ["MCQ", "FILL-IN-THE-BLANKS", "NUMERICAL"];
        const generatedQuestions = allQuestions.filter(
          (q) =>
            data.questionIds?.includes(q._id) &&
            allowedTypes.includes(q.type.toUpperCase()),
        );

        if (generatedQuestions.length === 0) {
          swalWarning(
            "No MCQ questions were generated. Try a different topic.",
          );
          setIsGeneratingQuestions(false);
          setGenerateQProgress("");
          return;
        }

        // Convert to online exam format
        const setTest = isEdit ? setEditTest : setNewTest;
        const test = isEdit ? editTest : newTest;
        const startIndex = (test.onlineExamQuestions || []).length;
        const convertedQuestions = generatedQuestions.map((q, i) =>
          convertMcqToOnlineExam(q, startIndex + i),
        );

        setTest((prev) => {
          const updated = [
            ...(prev.onlineExamQuestions || []),
            ...convertedQuestions,
          ];
          const renumbered = updated.map((q, i) => ({
            ...q,
            questionNumber: String(i + 1),
          }));
          const totalMarks = roundTo2(
            renumbered.reduce(
              (sum, q) => sum + (parseFloat(q.maxScore) || 0),
              0,
            ),
          );
          const extra = {};
          if (
            prev.isAdvancedOnlineExam &&
            prev.advancedOnlineExamOptions?.isTimedExam
          ) {
            extra.duration = String(
              roundTo2(
                renumbered.reduce(
                  (sum, q) => sum + (parseFloat(q.duration) || 0),
                  0,
                ),
              ),
            );
          }
          return {
            ...prev,
            onlineExamQuestions: renumbered,
            maxScore: totalMarks,
            ...extra,
          };
        });
        if (isEdit) setIsEditChanged(true);

        // Also update the mcqQuestions list so they show as "already added" in the QB picker
        setMcqQuestions(
          allQuestions.filter((q) =>
            allowedTypes.includes(q.type.toUpperCase()),
          ),
        );

        setIsGenerateQuestionsOpen(false);
        setGenerateQForm({ topic: "", difficulty: [], questionCount: 5 });
        swalSuccess(
          `${convertedQuestions.length} question(s) generated and added!`,
        );
      } catch (error) {
        console.error("Error generating questions:", error);
        swalError(error.message || "Failed to generate questions.");
      } finally {
        setIsGeneratingQuestions(false);
        setGenerateQProgress("");
      }
    },
    [
      generateQForm,
      serverUrl,
      classId,
      editTest,
      newTest,
      convertMcqToOnlineExam,
      standardizedTestOptions,
      stdTestEntries,
    ],
  );

  const handleCloseExtractAnswerKeyModal = async () => {
    if (isExtractingAnswerKey) return;
    const result = await swalConfirm(
      "Discard Changes?",
      "Are you sure you want to discard your extracted answer keys? The uploaded file will also be removed.",
      "Yes, discard",
      "No, keep them"
    );
    if (result.isConfirmed) {
      if (answerKeyPreviewUrl) {
        URL.revokeObjectURL(answerKeyPreviewUrl);
        setAnswerKeyPreviewUrl(null);
      }
      setIsExtractAnswerKeyModalOpen(false);
      setExtractedAnswerKeys([]);
      setExtractPdfForm((prev) => ({
        ...prev,
        answerKeysFile: null,
      }));
    }
  };

  const handleExtractAnswerKey = async (file) => {
    setIsExtractingAnswerKey(true);
    try {
      if (file && (file instanceof Blob || file instanceof File)) {
        if (answerKeyPreviewUrl) {
          URL.revokeObjectURL(answerKeyPreviewUrl);
        }
        const url = URL.createObjectURL(file);
        setAnswerKeyPreviewUrl(url);
      }
      setAnswerKeyPage(1);
      setIsAnswerKeyTransposed(false);

      const formData = new FormData();
      formData.append("answerKeysFile", file);

      const response = await fetchWithAuth(
        `${serverUrl}/api/question-bank/extract-answer-keys`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response) {
        setIsExtractingAnswerKey(false);
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to extract answer keys.");
      }

      if (data.answerKeyMap && Array.isArray(data.answerKeyMap)) {
        setExtractedAnswerKeys(data.answerKeyMap);
        setIsExtractAnswerKeyModalOpen(true);
      } else {
        swalError("Could not extract answer keys. Invalid response format.");
      }
    } catch (err) {
      console.error("Error extracting answer keys:", err);
      swalError(err.message || "Something went wrong extracting answer keys.");
    } finally {
      setIsExtractingAnswerKey(false);
    }
  };

  const handleSaveExtractedAnswerKeys = () => {
    if (extractedAnswerKeys.length === 0) {
      swalError("No answer keys to save.");
      return;
    }

    let csvContent = "questionNumber,answerKey\n";
    extractedAnswerKeys.forEach((item) => {
      csvContent += `${item.questionNumber},${item.answerKey}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const file = new File([blob], "extracted_answer_key.csv", { type: "text/csv" });

    if (answerKeyPreviewUrl) {
      URL.revokeObjectURL(answerKeyPreviewUrl);
      setAnswerKeyPreviewUrl(null);
    }

    setExtractPdfForm((prev) => ({
      ...prev,
      answerKeysFile: file,
    }));

    setIsExtractAnswerKeyModalOpen(false);
    swalSuccess("Answer keys saved and attached successfully!");
  };

  // Extract Questions from Physical Test Paper PDF
  const handleExtractFromPdf = useCallback(
    async (isEdit = false) => {
      if (!extractPdfForm.questionBankName.trim()) {
        swalError("Question Bank name is required.");
        return;
      }
      if (!extractPdfForm.numberOfQs) {
        swalError("Please enter the number of questions you want to extract.");
        return;
      }
      const numQs = parseInt(extractPdfForm.numberOfQs);
      if (isNaN(numQs) || numQs <= 0) {
        swalError("Please enter a valid number of questions to extract.");
        return;
      }
      if (!extractPdfForm.sourceTestPaper) {
        swalError("Please upload the test paper.");
        return;
      }
      if (
        extractPdfForm.answerKeysFile &&
        !extractPdfForm.answerKeysFile.name.toLowerCase().endsWith(".csv")
      ) {
        swalError("Answer key must be a CSV file.");
        return;
      }

      setIsExtractingPdf(true);
      setExtractPdfProgress("Submitting extraction request...");

      try {
        const formData = new FormData();
        formData.append("name", extractPdfForm.questionBankName.trim());
        formData.append("sourceTestPaper", extractPdfForm.sourceTestPaper);
        formData.append("numberOfQs", extractPdfForm.numberOfQs);
        if (extractPdfForm.answerKeysFile) {
          formData.append("answerKeysFile", extractPdfForm.answerKeysFile);
        }
        if (extractPdfForm.customInstructions?.trim()) {
          formData.append(
            "customInstructions",
            extractPdfForm.customInstructions.trim(),
          );
        }
        const standardizedTestId = isEdit
          ? editTest.standardizedTestId
          : newTest.standardizedTestId;
        if (standardizedTestId) {
          formData.append("standardizedTestTemplateId", standardizedTestId);
        }
        if (isEdit && (editTest.id || editTest._id)) {
          formData.append("testId", editTest.id || editTest._id);
        } else {
          formData.append(
            "testName",
            extractPdfForm.questionBankName.trim() || (newTest.testName || "").trim(),
          );
        }

        const response = await fetchWithAuth(
          `${serverUrl}/api/question-bank/extract-questions/${classId}`,
          {
            method: "POST",
            credentials: "include",
            body: formData,
          },
        );
        if (!response) {
          setIsExtractingPdf(false);
          return;
        }
        const data = await response.json();
        if (!response.ok) {
          throw new Error(
            data.message || "Failed to start extraction from PDF",
          );
        }

        setIsExtractPdfOpen(false);
        setIsCreateModalOpen(false);
        setIsEditModalOpen(false);

        setExtractPdfForm({
          questionBankName: "",
          sourceTestPaper: null,
          answerKeysFile: null,
          customInstructions: "",
          numberOfQs: "",
        });

        await fetchTests();

        swalSuccess(
          "Test paper extraction started in background! Check the test list to review questions once processing completes.",
        );
      } catch (error) {
        console.error("Error starting extraction from PDF:", error);
        swalError(error.message || "Failed to extract questions from PDF.");
      } finally {
        setIsExtractingPdf(false);
        setExtractPdfProgress("");
      }
    },
    [
      extractPdfForm,
      serverUrl,
      classId,
      editTest,
      newTest,
      fetchTests,
    ],
  );

  const fetchGlobalMetadataForCache = useCallback(
    async (libraryIds, force = false) => {
      if (!libraryIds || libraryIds.length === 0) return;
      const cacheKey = "global";
      if (!force && libraryMetadataCache[cacheKey]) return;
      if (!force && fetchingMetadataIds.has(cacheKey)) return;

      setFetchingMetadataIds((prev) => {
        const next = new Set(prev);
        next.add(cacheKey);
        return next;
      });

      try {
        const metadataResponse = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/get-library-metadata`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ libraryIds, excludeInvalidQuestions: true }),
          },
        );

        if (metadataResponse && metadataResponse.ok) {
          const metadata = await metadataResponse.json();
          const countsMap = {};
          if (
            metadata.questionCountsArray &&
            Array.isArray(metadata.questionCountsArray)
          ) {
            metadata.questionCountsArray.forEach((item) => {
              const topic = Object.keys(item)[0];
              if (topic) {
                countsMap[topic] = item[topic];
              }
            });
          }

          setLibraryMetadataCache((prev) => ({
            ...prev,
            [cacheKey]: {
              topics: metadata.topics || [],
              difficulties: metadata.difficulties || [],
              subjects: metadata.subjects || [],
              years: metadata.years || [],
              types: metadata.types || [],
              questions: metadata.questions || [],
              counts: countsMap,
            },
          }));
        }
      } catch (err) {
        console.error("Failed to fetch global metadata:", err);
      } finally {
        setFetchingMetadataIds((prev) => {
          const next = new Set(prev);
          next.delete(cacheKey);
          return next;
        });
      }
    },
    [serverUrl, libraryMetadataCache, fetchingMetadataIds],
  );

  const fetchAllLibraryQuestions = useCallback(
    async (examFor, targetStdTestId = null) => {
      try {
        setIsFetchingLibraryQuestions(true);
        setLibraryQuestionsLoaded(false);
        setLibrarySelectedQuestions(new Map());
        setLibraryActiveSubject(null);
        setLibraryFilters({ topic: [], subTopic: [], type: [], difficulty: [] });
        setLibrarySearchTerm("");
        setAllLibraries([]);
        setLibraryMetadataCache({});
        setFetchingMetadataIds(new Set());
        setLibrarySubjectStates({});
        setSubjectQuestionsCache({});

        const mode = examFor || libraryExamFor;
        const isEdit = mode === "edit";
        const currentTest = isEdit ? editTest : newTest;
        const omrTemplateId = currentTest?.omrTemplateId;
        const standardizedTestId = targetStdTestId;

        let targetTemplateId = null;

        if (standardizedTestId) {
          let stdOptions = standardizedTestOptions;
          if (stdOptions.length === 0) {
            try {
              const res = await fetchWithAuth(
                `${serverUrl}/api/standardized-test/get-all`,
                { credentials: "include" },
              );
              if (res && res.ok) {
                const data = await res.json();
                stdOptions = (data.data || data).map((item) => ({
                  value: item._id,
                  label: item.templateName,
                  templateId: item.templateId,
                }));
                setStandardizedTestOptions(stdOptions);
              }
            } catch (err) {
              console.error("Error fetching Exam Templqates in fallback:", err);
            }
          }
          const matchedStd = stdOptions.find(
            (opt) => opt.value === standardizedTestId,
          );
          if (matchedStd?.templateId) {
            targetTemplateId = matchedStd.templateId;
          }
        } else if (omrTemplateId) {
          let options = omrTemplateOptions;
          if (options.length === 0) {
            try {
              const res = await fetchWithAuth(
                `${serverUrl}/api/omr-template/get-all`,
                { credentials: "include" },
              );
              if (res && res.ok) {
                const data = await res.json();
                options = (data.data || data).map((item) => ({
                  value: item._id,
                  label: item.templateName,
                  templateURL: item.templateURL,
                  templateId: item.templateId,
                  standardizedTestId:
                    typeof item.standardizedTestId === "object"
                      ? item.standardizedTestId?._id
                      : item.standardizedTestId || null,
                }));
                setOmrTemplateOptions(options);
              }
            } catch (err) {
              console.error("Error fetching OMR templates in fallback:", err);
            }
          }
          const matchedOmr = options.find((opt) => opt.value === omrTemplateId);
          if (matchedOmr?.templateId) {
            targetTemplateId = matchedOmr.templateId;
          }
        }

        if (!targetTemplateId) {
          setIsFetchingLibraryQuestions(false);
          return;
        }

        let url = `${serverUrl}/api/library-mcq-bank/get-libraries`;
        if (targetTemplateId) {
          url = `${serverUrl}/api/library-mcq-bank/get-library-by-template/${targetTemplateId}`;
        }

        const response = await fetchWithAuth(url, {
          method: "GET",
          credentials: "include",
        });
        if (!response) return;
        const fetchedLibraries = await response.json();
        if (!response.ok) {
          throw new Error(
            fetchedLibraries.message || "Failed to fetch libraries",
          );
        }

        // The backend endpoint already filters by templateId if needed.
        let libraries = fetchedLibraries;

        setAllLibraries(libraries);

        if (libraries.length > 0) {
          const libraryIds = libraries.map((lib) => lib._id);
          await fetchGlobalMetadataForCache(libraryIds, true);
        }

        setLibraryQuestionsLoaded(true);

        // Reset selection states for the new UI
        setLibrarySelectedQuestions(new Map());
        setLibraryActiveSubject(null);
        setLibraryFilters({ topic: [], subTopic: [], type: [], difficulty: [] });
        setLibrarySearchTerm("");
        setLibrarySubjectStates({});
        setSubjectQuestionsCache({});
      } catch (err) {
        swalError(err.message || "Failed to fetch library questions");
      } finally {
        setIsFetchingLibraryQuestions(false);
      }
    },
    [
      serverUrl,
      libraryExamFor,
      editTest,
      newTest,
      omrTemplateOptions,
      standardizedTestOptions,
      fetchGlobalMetadataForCache,
    ],
  );

  // handleAddLibraryQuestionsToExam removed as questions are now added directly

  useEffect(() => {
    const fetchTopicQuestions = async () => {
      let effectiveSubject = libraryActiveSubject;
      if (!effectiveSubject) {
        const globalCache = libraryMetadataCache["global"] || {};
        const allQuestions = globalCache.questions || [];
        const allSubjects = [
          ...new Set(allQuestions.map((q) => q.subject).filter(Boolean)),
        ].sort();
        if (allSubjects.length > 0) {
          effectiveSubject = allSubjects[0];
        }
      }

      if (
        !effectiveSubject ||
        !libraryFilters.topic ||
        libraryFilters.topic.length === 0
      ) {
        setLibraryTopicQuestions([]);
        setLibraryTopicTotalPages(1);
        setLibraryTopicTotalCount(0);
        return;
      }

      if (libraryRandomSortedPool && libraryRandomSortedPool.length > 0) {
        const pageLimit = 30;
        const start = (libraryTopicPage - 1) * pageLimit;
        setLibraryTopicQuestions(
          libraryRandomSortedPool.slice(start, start + pageLimit),
        );
        return;
      }

      const cacheKey = JSON.stringify({
        subject: effectiveSubject,
        topics: libraryFilters.topic,
        subTopics: libraryFilters.subTopic,
        type: libraryFilters.type,
        difficulty: libraryFilters.difficulty,
        searchTerm: libraryDebouncedSearchTerm,
        page: libraryTopicPage,
      });

      if (subjectQuestionsCache[cacheKey]) {
        const data = subjectQuestionsCache[cacheKey];
        setLibraryTopicQuestions(data.questions || []);
        setLibraryTopicTotalPages(data.totalPages || 1);
        setLibraryTopicTotalCount(data.totalCount || 0);
        return;
      }

      setIsFetchingTopicQuestions(true);
      try {
        const allLibIds = allLibraries.map((l) => l._id);
        if (allLibIds.length === 0) return;

        const response = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/get-questions-by-topic`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              libraryIds: allLibIds,
              subject: effectiveSubject,
              topics: libraryFilters.topic,
              subTopics: libraryFilters.subTopic,
              type: libraryFilters.type,
              difficulty: libraryFilters.difficulty,
              searchTerm: libraryDebouncedSearchTerm,
              page: libraryTopicPage,
              limit: 30,
              excludeInvalidQuestions: true,
            }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          setSubjectQuestionsCache((prev) => ({
            ...prev,
            [cacheKey]: data,
          }));
          setLibraryTopicQuestions(data.questions || []);
          setLibraryTopicTotalPages(data.totalPages || 1);
          setLibraryTopicTotalCount(data.totalCount || 0);
        }
      } catch (err) {
        console.error("Failed to fetch topic questions", err);
      } finally {
        setIsFetchingTopicQuestions(false);
      }
    };

    fetchTopicQuestions();
  }, [
    libraryActiveSubject,
    libraryFilters.topic,
    libraryFilters.subTopic,
    libraryFilters.type,
    libraryFilters.difficulty,
    libraryDebouncedSearchTerm,
    libraryTopicPage,
    allLibraries,
    libraryMetadataCache,
    serverUrl,
    subjectQuestionsCache,
    libraryRandomSortedPool,
  ]);

  const normalizeText = useCallback((text) => {
    if (!text) return "";
    let cleaned = text.replace(/<[^>]*>/g, "");
    cleaned = cleaned.replace(/\s+/g, " ");
    cleaned = cleaned.replace(/[^a-zA-Z0-9 ]/g, "");
    return cleaned.trim().toLowerCase();
  }, []);

  const isSimilarQuestion = useCallback(
    (cleanNewText, comparisonPool, threshold = 0.85) => {
      if (!cleanNewText) return false;

      for (const clean of comparisonPool) {
        if (!clean) continue;
        if (cleanNewText === clean) {
          return true;
        }

        const similarity = stringSimilarity.compareTwoStrings(
          cleanNewText,
          clean,
        );
        if (similarity >= threshold) {
          return true;
        }
      }

      return false;
    },
    [],
  );

  // ── New Library Bank: Toggle question selection ──
  const handleToggleLibraryQuestion = useCallback((question) => {
    setLibrarySelectedQuestions((prev) => {
      const next = new Map(prev);
      if (next.has(question._id)) {
        next.delete(question._id);
      } else {
        next.set(question._id, question);
      }
      return next;
    });
    setLibraryAutoPickedIds((prev) => {
      if (prev.has(question._id)) {
        const next = new Set(prev);
        next.delete(question._id);
        return next;
      }
      return prev;
    });
  }, []);

  // ── New Library Bank: Select/Deselect all visible questions on current page ──
  const handleSelectAllLibraryQuestions = useCallback(
    (filteredQuestions, selectAll) => {
      setLibrarySelectedQuestions((prev) => {
        const next = new Map(prev);
        filteredQuestions.forEach((q) => {
          if (selectAll) {
            next.set(q._id, q);
          } else {
            next.delete(q._id);
          }
        });
        return next;
      });
      if (!selectAll) {
        // Remove deselected page items from auto-picked set if any
        setLibraryAutoPickedIds((prev) => {
          const next = new Set(prev);
          filteredQuestions.forEach((q) => next.delete(q._id));
          return next;
        });
      }
    },
    [],
  );

  // ── New Library Bank: Select/Deselect all questions across all pages in this topic ──
  const handleSelectAllTopicQuestions = useCallback(
    async (selectAll) => {
      if (!selectAll) {
        // Deselect all questions belonging to current pool
        setLibrarySelectedQuestions((prev) => {
          const next = new Map(prev);
          const pool = libraryRandomSortedPool || libraryTopicQuestions || [];
          pool.forEach((q) => {
            next.delete(q._id);
          });
          return next;
        });
        setLibraryAutoPickedIds(new Set());
        return;
      }

      if (!libraryTopicTotalCount || libraryTopicTotalCount === 0) {
        swalWarning("No questions available in this topic to select.");
        return;
      }

      setIsSelectingAllTopic(true);
      try {
        let pool = libraryRandomSortedPool || libraryTopicQuestions || [];

        if (libraryTopicTotalCount > pool.length) {
          const allLibIds = allLibraries.map((l) => l._id);
          let effectiveSubject = libraryActiveSubject;
          if (!effectiveSubject) {
            const globalCache = libraryMetadataCache["global"] || {};
            const allQ = globalCache.questions || [];
            const allSubs = [
              ...new Set(allQ.map((q) => q.subject).filter(Boolean)),
            ].sort();
            if (allSubs.length > 0) {
              effectiveSubject = allSubs[0];
            }
          }

          const response = await fetchWithAuth(
            `${serverUrl}/api/library-mcq-bank/get-questions-by-topic`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                libraryIds: allLibIds,
                subject: effectiveSubject,
                topics: libraryFilters.topic,
                subTopics: libraryFilters.subTopic,
                type: libraryFilters.type,
                difficulty: libraryFilters.difficulty,
                searchTerm: libraryDebouncedSearchTerm,
                page: 1,
                limit: libraryTopicTotalCount,
                excludeInvalidQuestions: true,
              }),
            },
          );

          if (response.ok) {
            const data = await response.json();
            if (data.questions && data.questions.length > 0) {
              pool = data.questions;
            }
          } else {
            throw new Error("Failed to fetch questions for selecting all");
          }
        }

        if (!pool || pool.length === 0) {
          swalWarning("No questions found matching your filters.");
          return;
        }

        setLibrarySelectedQuestions((prev) => {
          const next = new Map(prev);
          pool.forEach((q) => {
            next.set(q._id, q);
          });
          return next;
        });

        swalSuccess(
          `Selected all ${pool.length} questions in this topic across all pages.`,
        );
      } catch (err) {
        console.error("Failed to select all questions in topic", err);
        swalWarning("Could not select all questions. Please try again.");
      } finally {
        setIsSelectingAllTopic(false);
      }
    },
    [
      libraryTopicTotalCount,
      libraryRandomSortedPool,
      libraryTopicQuestions,
      allLibraries,
      libraryActiveSubject,
      libraryMetadataCache,
      libraryFilters,
      libraryDebouncedSearchTerm,
      serverUrl,
    ],
  );

  // ── New Library Bank: Smart Auto-Pick / Database Sampled Selection of N questions ──
  const handleRandomSelectLibraryQuestions = useCallback(
    async (filteredQuestions, targetCountOverride = null, forceApiSample = false) => {
      const rawCount =
        targetCountOverride !== null ? targetCountOverride : libraryRandomCount;
      const count = parseInt(rawCount, 10);
      if (isNaN(count) || count <= 0) {
        swalWarning("Please enter a valid number of questions to pick.");
        return;
      }

      if (!libraryTopicTotalCount || libraryTopicTotalCount === 0) {
        swalWarning("No questions available to select from.");
        return;
      }

      if (count > libraryTopicTotalCount) {
        swalWarning(
          `You cannot select more questions than the total available (${libraryTopicTotalCount}).`,
        );
        return;
      }

      setIsRandomSelecting(true);
      try {
        let picked = [];
        const currentLoaded = filteredQuestions || libraryTopicQuestions || [];

        // System Design Optimization:
        // If the user requests `count` questions (e.g. 25) and we already have at least `count`
        // questions loaded on the active page (e.g. standard page size is 30), do NOT make an API call!
        // Simply select the first `count` questions directly on this page.
        if (
          !forceApiSample &&
          currentLoaded.length > 0 &&
          count <= currentLoaded.length
        ) {
          picked = currentLoaded.slice(0, count);

          // If there was a previous API-sampled pool active, reset it to restore standard topic pagination
          if (libraryRandomSortedPool && libraryRandomSortedPool.length > 0) {
            setLibraryRandomSortedPool(null);
            setLibraryTopicTotalPages(
              Math.ceil((libraryTopicTotalCount || currentLoaded.length) / 30),
            );
          }
        } else if (
          !forceApiSample &&
          libraryTopicTotalCount <= currentLoaded.length &&
          currentLoaded.length > 0
        ) {
          // If all questions in the entire topic are already loaded (e.g. topic has <= 30 total questions)
          const shuffled = [...currentLoaded];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          picked = shuffled.slice(0, count);
        } else {
          // Native Database Sampling: Only fetch EXACTLY `count` questions from MongoDB!
          const allLibIds = allLibraries.map((l) => l._id);
          let effectiveSubject = libraryActiveSubject;
          if (!effectiveSubject) {
            const globalCache = libraryMetadataCache["global"] || {};
            const allQ = globalCache.questions || [];
            const allSubs = [
              ...new Set(allQ.map((q) => q.subject).filter(Boolean)),
            ].sort();
            if (allSubs.length > 0) {
              effectiveSubject = allSubs[0];
            }
          }

          const response = await fetchWithAuth(
            `${serverUrl}/api/library-mcq-bank/get-questions-by-topic`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                libraryIds: allLibIds,
                subject: effectiveSubject,
                topics: libraryFilters.topic,
                subTopics: libraryFilters.subTopic,
                type: libraryFilters.type,
                difficulty: libraryFilters.difficulty,
                searchTerm: libraryDebouncedSearchTerm,
                page: 1,
                limit: count, // ONLY fetch the required number of questions!
                isRandom: true, // Database sampling via MongoDB $sample
                excludeInvalidQuestions: true,
              }),
            },
          );

          if (response.ok) {
            const data = await response.json();
            if (data.questions && data.questions.length > 0) {
              picked = data.questions;
            }
          } else {
            throw new Error("Failed to sample questions from database");
          }

          if (!picked || picked.length === 0) {
            swalWarning("No questions found matching your filters.");
            return;
          }

          // For questions sampled from database across pages, store the pool and paginate through it
          setLibraryRandomSortedPool(picked);
          setLibraryTopicQuestions(picked.slice(0, 30));
          setLibraryTopicTotalPages(Math.ceil(picked.length / 30));
          setLibraryTopicPage(1);
        }

        if (!picked || picked.length === 0) {
          swalWarning("No questions found matching your filters.");
          return;
        }

        // Update selected questions:
        // Clear previous auto-picked questions, and add the newly picked ones
        setLibrarySelectedQuestions((prev) => {
          const next = new Map(prev);
          libraryAutoPickedIds.forEach((id) => {
            next.delete(id);
          });
          picked.forEach((q) => {
            next.set(q._id, q);
          });
          return next;
        });

        setLibraryAutoPickedIds(new Set(picked.map((q) => q._id)));
        setLibraryRandomCount(String(count));
        setLastPickedRandomCount(String(count).trim());

        if (!forceApiSample && count <= currentLoaded.length) {
          swalSuccess(
            `Selected first ${picked.length} question${picked.length > 1 ? "s" : ""} on this page for your test!`,
          );
        } else {
          swalSuccess(
            `Picked ${picked.length} question${picked.length > 1 ? "s" : ""} for your test!`,
          );
        }
      } catch (err) {
        console.error("Failed to auto-pick questions", err);
        swalWarning("Could not complete question selection. Please try again.");
      } finally {
        setIsRandomSelecting(false);
      }
    },
    [
      libraryRandomCount,
      libraryTopicTotalCount,
      libraryRandomSortedPool,
      libraryTopicQuestions,
      libraryAutoPickedIds,
      allLibraries,
      libraryActiveSubject,
      libraryMetadataCache,
      libraryFilters,
      libraryDebouncedSearchTerm,
      serverUrl,
    ],
  );

  // ── New Library Bank: Clear only auto-picked questions ──
  const handleClearAutoPicked = useCallback(() => {
    setLibrarySelectedQuestions((prev) => {
      const next = new Map(prev);
      libraryAutoPickedIds.forEach((id) => {
        next.delete(id);
      });
      return next;
    });
    setLibraryAutoPickedIds(new Set());
    setLastPickedRandomCount(null);
    setLibraryRandomCount("");
    setLibraryRandomSortedPool(null);
    swalSuccess("Auto-picked questions cleared.");
  }, [libraryAutoPickedIds]);

  // ── New Library Bank: Save all selected questions to exam ──
  const handleSaveLibrarySelections = useCallback(() => {
    if (librarySelectedQuestions.size === 0) {
      swalWarning(
        "No questions selected. Please select at least one question.",
      );
      return;
    }

    const isEdit = libraryExamFor === "edit";
    const setTest = isEdit ? setEditTest : setNewTest;
    const test = isEdit ? editTest : newTest;

    const existingQuestions = test.onlineExamQuestions || [];
    const existingSourceIds = new Set(
      existingQuestions.map((q) => q._sourceId).filter(Boolean),
    );

    // Pre-compute comparison pool for similarity checking
    const comparisonPool = existingQuestions.map((eq) =>
      normalizeText(eq.questionText || ""),
    );

    const selectedArr = Array.from(librarySelectedQuestions.values());
    let acceptedQuestions = [];

    for (const q of selectedArr) {
      // Skip if already exists
      if (existingSourceIds.has(q._id)) continue;

      // Check similarity
      const cleanNewText = normalizeText(q.questionText || "");
      if (isSimilarQuestion(cleanNewText, comparisonPool)) continue;

      acceptedQuestions.push(q);
      comparisonPool.push(cleanNewText);
    }

    if (acceptedQuestions.length === 0) {
      swalWarning(
        "All selected questions are already added or are duplicates.",
      );
      setIsLibraryBankModalOpen(false);
      return;
    }

    const startIndex = existingQuestions.length;
    const questionsToAdd = acceptedQuestions.map((q, i) => {
      const mapped = convertMcqToOnlineExam(q, startIndex + i);
      return {
        ...mapped,
        _isCustom: true,
        questionId: null,
        _sourceId: q._id,
        libraryQuestionId: q._id,
      };
    });

    setTest((prev) => {
      const allQuestions = [
        ...(prev.onlineExamQuestions || []),
        ...questionsToAdd,
      ];

      const subjectOrder = [];
      const groupedBySubject = {};

      allQuestions.forEach((q) => {
        const sub = q.subject || "Other";
        if (!groupedBySubject[sub]) {
          groupedBySubject[sub] = [];
          subjectOrder.push(sub);
        }
        groupedBySubject[sub].push(q);
      });

      const reorderedQuestions = [];
      subjectOrder.forEach((sub) => {
        reorderedQuestions.push(...groupedBySubject[sub]);
      });

      const renumbered = reorderedQuestions.map((q, i) => ({
        ...q,
        questionNumber: String(i + 1),
      }));
      const totalMarks = roundTo2(
        renumbered.reduce(
          (sum, q) => sum + (parseFloat(q.maxScore) || 0),
          0,
        ),
      );
      const extra = {};
      if (
        prev.isAdvancedOnlineExam &&
        prev.advancedOnlineExamOptions?.isTimedExam
      ) {
        extra.duration = String(
          roundTo2(
            renumbered.reduce(
              (sum, q) => sum + (parseFloat(q.duration) || 0),
              0,
            ),
          ),
        );
      }
      return { ...prev, onlineExamQuestions: renumbered, maxScore: totalMarks, ...extra };
    });

    if (isEdit) setIsEditChanged(true);

    // Reset and close
    setIsLibraryBankModalOpen(false);
    setLibraryQuestionsLoaded(false);
    setLibrarySelectedQuestions(new Map());
    setLibraryActiveSubject(null);
    setLibraryFilters({ topic: [], subTopic: [], type: [], difficulty: [] });
    setLibrarySearchTerm("");
    setLibraryRandomCount("");
    setLastPickedRandomCount(null);
    setLibraryRandomSortedPool(null);
    setLibrarySubjectStates({});
    setSubjectQuestionsCache({});

    setTimeout(() => {
      swalSuccess(
        `${questionsToAdd.length} library question${questionsToAdd.length > 1 ? "s" : ""} added to exam!`,
      );
    }, 100);
  }, [
    librarySelectedQuestions,
    libraryExamFor,
    editTest,
    newTest,
    convertMcqToOnlineExam,
    normalizeText,
    isSimilarQuestion,
  ]);

  // Batch-save custom questions (without questionId) to a single QB before submitting the test
  const saveCustomQuestionsToQB = useCallback(
    async (questions, testName) => {
      const customQuestions = questions.filter(
        (q) => q._isCustom && !q.questionId,
      );
      if (customQuestions.length === 0) return questions;

      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/question-bank/create-custom-questions/${classId}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ testName, questions: customQuestions }),
          },
        );
        if (!response || !response.ok) {
          const errorData = response ? await response.json() : {};
          throw new Error(
            errorData.message || "Failed to save custom questions",
          );
        }
        const data = await response.json();

        // Map returned questionIds back to the original questions
        let customIdx = 0;
        return questions.map((q) => {
          if (
            q._isCustom &&
            !q.questionId &&
            customIdx < data.questions.length
          ) {
            const saved = data.questions[customIdx++];
            return {
              ...q,
              questionId: saved._id,
              _sourceId: saved._id,
              _isCustom: false,
            };
          }
          return q;
        });
      } catch (error) {
        console.error("Error saving custom questions to QB:", error);
        throw error;
      }
    },
    [serverUrl, classId],
  );

  useEffect(() => {
    if (classId) {
      fetchTests();
      fetchClassDetails();
      // fetchCourseOutcomes(); // moved: only fetch when manual-entry is enabled
    } else {
      setError("Invalid class ID.");
      setLoading(false);
    }
  }, [classId, fetchTests, fetchClassDetails]);

  useEffect(() => {
    if (classId) {
      fetchRubrics();
    }
  }, [classId, fetchRubrics]);

  const handleRubricPreview = async (rubricId) => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/rubric/get/${rubricId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch rubric");
      const data = await response.json();
      if (data.url) {
        window.open(data.url, "_blank");
      } else if (data.text) {
        setPreviewText(data.text);
        setIsPreviewTextOpen(true);
      } else {
        swalWarning("No content to preview for this rubric.");
      }
    } catch (err) {
      swalError(err.message);
    }
  };

  // Handle Delete Test
  const handleDeleteTest = async (testId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this test?",
      "",
      "Yes, delete it!",
      "No, Cancel",
    );
    if (result.isConfirmed) {
      setDeletingTests((prev) => new Set(prev).add(testId));
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/delete/${testId}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to delete test");
        swalSuccess("Test deleted successfully.");
        fetchTests();
      } catch (err) {
        setError(err.message);
        swalError(err.message);
      } finally {
        setDeletingTests((prev) => {
          const newSet = new Set(prev);
          newSet.delete(testId);
          return newSet;
        });
      }
    }
  };

  const generateUserPrompt = async (testId, showProgress = true) => {
    if (showProgress) {
      setUserPromptProgress("Analyzing your paper...");
      setUserPromptPhase("analyzing");
    }

    // Set up timed progress updates
    let progressTimer = null;
    if (showProgress) {
      progressTimer = setTimeout(() => {
        setUserPromptProgress("Understanding key patterns and requirements...");
        setUserPromptPhase("generating");

        // Second update after more time
        setTimeout(() => {
          setUserPromptProgress(
            "Creating personalized grading instructions...",
          );
          setUserPromptPhase("generating");
        }, 3000);
      }, 2000);
    }

    try {
      const testResponse = await fetchWithAuth(
        `${serverUrl}/api/test/get/${testId}`,
        { credentials: "include" },
      );
      if (!testResponse) {
        if (progressTimer) clearTimeout(progressTimer);
        return;
      }
      const testData = await testResponse.json();
      if (!testResponse.ok) {
        throw new Error(testData.message || "Failed to fetch test details");
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/test/generate-userPrompt/${testId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userTypedPrompt: "",
          }),
          credentials: "include",
        },
      );
      if (!response) {
        if (progressTimer) clearTimeout(progressTimer);
        return;
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to generate user prompt");
      }

      // Clear the timer as API call completed
      if (progressTimer) clearTimeout(progressTimer);

      // Update progress to saving
      if (showProgress) {
        setUserPromptProgress("Finalizing grading instructions...");
        setUserPromptPhase("generating");
      }

      // Update the test with the generated userPrompt
      const updateFormData = new FormData();
      updateFormData.append("userPrompt", data.userPrompt);
      const updateRes = await fetchWithAuth(
        `${serverUrl}/api/test/update/${testId}`,
        {
          method: "PUT",
          body: updateFormData,
          credentials: "include",
        },
      );
      if (!updateRes.ok) {
        const updateData = await updateRes.json().catch(() => ({}));
        throw new Error(
          updateData.message || "Failed to update test with user prompt",
        );
      }

      // Update progress to completed
      if (showProgress) {
        setUserPromptProgress("Instructions generated successfully!");
        setUserPromptPhase("completed");
      }

      // Check whether userPrompt total marks matches test maxScore
      const scoreCheck = checkUserPromptScoreMatch(
        data.userPrompt,
        testData.maxScore,
      );

      return {
        success: true,
        userPrompt: data.userPrompt,
        ...scoreCheck,
      };
    } catch (error) {
      console.error("Auto generation failed:", error);
      if (progressTimer) clearTimeout(progressTimer);
      return { success: false, error: error?.message || "Auto generation failed" };
    } finally {
      if (showProgress) {
        // Clear progress after showing the final message
        setTimeout(() => {
          setUserPromptProgress("");
          setUserPromptPhase("");
        }, 1500);
      }
    }
  };

  // Create new rubric and associate with test
  const handleCreateInlineRubric = async (isEdit = false) => {
    if (!newRubricData.name.trim()) {
      swalError("Rubric name is required.");
      return null;
    }

    if (!newRubricData.text && !newRubricData.file) {
      swalError("Either rubric text or file must be provided.");
      return null;
    }

    const formData = new FormData();
    formData.append("name", newRubricData.name);

    if (newRubricData.file) {
      formData.append("file", newRubricData.file);
    } else if (newRubricData.text) {
      formData.append("text", newRubricData.text);
    }

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/rubric/add`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response) return null;

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to create rubric");
      }

      // Refresh rubrics list
      await fetchRubrics();

      // Reset new rubric form
      setNewRubricData({
        name: "",
        file: null,
        text: "",
        capturedFile: null,
      });
      setShowNewRubricForm(false);

      // Return the new rubric ID
      return data._id || data.rubricId;
    } catch (error) {
      swalError(error.message);
      return null;
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    if (!newTest.testName.trim()) {
      setFormError("Test name is required.");
      return;
    }

    // Validate manual entry questions if enabled
    let effectiveManualQuestions = newTest.manualEntryQuestions || [];
    const isK12ManualEntry =
      newTest.isManualEntryQuestions &&
      instituteData?.category === "K12_SCHOOL";

    if (newTest.isManualEntryQuestions) {
      if (isK12ManualEntry) {
        effectiveManualQuestions = [
          {
            questionNumber: "Obtained Score",
            questionText: `Mannual Test for ${newTest.testName}.`,
            maxScore: newTest.maxScore,
          },
        ];
      }

      if (effectiveManualQuestions.length === 0) {
        swalError("Please add at least one task for manual entry test.");
        return;
      }

      for (let i = 0; i < effectiveManualQuestions.length; i++) {
        const q = effectiveManualQuestions[i];

        if (!isK12ManualEntry) {
          if (
            !q.questionNumber ||
            !q.questionText ||
            !q.maxScore ||
            !q.courseOutcome
          ) {
            swalError(
              `Task ${i + 1
              }: Task Number, Task Description, Max Score, and CO are required.`,
            );
            return;
          }
        } else {
          if (!q.questionNumber || !q.maxScore) {
            swalError(
              "Obtained Score task must have question number and max score.",
            );
            return;
          }
        }

        if (q.maxScore <= 0) {
          swalError(`Task ${i + 1}: Max Score must be greater than 0.`);
          return;
        }
      }

      const totalTaskScore = roundTo2(
        effectiveManualQuestions.reduce(
          (sum, q) => sum + (parseFloat(q.maxScore) || 0),
          0,
        ),
      );
      if (totalTaskScore !== roundTo2(newTest.maxScore)) {
        swalError(
          "The total max score from tasks must equal the overall max score.",
        );
        return;
      }

      if (!isK12ManualEntry) {
        const taskNumbers = effectiveManualQuestions.map(
          (q) => q.questionNumber,
        );
        const uniqueTaskNumbers = new Set(taskNumbers);
        if (uniqueTaskNumbers.size !== taskNumbers.length) {
          swalError("Task numbers must be unique.");
          return;
        }
      }
    }

    // Validate online exam questions if enabled (required for both tabs)
    if (newTest.isOnlineExamination) {
      if (
        !newTest.onlineExamQuestions ||
        newTest.onlineExamQuestions.length === 0
      ) {
        swalError("Please add at least one question for the Objective exam.");
        return;
      }
      for (let i = 0; i < newTest.onlineExamQuestions.length; i++) {
        const q = newTest.onlineExamQuestions[i];
        const isMcq = !q.type || q.type.toUpperCase() === "MCQ";
        const isObjectiveNonMcq =
          q.type &&
          (q.type.toUpperCase() === "FILL-IN-THE-BLANKS" ||
            q.type.toUpperCase() === "NUMERICAL");

        if (
          !q.questionText ||
          !q.maxScore ||
          q.maxScore <= 0 ||
          (isMcq &&
            (!q.correctOptions ||
              q.correctOptions.length === 0 ||
              !q.correctOptions[0])) ||
          (isObjectiveNonMcq &&
            (!q.correctAnswer || q.correctAnswer.trim() === ""))
        ) {
          const element = document.getElementById(`oe-question-create-${i}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.style.borderColor = "#ef4444";
            element.style.boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.25)";
            setTimeout(() => {
              element.style.borderColor = "#e2e8f0";
              element.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
            }, 3000);
          }
          return;
        }
      }
    }

    const scheduled = new Date(newTest.scheduledDate);
    const due = new Date(newTest.dueDate);
    if (scheduled && due && due <= scheduled) {
      swalError("Due Date must be later than Scheduled Date.");
      return;
    }

    setIsSubmitting(true);
    setUserPromptPhase("analyzing");
    setUserPromptProgress("Preparing Test...");

    try {
      // Create inline rubric if needed
      let rubricIdToUse = newTest.rubricId;
      if (showNewRubricForm) {
        const newRubricId = await handleCreateInlineRubric(false);
        if (!newRubricId) {
          setIsSubmitting(false);
          return;
        }
        rubricIdToUse = newRubricId;
      }

      const formData = new FormData();
      formData.append("classId", classId);
      formData.append("testName", newTest.testName);
      formData.append("maxScore", newTest.maxScore);
      formData.append("isExternal", String(newTest.isExternal));
      formData.append(
        "isManualEntryQuestions",
        String(newTest.isManualEntryQuestions),
      );
      formData.append(
        "isOnlineExamination",
        String(newTest.isOnlineExamination),
      );

      if (newTest.isOnlineExamination && newTest.onlineExamPasscode) {
        formData.append("onlineExamPasscode", newTest.onlineExamPasscode);
      }

      if (newTest.isOnlineExamination && newTest.isAdvancedOnlineExam) {
        formData.append(
          "advancedOnlineExamOptions",
          JSON.stringify([newTest.advancedOnlineExamOptions || {}])
        );
      } else if (newTest.isOnlineExamination) {
        formData.append("advancedOnlineExamOptions", JSON.stringify([]));
      }
      formData.append(
        "isCalculatorAllowed",
        String(newTest.isCalculatorAllowed),
      );

      if (newTest.gradingPolicy) {
        formData.append("gradingPolicy", newTest.gradingPolicy);
      }

      if (rubricIdToUse) {
        formData.append("rubricId", rubricIdToUse);
      }

      if (newTest.testType?.value !== undefined) {
        formData.append("testType", newTest.testType?.value);
      }
      if (newTest.scheduledDate) {
        formData.append("scheduledDate", convertToUTC(newTest.scheduledDate));
      }
      if (newTest.dueDate) {
        formData.append("dueDate", convertToUTC(newTest.dueDate));
      }
      if (newTest.duration) {
        formData.append("duration", String(roundTo2(newTest.duration)));
      }

      // If "Results Available Immediately" is ON, set scorePublishedDate to now
      if (newTest.resultsAvailableImmediately) {
        formData.append("scorePublishedDate", new Date().toISOString());
      }

      if (!newTest.isManualEntryQuestions) {
        if (newTest.testPaperURL) {
          formData.append("testPaper", newTest.testPaperURL);
        }
        if (newTest.answerKeyFile) {
          formData.append("answerKeysFile", newTest.answerKeyFile);
        }
        if (newTest.rubricURL) formData.append("rubric", newTest.rubricURL);
        if (newTest.verifyAnswerPaperOnUpload !== undefined) {
          formData.append(
            "verifyAnswerPaperOnUpload",
            String(!!newTest.verifyAnswerPaperOnUpload),
          );
        }
      }

      const questionsToAppend = newTest.isManualEntryQuestions
        ? effectiveManualQuestions
        : [];

      if (newTest.isManualEntryQuestions && questionsToAppend.length > 0) {
        questionsToAppend.forEach((question, index) => {
          formData.append(
            `manualEntryQuestions[${index}][questionNumber]`,
            question.questionNumber,
          );
          formData.append(
            `manualEntryQuestions[${index}][questionText]`,
            question.questionText,
          );
          formData.append(
            `manualEntryQuestions[${index}][maxScore]`,
            question.maxScore,
          );
          if (question.courseOutcome) {
            formData.append(
              `manualEntryQuestions[${index}][courseOutcome]`,
              question.courseOutcome,
            );
          }
          if (question.bloomsLevel) {
            formData.append(
              `manualEntryQuestions[${index}][bloomsLevel]`,
              question.bloomsLevel,
            );
          }
        });
      }

      // Append online exam questions as JSON (for both online and OMR exams)
      if (
        newTest.isOnlineExamination &&
        newTest.onlineExamQuestions.length > 0
      ) {
        // First save any custom questions to QB to get questionIds
        let questionsToSend = newTest.onlineExamQuestions;
        const hasCustom = questionsToSend.some(
          (q) => q._isCustom && !q.questionId,
        );
        if (hasCustom) {
          questionsToSend = await saveCustomQuestionsToQB(
            questionsToSend,
            newTest.testName,
          );
        }
        // Strip internal-only fields before sending
        const cleanQuestions = questionsToSend.map(
          ({ _sourceId, _sourceSolution, _isCustom, ...rest }) => rest,
        );
        formData.append("onlineExamQuestions", JSON.stringify(cleanQuestions));
      }

      if (newTest.omrTemplateId) {
        formData.append("omrTemplateId", newTest.omrTemplateId);
      }

      if (newTest.standardizedTestId) {
        formData.append("standardizedTestId", newTest.standardizedTestId);
      }

      if (newTest.certificateTemplateId) {
        formData.append("certificateTemplateId", newTest.certificateTemplateId);
      }

      if (newTest.annotationPrintingOffsetConfigId) {
        formData.append(
          "annotationPrintingOffsetConfigId",
          newTest.annotationPrintingOffsetConfigId,
        );
      }

      const response = await fetchWithAuth(`${serverUrl}/api/test/add`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to create test");

      const testId = data._id || data.testId;

      let promptResult = null;
      if (
        !newTest.isManualEntryQuestions &&
        newTest.testPaperURL &&
        generateAIPrompt
      ) {
        promptResult = await generateUserPrompt(testId, generateAIPrompt);
      }

      if (
        newTest.isOnlineExamination &&
        newTest.onlineExamQuestions?.length > 0
      ) {
        try {
          setUserPromptPhase("generating");
          setUserPromptProgress("Generating Question Paper PDF...");
          const res = await fetchWithAuth(
            `${serverUrl}/api/test/generate-question-paper/${testId}`,
          );
          if (res && res.ok) {
            const blob = await res.blob();
            setGeneratedTestPaperBlob(blob);
          }

          setUserPromptProgress("Generating Answer Key PDF...");
          const ansRes = await fetchWithAuth(
            `${serverUrl}/api/test/generate-answer-key/${testId}`,
          );
          if (ansRes && ansRes.ok) {
            const ansBlob = await ansRes.blob();
            setGeneratedAnswerKeyBlob(ansBlob);
          }

          setUserPromptProgress("Generating Solutions PDF...");
          const solRes = await fetchWithAuth(
            `${serverUrl}/api/test/generate-answer-key/${testId}?generateSolution=true`,
          );
          if (solRes && solRes.ok) {
            const solBlob = await solRes.blob();
            setGeneratedSolutionsBlob(solBlob);
          }
        } catch (error) {
          console.error("Error generating PDF on backend:", error);
        }
      }

      const hasScoreMismatch = Boolean(
        generateAIPrompt &&
        !newTest.isManualEntryQuestions &&
        newTest.testPaperURL &&
        promptResult &&
        promptResult.success &&
        !promptResult.isScoreMatch
      );

      setIsCreateModalOpen(false);
      setNewTest({
        testType: null,
        testName: "",
        testPaperURL: null,
        rubricURL: null,
        scheduledDate: "",
        dueDate: "",
        duration: "",
        capturedFile: null,
        isExternal: false,
        isManualEntryQuestions: false,
        manualEntryQuestions: [],
        isOnlineExamination: false,
        onlineExamQuestions: [],
        omrTemplateId: null,
        standardizedTestId: null,
        certificateTemplateId: null,
        annotationPrintingOffsetConfigId: null,
        resultsAvailableImmediately: false,
        isCalculatorAllowed: false,
        verifyAnswerPaperOnUpload: false,
      });
      setShowNewRubricForm(false);
      setIsSubmitting(false);
      setUserPromptPhase("");
      setUserPromptProgress("");

      if (hasScoreMismatch) {
        navigate(`/instructor/test-grading/${testId}?scoreMismatch=true`, {
          state: {
            scoreMismatch: true,
            openSpecialInstructions: true,
            promptMaxScore: promptResult.userPromptMaxScore,
            testMaxScore: promptResult.testMaxScore,
            initialUserPrompt: promptResult.userPrompt,
          },
        });
      } else {
        swalSuccess("Test created successfully.");
        if (
          newTest.isOnlineExamination &&
          newTest.onlineExamQuestions?.length > 0
        ) {
          setDownloadModalTestId(testId);
          setDownloadModalTestName(newTest.testName);
          setDownloadModalAction("navigate");
          setIsDownloadModalOpen(true);
        } else {
          navigate(`/instructor/test-grading/${testId}`);
        }
      }
    } catch (err) {
      setError(err.message);
      swalError(err.message);
      setIsSubmitting(false);
    }
  };

  const handleCreateHomework = async (e) => {
    e.preventDefault();

    if (!newHomework.homeworkName?.trim()) {
      swalError("Please enter a homework name.");
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("classId", classId);
      formData.append("testName", newHomework.homeworkName.trim());
      formData.append("maxScore", "100");
      formData.append("isHomeworkTest", "true");
      formData.append("isManualEntryQuestions", "false");
      formData.append("isOnlineExamination", "false");
      formData.append("status", newHomework.status || "Ready");
      if (newHomework.dueDate) {
        formData.append("dueDate", convertToUTC(newHomework.dueDate));
      }
      if (newHomework.testPaperURL) {
        formData.append("testPaper", newHomework.testPaperURL);
      }
      if (newHomework.answerKeyFile) {
        formData.append("answerKeysFile", newHomework.answerKeyFile);
      }

      const response = await fetchWithAuth(`${serverUrl}/api/test/add`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to create homework");
      }

      setIsCreateHomeworkModalOpen(false);
      setNewHomework({
        homeworkName: "",
        dueDate: "",
        status: "Ready",
        testPaperURL: null,
        capturedFile: null,
        answerKeyFile: null,
        answerKeyCapturedFile: null,
      });

      await fetchTests();
      swalSuccess("Homework created successfully!");
    } catch (err) {
      swalError(err.message || "Failed to create homework");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditHomework = (test) => {
    const formattedDueDate = test.dueDate
      ? convertToLocal(test.dueDate).slice(0, 16)
      : "";
    setEditHomework({
      id: test._id || test.id,
      homeworkName: test.testName || "",
      dueDate: formattedDueDate,
      initialDueDate: formattedDueDate,
      status: test.status || "Ready",
      initialStatus: test.status || "Ready",
      testPaperURL: null,
      capturedFile: null,
      removeTestPaper: false,
      originalTestPaperURL: test.testPaperURL || null,
      originalTestPaperFileName:
        test.testPaperFileName ||
        (test.testPaperURL ? test.testPaperURL.split("/").pop() : null),
      answerKeyFile: null,
      answerKeyCapturedFile: null,
      removeAnswerKey: false,
      originalAnswerKeysFileURL: test.answerKeysFileURL || null,
      originalAnswerKeysFileName:
        test.answerKeysFileName ||
        (test.answerKeysFileURL ? test.answerKeysFileURL.split("/").pop() : null),
    });
    setIsEditHomeworkModalOpen(true);
  };

  const handleEditHomework = async (e) => {
    e.preventDefault();

    if (!editHomework.homeworkName?.trim()) {
      swalError("Please enter a homework name.");
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("testName", editHomework.homeworkName.trim());

      if (editHomework.status) {
        formData.append("status", editHomework.status);
      }

      if (editHomework.dueDate !== editHomework.initialDueDate) {
        if (editHomework.dueDate) {
          formData.append("dueDate", convertToUTC(editHomework.dueDate));
        } else {
          formData.append("dueDate", "null");
        }
      }

      if (editHomework.testPaperURL) {
        formData.append("testPaper", editHomework.testPaperURL);
      } else if (editHomework.removeTestPaper) {
        formData.append("removeTestPaper", "true");
      }

      if (editHomework.answerKeyFile) {
        formData.append("answerKeysFile", editHomework.answerKeyFile);
      } else if (editHomework.removeAnswerKey) {
        formData.append("removeAnswerKeysFile", "true");
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/test/update/${editHomework.id}`,
        {
          method: "PUT",
          body: formData,
          credentials: "include",
        }
      );

      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to update homework");
      }

      setIsEditHomeworkModalOpen(false);
      setEditHomework({
        id: "",
        homeworkName: "",
        dueDate: "",
        initialDueDate: "",
        status: "Ready",
        initialStatus: "Ready",
        testPaperURL: null,
        capturedFile: null,
        removeTestPaper: false,
        originalTestPaperURL: null,
        originalTestPaperFileName: null,
        answerKeyFile: null,
        answerKeyCapturedFile: null,
        removeAnswerKey: false,
        originalAnswerKeysFileURL: null,
        originalAnswerKeysFileName: null,
      });

      await fetchTests();
      swalSuccess("Homework updated successfully!");
    } catch (err) {
      swalError(err.message || "Failed to update homework");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTest = async (e) => {
    e.preventDefault();
    if (!editTest.testName) {
      setFormError("Test name is required.");
      return;
    }

    if (editTest.isManualEntryQuestions) {
      if (
        !editTest.manualEntryQuestions ||
        editTest.manualEntryQuestions.length === 0
      ) {
        swalError("Please add at least one task for manual entry test.");
        return;
      }

      for (let i = 0; i < editTest.manualEntryQuestions.length; i++) {
        const q = editTest.manualEntryQuestions[i];
        if (
          !q.questionNumber ||
          !q.maxScore ||
          (!isK12School && (!q.questionText || !q.courseOutcome))
        ) {
          swalError(
            `Task ${i + 1}: Task Number and Max Score are required.${isK12School ? "" : " Task Description and CO are required."
            }`,
          );
          return;
        }
        if (q.maxScore <= 0) {
          swalError(`Task ${i + 1}: Max Score must be greater than 0.`);
          return;
        }
      }

      const totalTaskScore = roundTo2(
        editTest.manualEntryQuestions.reduce(
          (sum, q) => sum + (parseFloat(q.maxScore) || 0),
          0,
        ),
      );
      if (totalTaskScore !== roundTo2(editTest.maxScore)) {
        swalError(
          "The total max score from tasks must equal the overall max score.",
        );
        return;
      }

      const taskNumbers = editTest.manualEntryQuestions.map(
        (q) => q.questionNumber,
      );
      const uniqueTaskNumbers = new Set(taskNumbers);
      if (uniqueTaskNumbers.size !== taskNumbers.length) {
        swalError("Task numbers must be unique.");
        return;
      }
    }

    // Validate online exam questions if enabled (required for both tabs)
    if (editTest.isOnlineExamination) {
      if (
        !editTest.onlineExamQuestions ||
        editTest.onlineExamQuestions.length === 0
      ) {
        swalError("Please add at least one question for the Objective exam.");
        return;
      }
      for (let i = 0; i < editTest.onlineExamQuestions.length; i++) {
        const q = editTest.onlineExamQuestions[i];
        const isMcq = !q.type || q.type.toUpperCase() === "MCQ";
        const isObjectiveNonMcq =
          q.type &&
          (q.type.toUpperCase() === "FILL-IN-THE-BLANKS" ||
            q.type.toUpperCase() === "NUMERICAL");

        if (
          !q.questionText ||
          !q.maxScore ||
          q.maxScore <= 0 ||
          (isMcq &&
            (!q.correctOptions ||
              q.correctOptions.length === 0 ||
              !q.correctOptions[0])) ||
          (isObjectiveNonMcq &&
            (!q.correctAnswer || q.correctAnswer.trim() === ""))
        ) {
          const element = document.getElementById(`oe-question-edit-${i}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.style.borderColor = "#ef4444";
            element.style.boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.25)";
            setTimeout(() => {
              element.style.borderColor = "#e2e8f0";
              element.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
            }, 3000);
          }
          return;
        }
      }
    }

    const scheduled = editTest.scheduledDate
      ? new Date(editTest.scheduledDate)
      : null;
    const due = editTest.dueDate ? new Date(editTest.dueDate) : null;

    let skipDateValidation =
      !scheduled || isNaN(scheduled) || !due || isNaN(due);

    if (!skipDateValidation && due < scheduled) {
      swalError("Due Date must be later than Scheduled Date.");
      return;
    }

    let finalStatus = editTest.status;

    setIsSubmitting(true);
    setUserPromptPhase("analyzing");
    setUserPromptProgress("Preparing Test...");

    try {
      // Create inline rubric if needed
      let rubricIdToUse = editTest.rubricId;
      if (showNewRubricForm) {
        const newRubricId = await handleCreateInlineRubric(true);
        if (!newRubricId) {
          setIsSubmitting(false);
          return;
        }
        rubricIdToUse = newRubricId;
      }

      const formData = new FormData();
      if (editTest.testName !== initialEditTest.testName)
        formData.append("testName", editTest.testName);
      if (finalStatus !== initialEditTest.status && finalStatus)
        formData.append("status", finalStatus);
      if (String(editTest.maxScore) !== String(initialEditTest.maxScore))
        formData.append("maxScore", editTest.maxScore);
      if (String(editTest.isExternal) !== String(initialEditTest.isExternal)) {
        formData.append("isExternal", String(editTest.isExternal));
      }
      if (
        String(editTest.isManualEntryQuestions) !==
        String(initialEditTest.isManualEntryQuestions)
      ) {
        formData.append(
          "isManualEntryQuestions",
          String(editTest.isManualEntryQuestions),
        );
      }
      if (editTest.testPaperURL || generateAIPrompt !== true) {
        formData.append("generateAIPrompt", generateAIPrompt);
      }

      const currentGradingPolicy = editTest.gradingPolicy || "";
      const prevGradingPolicy = initialEditTest.gradingPolicy || "";
      if (currentGradingPolicy !== prevGradingPolicy) {
        formData.append(
          "gradingPolicy",
          currentGradingPolicy === "" ? "null" : currentGradingPolicy,
        );
      }

      const editTestTypeValue =
        editTest.testType?.value !== undefined
          ? editTest.testType.value
          : undefined;
      if (editTestTypeValue !== initialEditTest.testType) {
        if (editTestTypeValue !== undefined)
          formData.append("testType", editTestTypeValue);
      }

      if (editTest.scheduledDate !== initialEditTest.scheduledDate) {
        if (editTest.scheduledDate)
          formData.append(
            "scheduledDate",
            convertToUTC(editTest.scheduledDate),
          );
      }
      if (editTest.dueDate !== initialEditTest.dueDate) {
        if (editTest.dueDate)
          formData.append("dueDate", convertToUTC(editTest.dueDate));
        else formData.append("dueDate", "");
      }
      if (editTest.duration !== initialEditTest.duration) {
        formData.append("duration", String(roundTo2(editTest.duration)));
      }

      // Handle "Calculator Allowed" toggle change
      if (
        editTest.isCalculatorAllowed !== initialEditTest.isCalculatorAllowed
      ) {
        formData.append(
          "isCalculatorAllowed",
          String(editTest.isCalculatorAllowed),
        );
      }

      if (
        editTest.verifyAnswerPaperOnUpload !==
        initialEditTest.verifyAnswerPaperOnUpload
      ) {
        formData.append(
          "verifyAnswerPaperOnUpload",
          String(!!editTest.verifyAnswerPaperOnUpload),
        );
      }

      // Handle "Results Available Immediately" toggle change
      if (
        editTest.resultsAvailableImmediately !==
        initialEditTest.resultsAvailableImmediately
      ) {
        if (editTest.resultsAvailableImmediately) {
          formData.append("scorePublishedDate", new Date().toISOString());
        } else {
          formData.append("scorePublishedDate", "");
        }
      }

      if (!editTest.isManualEntryQuestions) {
        if (editTest.testPaperURL) {
          formData.append("testPaper", editTest.testPaperURL);
        }
        if (editTest.answerKeyFile) {
          formData.append("answerKeysFile", editTest.answerKeyFile);
        }
        if (editTest.removeAnswerKey) {
          formData.append("removeAnswerKeysFile", editTest.removeAnswerKey);
        }
        if (editTest.removeTestPaper) {
          formData.append("removeTestPaper", editTest.removeTestPaper);
          formData.append("userPrompt", "");
        }
      }

      if (rubricIdToUse !== initialEditTest.rubricId) {
        formData.append("rubricId", rubricIdToUse ? rubricIdToUse : "");
      }

      if (
        editTest.isManualEntryQuestions &&
        editTest.manualEntryQuestions.length > 0
      ) {
        const manualChanged =
          JSON.stringify(editTest.manualEntryQuestions) !==
          JSON.stringify(initialEditTest.manualEntryQuestions || []);
        if (
          manualChanged ||
          String(editTest.isManualEntryQuestions) !==
          String(initialEditTest.isManualEntryQuestions)
        ) {
          editTest.manualEntryQuestions.forEach((question, index) => {
            formData.append(
              `manualEntryQuestions[${index}][questionNumber]`,
              question.questionNumber,
            );
            formData.append(
              `manualEntryQuestions[${index}][questionText]`,
              question.questionText,
            );
            formData.append(
              `manualEntryQuestions[${index}][maxScore]`,
              question.maxScore,
            );
            if (question.courseOutcome) {
              formData.append(
                `manualEntryQuestions[${index}][courseOutcome]`,
                question.courseOutcome,
              );
            }
            if (question.bloomsLevel) {
              formData.append(
                `manualEntryQuestions[${index}][bloomsLevel]`,
                question.bloomsLevel,
              );
            }
          });
        }
      }

      // Append online exam questions as JSON when editing (for both online and OMR exams)
      if (editTest.isOnlineExamination) {
        formData.append("isOnlineExamination", true);

        const currentOptions = editTest.isAdvancedOnlineExam ? [editTest.advancedOnlineExamOptions || {}] : [];
        const initialOptions = initialEditTest.isAdvancedOnlineExam ? [initialEditTest.advancedOnlineExamOptions || {}] : [];
        if (JSON.stringify(currentOptions) !== JSON.stringify(initialOptions)) {
          formData.append("advancedOnlineExamOptions", JSON.stringify(currentOptions));
        }
        const onlineChanged =
          JSON.stringify(editTest.onlineExamQuestions) !==
          JSON.stringify(initialEditTest.onlineExamQuestions || []);
        if (onlineChanged) {
          // First save any custom questions to QB to get questionIds
          let questionsToSend = editTest.onlineExamQuestions;
          const hasCustom = questionsToSend.some(
            (q) => q._isCustom && !q.questionId,
          );
          if (hasCustom) {
            questionsToSend = await saveCustomQuestionsToQB(
              questionsToSend,
              editTest.testName,
            );
          }
          const cleanQuestions = questionsToSend.map(
            ({ _sourceId, _sourceSolution, _isCustom, ...rest }) => rest,
          );
          formData.append(
            "onlineExamQuestions",
            JSON.stringify(cleanQuestions),
          );
        }

        const omrChanged =
          editTest.omrTemplateId !== initialEditTest.omrTemplateId;
        if (omrChanged) {
          formData.append("omrTemplateId", editTest.omrTemplateId || "");
        }

        const standardizedTestChanged =
          editTest.standardizedTestId !== initialEditTest.standardizedTestId;
        if (standardizedTestChanged) {
          formData.append(
            "standardizedTestId",
            editTest.standardizedTestId || "",
          );
        }
        if (
          editTest.onlineExamPasscode &&
          editTest.onlineExamPasscode !== initialEditTest.onlineExamPasscode
        ) {
          formData.append("onlineExamPasscode", editTest.onlineExamPasscode);
        }
      }

      const certificateTemplateChanged =
        editTest.certificateTemplateId !== initialEditTest.certificateTemplateId;
      if (certificateTemplateChanged) {
        formData.append(
          "certificateTemplateId",
          editTest.certificateTemplateId || "",
        );
      }

      const offsetConfigChanged =
        editTest.annotationPrintingOffsetConfigId !==
        initialEditTest?.annotationPrintingOffsetConfigId;
      if (offsetConfigChanged) {
        formData.append(
          "annotationPrintingOffsetConfigId",
          editTest.annotationPrintingOffsetConfigId || "",
        );
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/test/update/${editTest.id}`,
        { method: "PUT", body: formData, credentials: "include" },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to update test");

      const maxScoreChanged =
        String(editTest.maxScore) !== String(initialEditTest?.maxScore);
      const hasTestPaper =
        editTest.testPaperURL ||
        (editTest.original?.testPaperFileName && !editTest.removeTestPaper);

      if (
        !editTest.isManualEntryQuestions &&
        (testPaperChanged || maxScoreChanged) &&
        hasTestPaper &&
        generateAIPrompt
      ) {
        await generateUserPrompt(editTest.id, generateAIPrompt);
      }

      const questionsChanged =
        (editTest.isOnlineExamination &&
          JSON.stringify(editTest.onlineExamQuestions || []) !==
          JSON.stringify(initialEditTest?.onlineExamQuestions || [])) ||
        (editTest.isManualEntryQuestions &&
          JSON.stringify(editTest.manualEntryQuestions || []) !==
          JSON.stringify(initialEditTest?.manualEntryQuestions || [])) ||
        Boolean(testPaperChanged);

      const startDateChanged =
        editTest.scheduledDate !== initialEditTest?.scheduledDate;

      const durationChanged =
        String(editTest.duration || "") !==
        String(initialEditTest?.duration || "");

      const shouldRegeneratePaper =
        questionsChanged || startDateChanged || durationChanged;

      if (
        shouldRegeneratePaper &&
        editTest.isOnlineExamination &&
        editTest.onlineExamQuestions?.length > 0
      ) {
        try {
          setUserPromptPhase("generating");
          setUserPromptProgress("Generating Question Paper PDF...");
          const res = await fetchWithAuth(
            `${serverUrl}/api/test/generate-question-paper/${editTest.id}`,
          );
          if (res && res.ok) {
            const blob = await res.blob();
            setGeneratedTestPaperBlob(blob);
          }

          setUserPromptProgress("Generating Answer Key PDF...");
          const ansRes = await fetchWithAuth(
            `${serverUrl}/api/test/generate-answer-key/${editTest.id}`,
          );
          if (ansRes && ansRes.ok) {
            const ansBlob = await ansRes.blob();
            setGeneratedAnswerKeyBlob(ansBlob);
          }

          setUserPromptProgress("Generating Solutions PDF...");
          const solRes = await fetchWithAuth(
            `${serverUrl}/api/test/generate-answer-key/${editTest.id}?generateSolution=true`,
          );
          if (solRes && solRes.ok) {
            const solBlob = await solRes.blob();
            setGeneratedSolutionsBlob(solBlob);
          }
        } catch (error) {
          console.error("Error generating PDF on backend:", error);
        }
      }

      swalSuccess("Test updated successfully.");
      setIsEditModalOpen(false);
      setEditTest({
        id: editTest.id,
        testType: null,
        testName: "",
        maxScore: "",
        testPaperURL: null,
        rubricId: null,
        scheduledDate: "",
        dueDate: "",
        duration: "",
        removeTestPaper: false,
        capturedFile: null,
        isExternal: false,
        isManualEntryQuestions: false,
        manualEntryQuestions: [],
        original: {},
        resultsAvailableImmediately: false,
        isCalculatorAllowed: false,
        standardizedTestId: null,
        certificateTemplateId: null,
      });
      setInitialEditTest(null);
      setIsEditChanged(false);
      setTestPaperChanged(false);
      setShowNewRubricForm(false);
      setIsSubmitting(false);
      setUserPromptPhase("");
      setUserPromptProgress("");
      fetchTests();

      if (
        shouldRegeneratePaper &&
        editTest.isOnlineExamination &&
        editTest.onlineExamQuestions?.length > 0
      ) {
        setDownloadModalTestId(editTest.id);
        setDownloadModalTestName(editTest.testName);
        setDownloadModalAction("close");
        setIsDownloadModalOpen(true);
      }
    } catch (err) {
      setError(err.message);
      swalError(err.message);
      setIsSubmitting(false);
    }
  };

  // Handle Edit Test - Update to include manual entry questions
  const handleEditInputChange = async (e) => {
    const { name, value, files, type, checked } = e.target;
    let updatedValue = files ? files[0] : value;
    // Checkbox
    if (type === "checkbox") {
      updatedValue = checked;
    }

    setEditTest((prev) => ({
      ...prev,
      [name]: updatedValue,
      ...(name === "testPaperURL" && {
        removeTestPaper: false,
        capturedFile: null, // Reset captured file when new file is selected
      }),
    }));

    // Track if test paper was changed
    if (name === "testPaperURL" && files && files[0]) {
      setTestPaperChanged(true);
      if (generateAIPrompt) {
        await generateUserPrompt(editTest.id, generateAIPrompt);
      }
    }

    // If user toggles Manual Entry ON in Edit modal, fetch course outcomes (once)
    if (
      name === "isManualEntryQuestions" &&
      updatedValue &&
      courseOutcomes.length === 0
    ) {
      fetchCourseOutcomes();
    }

    if (name === "testPaperURL" && files && files[0]) {
      setTestPaperChanged(true);
      // Auto-regenerate always when paper changes, but show progress only if toggle is on
      await generateUserPrompt(editTest.id, generateAIPrompt);
    }

    if (initialEditTest) {
      const originalValue = initialEditTest[name];
      if (files) {
        setIsEditChanged(files[0] !== originalValue);
      } else {
        setIsEditChanged(updatedValue !== originalValue);
      }
    } else {
      setIsEditChanged(true);
    }
  };

  const handleNewRubricInputChange = (e, isEditMode = false) => {
    const { name, value, files } = e.target;
    setNewRubricData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
      ...(name === "file" && files && { capturedFile: null, text: "" }),
      ...(name === "text" && value && { capturedFile: null, file: null }),
    }));

    // Mark as changed when in edit mode
    if (isEditMode) {
      setIsEditChanged(true);
    }
  };

  const renderNewRubricForm = (isEdit = false) => {
    return (
      <div
        style={{
          position: "relative", // Keep for close button
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "1rem",
          marginBottom: "1rem",
          backgroundColor: "#f9f9f9",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Close button in top-right corner */}
        <button
          type="button"
          onClick={() => {
            setShowNewRubricForm(false);
            setNewRubricData({
              name: "",
              file: null,
              text: "",
              capturedFile: null,
            });
            // Reset isEditChanged if in edit mode
            if (isEdit) {
              setIsEditChanged(false);
            }
          }}
          disabled={isSubmitting}
          style={{
            position: "absolute",
            top: "0.75rem",
            right: "0.75rem",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "none",
            background: "#dc3545",
            color: "white",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.2rem",
            fontWeight: "bold",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            opacity: isSubmitting ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = "#c82333";
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#dc3545";
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
          }}
          aria-label="Close new rubric form"
        >
          ×
        </button>

        <h3
          style={{
            marginBottom: "1rem",
            fontSize: "1.1rem",
            color: "#333",
            paddingRight: "2rem",
          }}
        >
          Create New Rubric
        </h3>

        <div className="form-group">
          <label>
            <i className="fa-solid fa-asterisk requiredIcon"></i> Rubric Name
          </label>
          <input
            type="text"
            name="name"
            value={newRubricData.name}
            onChange={(e) => handleNewRubricInputChange(e, isEdit)}
            placeholder="Enter rubric name"
            disabled={isSubmitting}
            required
          />
        </div>

        <div className="form-group">
          <label>
            Rubric File <span className="optional-text">(Optional)</span>
          </label>
          <FileUploadSection
            currentFileUrl={null}
            currentFileName={null}
            isSubmitting={isSubmitting}
            accept=".pdf,.png,.jpg,.jpeg"
            onFileChange={(file, isCaptured) => {
              setNewRubricData((prev) => ({
                ...prev,
                file: file,
                capturedFile: isCaptured ? file : null,
                text: "", // Clear text when file is selected
              }));
              // Mark as changed when in edit mode
              if (isEdit) {
                setIsEditChanged(true);
              }
            }}
            onCapture={(callback) => {
              setCaptureFor(isEdit ? "edit-rubric" : "create-rubric");
              setIsCapturing(true);
              window.__fileUploadCallback = callback;
            }}
            onRemoveSelectedFile={() => {
              setNewRubricData((prev) => ({
                ...prev,
                file: null,
                capturedFile: null,
              }));
              // Mark as changed when in edit mode
              if (isEdit) {
                setIsEditChanged(true);
              }
            }}
            onPreviewFile={(url) => {
              window.open(url, "_blank");
            }}
            selectedFile={newRubricData.file}
            isCaptured={newRubricData.capturedFile !== null}
            browseTitle="Choose File"
            browseDescription="Select PDF or image file"
            captureTitle="Capture with Camera"
            captureDescription="Take photos and convert to PDF"
            additionalDisableCondition={!!newRubricData.text}
          />
        </div>

        <div className="form-group">
          <label>
            Rubric Text <span className="optional-text">(Optional)</span>
          </label>
          <textarea
            name="text"
            value={newRubricData.text}
            onChange={(e) => handleNewRubricInputChange(e, isEdit)}
            placeholder="Enter rubric text..."
            disabled={
              newRubricData.file || newRubricData.capturedFile || isSubmitting
            }
            rows={1}
            style={{ height: "100px" }}
          />
          <small className="helper-text">
            Provide either a file or text for the rubric.
          </small>
        </div>
      </div>
    );
  };

  const renderTestPaperUpload = (isEdit = false, excludeRubric = false) => {
    const test = isEdit ? editTest : newTest;

    const hasOriginalFile =
      isEdit &&
      test.original.testPaperFileName &&
      !test.removeTestPaper &&
      !test.testPaperURL;

    return (
      <div className="form-group">
        <label>
          Test Paper <span className="optional-text">(Optional)</span>
        </label>

        <FileUploadSection
          currentFileUrl={hasOriginalFile ? test.original.testPaperURL : null}
          currentFileName={
            hasOriginalFile
              ? test.original.testPaperFileName.split("/").pop()
              : null
          }
          isSubmitting={isSubmitting}
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          onFileChange={(file, isCaptured) => {
            if (isEdit) {
              setEditTest((prev) => ({
                ...prev,
                testPaperURL: file,
                capturedFile: isCaptured ? file : null,
                removeTestPaper: false,
              }));
              setIsEditChanged(true);
              setTestPaperChanged(true);
            } else {
              setNewTest((prev) => ({
                ...prev,
                testPaperURL: file,
                capturedFile: isCaptured ? file : null,
              }));
            }
          }}
          onCapture={(callback) => {
            setCaptureFor(isEdit ? "edit" : "create");
            setIsCapturing(true);
            window.__fileUploadCallback = callback;
          }}
          onRemoveCurrentFile={
            isEdit
              ? () => {
                setEditTest((prev) => ({
                  ...prev,
                  testPaperURL: null,
                  removeTestPaper: true,
                  capturedFile: null,
                  verifyAnswerPaperOnUpload: false,
                }));
                setGenerateAIPrompt(false);
                setIsEditChanged(true);
                setTestPaperChanged(false);
              }
              : undefined
          }
          onRemoveSelectedFile={() => {
            if (isEdit) {
              setEditTest((prev) => {
                const hasOriginalPaper =
                  prev.original?.testPaperFileName && !prev.removeTestPaper;
                return {
                  ...prev,
                  testPaperURL: null,
                  capturedFile: null,
                  verifyAnswerPaperOnUpload: hasOriginalPaper
                    ? prev.verifyAnswerPaperOnUpload
                    : false,
                };
              });
              setGenerateAIPrompt(false);
              setIsEditChanged(true);
              setTestPaperChanged(false);
            } else {
              setNewTest((prev) => ({
                ...prev,
                testPaperURL: null,
                capturedFile: null,
                verifyAnswerPaperOnUpload: false,
              }));
            }
          }}
          onPreviewFile={(url) => {
            window.open(url, "_blank");
          }}
          selectedFile={test.testPaperURL}
          isCaptured={test.capturedFile !== null}
          browseTitle="Choose File"
          browseDescription="Select PDF, Doc or image file from device"
          captureTitle="Capture with Camera"
          captureDescription="Take photos and convert to PDF"
        />

        <small className="helper-text">
          Upload a test paper file (pdf, doc, docx, jpg, png only) or capture using camera
          (only on mobile phone)
        </small>
      </div>
    );
  };

  const renderAnswerKeyUpload = (isEdit = false) => {
    const test = isEdit ? editTest : newTest;

    const hasOriginalFile =
      isEdit &&
      test.original.answerKeysFileName &&
      !test.removeAnswerKey &&
      !test.answerKeyFile;

    return (
      <div className="form-group">
        <label>
          Answer Key <span className="optional-text">(Optional)</span>
        </label>

        <FileUploadSection
          currentFileUrl={
            hasOriginalFile ? test.original.answerKeysFileURL : null
          }
          currentFileName={
            hasOriginalFile
              ? test.original.answerKeysFileName.split("/").pop()
              : null
          }
          isSubmitting={isSubmitting}
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          onFileChange={(file, isCaptured) => {
            if (isEdit) {
              setEditTest((prev) => ({
                ...prev,
                answerKeyFile: file,
                answerKeyCapturedFile: isCaptured ? file : null,
                removeAnswerKey: false,
              }));
              setIsEditChanged(true);
            } else {
              setNewTest((prev) => ({
                ...prev,
                answerKeyFile: file,
                answerKeyCapturedFile: isCaptured ? file : null,
              }));
            }
          }}
          onCapture={(callback) => {
            setCaptureFor(isEdit ? "edit-answerkey" : "create-answerkey");
            setIsCapturing(true);
            window.__fileUploadCallback = callback;
          }}
          onRemoveCurrentFile={
            isEdit
              ? () => {
                setEditTest((prev) => ({
                  ...prev,
                  answerKeyFile: null,
                  removeAnswerKey: true,
                  answerKeyCapturedFile: null,
                }));
                setIsEditChanged(true);
              }
              : undefined
          }
          onRemoveSelectedFile={() => {
            if (isEdit) {
              setEditTest((prev) => ({
                ...prev,
                answerKeyFile: null,
                answerKeyCapturedFile: null,
              }));
              setIsEditChanged(true);
            } else {
              setNewTest((prev) => ({
                ...prev,
                answerKeyFile: null,
                answerKeyCapturedFile: null,
              }));
            }
          }}
          onPreviewFile={(url) => {
            window.open(url, "_blank");
          }}
          selectedFile={test.answerKeyFile}
          isCaptured={test.answerKeyCapturedFile !== null}
          browseTitle="Choose File"
          browseDescription="Select PDF, Doc, or image file from device"
          captureTitle="Capture with Camera"
          captureDescription="Take photos and convert to PDF"
        />

        <small className="helper-text">
          Upload answer key file for objective questions (MCQ, Fill in the
          Blanks, etc.) to improve grading accuracy
        </small>
      </div>
    );
  };

  // New function to render Rubric section separately
  const renderRubricSection = (isEdit = false) => {
    const test = isEdit ? editTest : newTest;

    return (
      <div className="form-group">
        <label>
          Rubric <span className="optional-text">(Optional)</span>
        </label>
        {!showNewRubricForm ? (
          <>
            <Select
              options={[
                ...rubrics.map((rubric) => ({
                  label: rubric.name,
                  value: rubric._id,
                })),
                {
                  value: "new",
                  label: "Create New Rubric ➕",
                },
              ]}
              value={
                test.rubricId
                  ? {
                    value: test.rubricId,
                    label: getRubricsNameById(test.rubricId),
                  }
                  : null
              }
              onChange={(selected) => {
                if (selected?.value === "new") {
                  setShowNewRubricForm(true);
                  if (isEdit) {
                    setEditTest((prev) => ({ ...prev, rubricId: null }));
                    setIsEditChanged(true); // Mark as changed when showing new rubric form
                  } else {
                    setNewTest((prev) => ({ ...prev, rubricId: null }));
                  }
                } else {
                  const newValue = selected ? selected.value : null;
                  if (isEdit) {
                    setEditTest((prev) => ({ ...prev, rubricId: newValue }));
                    if (initialEditTest) {
                      const isChanged = newValue !== initialEditTest.rubricId;
                      setIsEditChanged(isChanged);
                    } else {
                      setIsEditChanged(true);
                    }
                  } else {
                    setNewTest((prev) => ({ ...prev, rubricId: newValue }));
                  }
                }
              }}
              placeholder="Select Rubric or Create New"
              isClearable
              isDisabled={isSubmitting}
              styles={{
                option: (provided, state) => ({
                  ...provided,
                  ...(state.data.value === "new" && {
                    backgroundColor: state.isFocused ? "#e3f2fd" : "#f0f8ff",
                    color: "#1976d2",
                    fontWeight: "600",
                    borderTop: "2px solid #e0e0e0",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#bbdefb",
                    },
                  }),
                }),
              }}
            />
          </>
        ) : (
          renderNewRubricForm(isEdit)
        )}
      </div>
    );
  };

  const renderGradingPolicySection = (isEdit = false) => {
    const test = isEdit ? editTest : newTest;
    const setTest = isEdit ? setEditTest : setNewTest;

    const gradingPolicyOptions = [
      { value: "Lenient", label: "Lenient" },
      { value: "Strict", label: "Strict" },
    ];

    return (
      <div className="form-group">
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontWeight: "600", fontSize: "1rem" }}>
            Grading Policy <span className="optional-text">(Optional)</span>
          </span>
        </label>
        <Select
          options={gradingPolicyOptions}
          value={
            test.gradingPolicy
              ? { value: test.gradingPolicy, label: test.gradingPolicy }
              : null
          }
          onChange={(selected) => {
            setTest((prev) => ({
              ...prev,
              gradingPolicy: selected ? selected.value : null,
            }));
            if (isEdit) setIsEditChanged(true);
          }}
          placeholder="Select Grading Policy"
          isClearable
          isDisabled={isSubmitting}
          styles={{
            control: (base) => ({
              ...base,
              minHeight: "40px",
            }),
          }}
        />
        <small
          className="helper-text"
          style={{ display: "block", marginTop: "0.5rem", color: "#666" }}
        >
          Choose your own grading policy. We recommend using the default AI
          grading policy for the best results — override it only if necessary.
        </small>


      </div>
    );
  };

  const handleManualQuestionAdd = (isEdit = false) => {
    const newQuestion = {
      questionNumber: "",
      questionText: "",
      maxScore: "",
      courseOutcome: "",
      bloomsLevel: "",
    };

    if (isEdit) {
      setEditTest((prev) => ({
        ...prev,
        manualEntryQuestions: [...prev.manualEntryQuestions, newQuestion],
      }));
      setIsEditChanged(true);
    } else {
      setNewTest((prev) => ({
        ...prev,
        manualEntryQuestions: [...prev.manualEntryQuestions, newQuestion],
      }));
    }
  };

  const handleManualQuestionChange = (index, field, value, isEdit = false) => {
    if (isEdit) {
      setEditTest((prev) => {
        const updated = [...prev.manualEntryQuestions];
        updated[index] = { ...updated[index], [field]: value };
        return { ...prev, manualEntryQuestions: updated };
      });
      setIsEditChanged(true);
    } else {
      setNewTest((prev) => {
        const updated = [...prev.manualEntryQuestions];
        updated[index] = { ...updated[index], [field]: value };
        return { ...prev, manualEntryQuestions: updated };
      });
    }
  };

  const handleManualQuestionRemove = (index, isEdit = false) => {
    if (isEdit) {
      setEditTest((prev) => ({
        ...prev,
        manualEntryQuestions: prev.manualEntryQuestions.filter(
          (_, i) => i !== index,
        ),
      }));
      setIsEditChanged(true);
    } else {
      setNewTest((prev) => ({
        ...prev,
        manualEntryQuestions: prev.manualEntryQuestions.filter(
          (_, i) => i !== index,
        ),
      }));
    }
  };

  // Render manual entry questions table
  const renderManualEntryQuestionsTable = (isEdit = false) => {
    const test = isEdit ? editTest : newTest;
    const questions = test.manualEntryQuestions || [];

    const headers = [
      {
        key: "questionNumber",
        label: "Task Number",
        width: "120px",
        required: true,
        placeholder: "e.g., 1",
      },
      ...(instituteData?.category !== "K12_SCHOOL"
        ? [
          {
            key: "questionText",
            label: "Task",
            width: "1fr",
            required: true,
            placeholder: "Enter task description",
          },
        ]
        : []),
      {
        key: "maxScore",
        label: "Max Score",
        width: "120px",
        required: true,
        placeholder: "e.g., 10",
      },
      ...(instituteData?.category !== "K12_SCHOOL"
        ? [
          {
            key: "courseOutcome",
            label: "CO",
            width: "150px",
            required: true,
          }, // Make required
          {
            key: "bloomsLevel",
            label: "BL",
            width: "150px",
            required: false,
          }, // Keep optional
        ]
        : []),
      { key: "actions", label: "Actions", width: "80px" },
    ];

    const renderCell = (header, row, rowIndex) => {
      switch (header.key) {
        case "questionNumber":
          return (
            <input
              type="text"
              value={row.questionNumber || ""}
              onChange={(e) =>
                handleManualQuestionChange(
                  rowIndex,
                  "questionNumber",
                  e.target.value,
                  isEdit,
                )
              }
              placeholder={header.placeholder}
              disabled={isSubmitting}
              className="modal-table-input"
            />
          );
        case "questionText":
          return (
            <input
              type="text"
              value={row.questionText || ""}
              onChange={(e) =>
                handleManualQuestionChange(
                  rowIndex,
                  "questionText",
                  e.target.value,
                  isEdit,
                )
              }
              placeholder={header.placeholder}
              disabled={isSubmitting}
              className="modal-table-input"
            />
          );
        case "maxScore":
          return (
            <input
              type="number"
              value={row.maxScore || ""}
              onChange={(e) =>
                handleManualQuestionChange(
                  rowIndex,
                  "maxScore",
                  e.target.value,
                  isEdit,
                )
              }
              placeholder={header.placeholder}
              disabled={isSubmitting}
              min="0"
              step="0.5"
              className="modal-table-input"
            />
          );
        case "courseOutcome":
          return (
            <Select
              options={courseOutcomes} // Use fetched courseOutcomes
              value={
                row.courseOutcome
                  ? { value: row.courseOutcome, label: row.courseOutcome }
                  : null
              }
              onChange={(selected) =>
                handleManualQuestionChange(
                  rowIndex,
                  "courseOutcome",
                  selected ? selected.value : "",
                  isEdit,
                )
              }
              placeholder="Select CO"
              isClearable
              isDisabled={isSubmitting}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "36px",
                  fontSize: "14px",
                }),
              }}
            />
          );
        case "bloomsLevel":
          return (
            <Select
              options={bloomsLevelOptions}
              value={
                row.bloomsLevel
                  ? { value: row.bloomsLevel, label: row.bloomsLevel }
                  : null
              }
              onChange={(selected) =>
                handleManualQuestionChange(
                  rowIndex,
                  "bloomsLevel",
                  selected ? selected.value : "",
                  isEdit,
                )
              }
              placeholder="Select BL"
              isClearable
              isDisabled={isSubmitting}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "36px",
                  fontSize: "14px",
                }),
              }}
            />
          );
        case "actions":
          return (
            <button
              type="button"
              onClick={() => handleManualQuestionRemove(rowIndex, isEdit)}
              className="modal-table-remove-btn"
              disabled={isSubmitting}
              style={{ padding: "4px 8px", fontSize: "14px" }}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          );
        default:
          return null;
      }
    };

    return (
      <div className="form-group" style={{ marginTop: "20px" }}>
        <label>
          <i className="fa-solid fa-asterisk requiredIcon"></i> Tasks
        </label>
        <ModalTableInput
          headers={headers}
          rows={questions}
          onRowChange={() => { }}
          onRowRemove={() => { }}
          onRowAdd={() => handleManualQuestionAdd(isEdit)}
          renderCell={renderCell}
          addButtonText="Add Task"
        />
      </div>
    );
  };

  // ── Online Exam Builder Render ──────────────────────────────────
  const renderOnlineExamBuilder = (isEdit = false) => {
    const test = isEdit ? editTest : newTest;
    const questions = test.onlineExamQuestions || [];
    const totalMarks = roundTo2(
      questions.reduce(
        (sum, q) => sum + (parseFloat(q.maxScore) || 0),
        0,
      ),
    );
    const isHigherEd = instituteData?.category === "HIGHER_EDUCATION";

    // Determine common score if all questions have the same score
    const allSameScore =
      questions.length > 0 &&
      questions.every(
        (q) => parseFloat(q.maxScore) === parseFloat(questions[0].maxScore),
      );
    const commonScoreValue = allSameScore ? questions[0].maxScore : "";

    return (
      <div className="online-exam-builder" style={{ marginTop: "1.5rem" }}>
        <div
          className="oeb-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <h3 className="oeb-title" style={{ margin: 0 }}>
            <i className="fa-solid fa-clipboard-question"></i>
            Exam Questions
            {questions.length > 0 && (
              <span className="oeb-count">
                ({questions.length} question{questions.length !== 1 ? "s" : ""}{" "}
                · {totalMarks} marks)
              </span>
            )}
          </h3>
        </div>

        {/* 3-Option Add Panel */}
        <div className="oeb-add-panel">
          <button
            type="button"
            className="oeb-add-card"
            onClick={() => {
              setGenerateQFor(isEdit ? "edit" : "create");
              setIsGenerateQuestionsOpen(true);
            }}
            disabled={isSubmitting}
          >
            <div
              className="oeb-add-card-icon"
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
              }}
            >
              <i className="fa-solid fa-wand-magic-sparkles"></i>
            </div>
            <div className="oeb-add-card-content">
              <span className="oeb-add-card-title">Generate with AI</span>
              <span className="oeb-add-card-desc">
                Auto-generate MCQs from a topic
              </span>
            </div>
          </button>

          <button
            type="button"
            className="oeb-add-card"
            onClick={() => {
              setMcqPickerFor(isEdit ? "edit" : "create");
              if (availableQuestionBanks.length === 0) fetchQuestionBanks();
              setIsMcqPickerOpen(true);
            }}
            disabled={isSubmitting}
          >
            <div
              className="oeb-add-card-icon"
              style={{
                background:
                  "linear-gradient(135deg, var(--color-1), var(--color-2))",
              }}
            >
              <i className="fa-solid fa-database"></i>
            </div>
            <div className="oeb-add-card-content">
              <span className="oeb-add-card-title">From Question Bank</span>
              <span className="oeb-add-card-desc">
                Pick from existing questions
              </span>
            </div>
          </button>

          <button
            type="button"
            className="oeb-add-card"
            onClick={() => {
              setMcqPickerFor(isEdit ? "edit" : "create");
              setIsCustomQuestionOpen(true);
            }}
            disabled={isSubmitting}
          >
            <div
              className="oeb-add-card-icon"
              style={{
                background: "linear-gradient(135deg, #059669, #34d399)",
              }}
            >
              <i className="fa-solid fa-pen-fancy"></i>
            </div>
            <div className="oeb-add-card-content">
              <span className="oeb-add-card-title">Create Manually</span>
              <span className="oeb-add-card-desc">
                Write your own MCQ question
              </span>
            </div>
          </button>

          <button
            type="button"
            className="oeb-add-card oeb-add-card-featured"
            onClick={() => {
              const testTitle = isEdit ? editTest.testName : newTest.testName;
              if (!testTitle || testTitle.trim() === "") {
                const inputId = isEdit ? "editTestNameInput" : "testNameInput";
                const inputElement = document.getElementById(inputId);
                if (inputElement) {
                  inputElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  inputElement.focus({ preventScroll: true });
                }
                return;
              }
              setExtractPdfForm((prev) => ({
                ...prev,
                questionBankName: testTitle,
              }));
              setExtractPdfFor(isEdit ? "edit" : "create");
              setIsExtractPdfOpen(true);
            }}
            disabled={isSubmitting}
          >
            <div
              className="oeb-add-card-icon"
              style={{
                background: "linear-gradient(135deg, #dc2626, #f97316)",
              }}
            >
              <i className="fa-solid fa-file-pdf"></i>
            </div>
            <div className="oeb-add-card-content">
              <span className="oeb-add-card-title">From Test Paper</span>
              <span className="oeb-add-card-desc">
                Convert physical paper to MCQ exam
              </span>
            </div>
            <span className="oeb-new-badge">NEW</span>
          </button>

          <button
            type="button"
            className={`oeb-add-card ${!hasStandardizedTemplates ? "oeb-add-card-locked" : ""}`}
            onClick={() => {
              if (!hasStandardizedTemplates) {
                setIsEnableFeatureModalOpen(true);
                return;
              }
              const mode = isEdit ? "edit" : "create";
              setLibraryExamFor(mode);
              setModalStandardizedTestId(null);
              setLibraryQuestionsLoaded(false);
              setLibraryRandomCount("");
              setLastPickedRandomCount(null);
              setLibraryRandomSortedPool(null);
              setIsLibraryBankModalOpen(true);
            }}
            disabled={isSubmitting}
            title={
              !hasStandardizedTemplates
                ? "Locked: Click to explore Competitive Exam Library (JEE • NEET • CUET)"
                : "Import from shared question library"
            }
          >
            {!hasStandardizedTemplates && (
              <span className="oeb-locked-badge">
                <i className="fa-solid fa-lock"></i> Locked
              </span>
            )}
            <div
              className="oeb-add-card-icon"
              style={{
                background: !hasStandardizedTemplates
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #8b5cf6, #6d28d9)",
              }}
            >
              <i
                className={
                  !hasStandardizedTemplates
                    ? "fa-solid fa-lock"
                    : "fa-solid fa-book-open"
                }
              ></i>
            </div>
            <div className="oeb-add-card-content">
              <span
                className="oeb-add-card-title"
                style={!hasStandardizedTemplates ? { color: "#64748b" } : undefined}
              >
                Library Bank
              </span>
              <span
                className="oeb-add-card-desc"
                style={!hasStandardizedTemplates ? { color: "#94a3b8" } : undefined}
              >
                {!hasStandardizedTemplates
                  ? "JEE • NEET • CUET Practice"
                  : "Import from shared question library"}
              </span>
            </div>
          </button>
        </div>
        {(() => {
          // ── Subject grouping logic ──
          const subjectSet = new Set(
            questions.map((q) => q.subject || "General").filter(Boolean),
          );
          const subjects = [...subjectSet];
          const hasSubjects = subjects.length > 1 || (subjects.length === 1 && subjects[0] !== "General");
          const currentTab = hasSubjects ? activeSubjectTab : "All";
          const filteredQuestions =
            currentTab === "All"
              ? questions
              : questions.filter(
                (q) => (q.subject || "General") === currentTab,
              );

          // Common score for filtered questions
          const filteredAllSameScore =
            filteredQuestions.length > 0 &&
            filteredQuestions.every(
              (q) =>
                parseFloat(q.maxScore) ===
                parseFloat(filteredQuestions[0].maxScore),
            );
          const filteredCommonScore = filteredAllSameScore
            ? filteredQuestions[0].maxScore
            : "";

          // Common duration for filtered questions (advanced only)
          const filteredAllSameDuration =
            test.isAdvancedOnlineExam &&
            filteredQuestions.length > 0 &&
            filteredQuestions.every(
              (q) =>
                parseFloat(q.duration) ===
                parseFloat(filteredQuestions[0].duration),
            );
          const filteredCommonDuration = filteredAllSameDuration
            ? filteredQuestions[0].duration
            : "";

          const totalDuration = roundTo2(
            questions.reduce(
              (sum, q) => sum + (parseFloat(q.duration) || 0),
              0,
            ),
          );

          return (
            <>
              {/* Subject Tabs */}
              {hasSubjects && questions.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    marginBottom: "0.75rem",
                    padding: "0.5rem",
                    background: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setActiveSubjectTab("All")}
                    style={{
                      padding: "6px 16px",
                      borderRadius: "20px",
                      border:
                        currentTab === "All"
                          ? "2px solid var(--color-1)"
                          : "1px solid #cbd5e1",
                      background:
                        currentTab === "All"
                          ? "linear-gradient(135deg, var(--color-1), var(--color-2))"
                          : "#fff",
                      color: currentTab === "All" ? "#fff" : "#475569",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    All ({questions.length})
                  </button>
                  {subjects.map((subj) => {
                    const count = questions.filter(
                      (q) => (q.subject || "General") === subj,
                    ).length;
                    return (
                      <button
                        key={subj}
                        type="button"
                        onClick={() => setActiveSubjectTab(subj)}
                        style={{
                          padding: "6px 16px",
                          borderRadius: "20px",
                          border:
                            currentTab === subj
                              ? "2px solid var(--color-1)"
                              : "1px solid #cbd5e1",
                          background:
                            currentTab === subj
                              ? "linear-gradient(135deg, var(--color-1), var(--color-2))"
                              : "#fff",
                          color:
                            currentTab === subj ? "#fff" : "#475569",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        {subj} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Bulk inputs bar */}
              {questions.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    background: "#f8fafc",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    flexWrap: "wrap",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.85rem",
                        color: "#475569",
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                      {hasSubjects && currentTab !== "All"
                        ? `Marks (${currentTab}):`
                        : "Marks Per Question:"}
                    </label>
                    <input
                      type="number"
                      value={
                        hasSubjects && currentTab !== "All"
                          ? (filteredCommonScore !== "" ? roundTo2(filteredCommonScore) : "")
                          : (commonScoreValue !== "" ? roundTo2(commonScoreValue) : "")
                      }
                      min="0.5"
                      step="0.5"
                      placeholder="Mixed"
                      onChange={(e) => {
                        if (hasSubjects && currentTab !== "All") {
                          handleCommonMaxScoreChangeScoped(
                            e.target.value,
                            isEdit,
                            currentTab,
                          );
                        } else {
                          handleCommonMaxScoreChange(
                            e.target.value,
                            isEdit,
                          );
                        }
                      }}
                      style={{
                        width: "70px",
                        padding: "4px 8px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "0.85rem",
                        textAlign: "center",
                      }}
                      disabled={isSubmitting}
                      title="Change max score for questions"
                    />
                  </div>

                  {test.isAdvancedOnlineExam && test.advancedOnlineExamOptions?.isTimedExam && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "0.85rem",
                          color: "#475569",
                          fontWeight: 600,
                          margin: 0,
                        }}
                      >
                        {hasSubjects && currentTab !== "All"
                          ? `Duration (${currentTab}):`
                          : "Duration Per Question:"}
                      </label>
                      <input
                        type="number"
                        value={
                          hasSubjects && currentTab !== "All"
                            ? (filteredCommonDuration !== "" ? roundTo2(filteredCommonDuration) : "")
                            : (() => {
                              const allSameDur =
                                questions.length > 0 &&
                                questions.every(
                                  (q) =>
                                    parseFloat(q.duration) ===
                                    parseFloat(questions[0].duration),
                                );
                              return allSameDur
                                ? (questions[0].duration !== "" && questions[0].duration != null ? roundTo2(questions[0].duration) : "")
                                : "";
                            })()
                        }
                        min="0.5"
                        step="0.5"
                        placeholder="Mixed"
                        onChange={(e) =>
                          handleCommonDurationChange(
                            e.target.value,
                            isEdit,
                            hasSubjects && currentTab !== "All"
                              ? currentTab
                              : null,
                          )
                        }
                        style={{
                          width: "70px",
                          padding: "4px 8px",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                          textAlign: "center",
                        }}
                        disabled={isSubmitting}
                        title="Change duration for questions (minutes)"
                      />
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#94a3b8",
                        }}
                      >
                        min
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Question list */}
              {filteredQuestions.length === 0 && questions.length === 0 ? (
                <div className="oeb-empty-state">
                  <i className="fa-solid fa-clipboard-question"></i>
                  <p>No questions added yet</p>
                  <span>
                    Use one of the options above to add questions to your exam.
                  </span>
                </div>
              ) : filteredQuestions.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#94a3b8",
                    fontSize: "0.9rem",
                  }}
                >
                  No questions in this subject.
                </div>
              ) : (
                <>
                  {filteredQuestions.map((q) => {
                    const idx = questions.indexOf(q);
                    return (
                      <div
                        key={idx}
                        id={`oe-question-${isEdit ? "edit" : "create"}-${idx}`}
                        className="oe-question-card"
                        style={{
                          background: "#fff",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          padding: "1rem 1.25rem",
                          marginBottom: "0.75rem",
                          transition: "all 0.2s ease",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "1rem",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                marginBottom: "0.5rem",
                              }}
                            >
                              <span
                                style={{
                                  background:
                                    "linear-gradient(135deg, var(--color-1), var(--color-2))",
                                  color: "#fff",
                                  borderRadius: "50%",
                                  width: "28px",
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {q.questionNumber}
                              </span>
                              {q.type && (
                                <span
                                  style={{
                                    fontSize: "0.7rem",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    background:
                                      q.type.toUpperCase() === "MCQ"
                                        ? "#e0e7ff"
                                        : q.type.toUpperCase() === "NUMERICAL"
                                          ? "#fef3c7"
                                          : "#dcfce7",
                                    color:
                                      q.type.toUpperCase() === "MCQ"
                                        ? "#3730a3"
                                        : q.type.toUpperCase() === "NUMERICAL"
                                          ? "#92400e"
                                          : "#166534",
                                    fontWeight: 600,
                                    marginRight: "0.25rem",
                                  }}
                                >
                                  {q.type.toUpperCase()}
                                </span>
                              )}
                              <div
                                style={{
                                  fontSize: "0.9rem",
                                  color: "#1e293b",
                                  lineHeight: 1.5,
                                  overflow: "hidden",
                                }}
                              >
                                <LazyMarkdown>
                                  {formatLatexText(q.questionText)}
                                </LazyMarkdown>
                              </div>
                            </div>
                            {(q.diagramFileURL || q.svgCode) && (
                              <div
                                className="diagram-viewer-container"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "0.75rem",
                                  marginLeft: "2.25rem",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  gap: "0.25rem",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "1rem",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      border: "1px solid #e2e8f0",
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      backgroundColor: "#f8fafc",
                                      maxWidth: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      boxShadow:
                                        "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                                      cursor: "pointer",
                                      transition:
                                        "transform 0.2s ease, box-shadow 0.2s ease",
                                    }}
                                    onClick={() => {
                                      if (!q.svgCode && q.diagramFileURL)
                                        window.open(
                                          q.diagramFileSignedURL ||
                                          q.diagramFileURL,
                                          "_blank",
                                        );
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1.02)";
                                      e.currentTarget.style.boxShadow =
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "scale(1)";
                                      e.currentTarget.style.boxShadow =
                                        "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
                                    }}
                                    title="Click to view full size image"
                                  >
                                    {q.svgCode ? (
                                      <>
                                        <style>{`
                                          .diagram-viewer-container svg {
                                            max-width: 100%;
                                            height: auto;
                                            max-height: 220px;
                                          }
                                        `}</style>
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: q.svgCode,
                                          }}
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            maxWidth: "100%",
                                            maxHeight: "220px",
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <img
                                        src={
                                          q.diagramFileSignedURL ||
                                          q.diagramFileURL
                                        }
                                        alt={`Diagram for Question ${q.questionNumber}`}
                                        style={{
                                          maxHeight: "220px",
                                          maxWidth: "100%",
                                          objectFit: "contain",
                                          display: "block",
                                        }}
                                      />
                                    )}
                                  </div>
                                  {(q.diagramPageFileURL ||
                                    q.diagramFileURL ||
                                    q.imageURL) &&
                                    q.source !== "library" &&
                                    !q.libraryId &&
                                    !q.libraryName && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setCropQuestion({
                                            ...q,
                                            isEditMode: isEdit,
                                          });
                                          setIsCropModalOpen(true);
                                        }}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.5rem",
                                          padding: "8px 14px",
                                          background: "var(--color-1)",
                                          color: "#ffffff",
                                          border: "none",
                                          borderRadius: "8px",
                                          cursor: "pointer",
                                          fontWeight: "600",
                                          fontSize: "0.85rem",
                                          boxShadow:
                                            "0 2px 4px rgba(79, 70, 229, 0.2)",
                                          transition: "all 0.2s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(-1px)";
                                          e.currentTarget.style.boxShadow =
                                            "0 4px 6px rgba(79, 70, 229, 0.3)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(0)";
                                          e.currentTarget.style.boxShadow =
                                            "0 2px 4px rgba(79, 70, 229, 0.2)";
                                        }}
                                      >
                                        <i className="fa-solid fa-crop-simple"></i>{" "}
                                        Edit Diagram
                                      </button>
                                    )}
                                </div>
                                <span
                                  style={{
                                    fontSize: "0.7rem",
                                    color: "#64748b",
                                    fontStyle: "italic",
                                    marginTop: "0.2rem",
                                  }}
                                ></span>
                              </div>
                            )}
                            {q.options &&
                              q.options.filter(Boolean).length > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.6rem",
                                    marginLeft: "2.25rem",
                                    marginBottom: "1rem",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: "0.4rem 1rem",
                                    }}
                                  >
                                    {q.options.filter(Boolean).map((opt, oi) => {
                                      const optLabel =
                                        opt && typeof opt === "object"
                                          ? opt.key
                                          : opt
                                            ? opt.charAt(0)
                                            : "";
                                      const optText =
                                        opt && typeof opt === "object"
                                          ? `${opt.key}) ${opt.text}`
                                          : opt || "";
                                      const isCorrect =
                                        q.correctOptions &&
                                        q.correctOptions.includes(optLabel);
                                      return (
                                        <div
                                          key={oi}
                                          style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "0.4rem",
                                            fontSize: "0.85rem",
                                            color: isCorrect
                                              ? "#16a34a"
                                              : "#475569",
                                            fontWeight: isCorrect ? 600 : 400,
                                            background: isCorrect
                                              ? "#f0fdf4"
                                              : "transparent",
                                            padding: "4px 8px",
                                            borderRadius: "6px",
                                            border: isCorrect
                                              ? "1px solid #bbf7d0"
                                              : "1px solid transparent",
                                            transition: "all 0.2s ease",
                                          }}
                                        >
                                          <span
                                            style={{
                                              lineHeight: 1.4,
                                              width: "100%",
                                              overflow: "hidden",
                                            }}
                                          >
                                            <LazyMarkdown
                                              components={SPAN_COMPONENTS}
                                            >
                                              {formatLatexText(optText)}
                                            </LazyMarkdown>
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Dedicated Correct Answer Pills */}
                                  <div style={{ marginTop: "0.5rem" }}>
                                    <label
                                      style={{
                                        fontSize: "0.78rem",
                                        color: "#64748b",
                                        fontWeight: 500,
                                        marginBottom: "0.4rem",
                                        display: "block",
                                      }}
                                    >
                                      Correct Answer:
                                      {(!q.correctOptions ||
                                        q.correctOptions.length === 0) && (
                                          <span
                                            style={{
                                              color: "#ef4444",
                                              fontStyle: "italic",
                                              marginLeft: "0.5rem",
                                            }}
                                          >
                                            *Required
                                          </span>
                                        )}
                                    </label>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "0.5rem",
                                      }}
                                    >
                                      {["A", "B", "C", "D"].map((optLabel) => {
                                        const isCorrect =
                                          q.correctOptions &&
                                          q.correctOptions.includes(optLabel);
                                        return (
                                          <button
                                            key={optLabel}
                                            type="button"
                                            onClick={() =>
                                              handleOnlineExamQuestionChange(
                                                idx,
                                                "correctOptions",
                                                [optLabel],
                                                isEdit,
                                              )
                                            }
                                            disabled={isSubmitting}
                                            style={{
                                              padding: "6px 14px",
                                              borderRadius: "20px",
                                              border: isCorrect
                                                ? "1px solid #16a34a"
                                                : "1px solid #cbd5e1",
                                              background: isCorrect
                                                ? "#16a34a"
                                                : "#fff",
                                              color: isCorrect
                                                ? "#fff"
                                                : "#475569",
                                              fontSize: "0.85rem",
                                              fontWeight: 600,
                                              cursor: isSubmitting
                                                ? "not-allowed"
                                                : "pointer",
                                              transition: "all 0.2s",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "0.3rem",
                                            }}
                                          >
                                            {optLabel}
                                            {isCorrect && (
                                              <i
                                                className="fa-solid fa-check"
                                                style={{ fontSize: "0.7rem" }}
                                              />
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              )}
                            {q.type &&
                              (q.type.toUpperCase() === "FILL-IN-THE-BLANKS" ||
                                q.type.toUpperCase() === "NUMERICAL") && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.6rem",
                                    marginLeft: "2.25rem",
                                    marginBottom: "1rem",
                                  }}
                                >
                                  <div style={{ marginTop: "0.5rem" }}>
                                    <label
                                      style={{
                                        fontSize: "0.78rem",
                                        color: "#64748b",
                                        fontWeight: 500,
                                        marginBottom: "0.4rem",
                                        display: "block",
                                      }}
                                    >
                                      Correct Answer:
                                      {(!q.correctAnswer ||
                                        q.correctAnswer.trim() === "") && (
                                          <span
                                            style={{
                                              color: "#ef4444",
                                              fontStyle: "italic",
                                              marginLeft: "0.5rem",
                                            }}
                                          >
                                            *Required
                                          </span>
                                        )}
                                    </label>
                                    <input
                                      type={
                                        q.type.toUpperCase() === "NUMERICAL"
                                          ? "number"
                                          : "text"
                                      }
                                      step="any"
                                      value={q.correctAnswer || ""}
                                      onChange={(e) => {
                                        handleOnlineExamQuestionChange(
                                          idx,
                                          "correctAnswer",
                                          e.target.value,
                                          isEdit,
                                        );
                                      }}
                                      placeholder={
                                        q.type.toUpperCase() === "NUMERICAL"
                                          ? "e.g. 42.5"
                                          : "e.g. correct answer text"
                                      }
                                      disabled={isSubmitting}
                                      style={{
                                        width: "100%",
                                        maxWidth: "300px",
                                        padding: "6px 12px",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "6px",
                                        fontSize: "0.85rem",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                alignItems: "center",
                                marginLeft: "2.25rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.3rem",
                                }}
                              >
                                <label
                                  style={{
                                    fontSize: "0.78rem",
                                    color: "#64748b",
                                    fontWeight: 500,
                                    margin: 0,
                                  }}
                                >
                                  Marks:
                                </label>
                                <input
                                  type="number"
                                  value={q.maxScore != null && q.maxScore !== "" ? roundTo2(q.maxScore) : ""}
                                  min="0.5"
                                  step="0.5"
                                  onChange={(e) =>
                                    handleOnlineExamQuestionChange(
                                      idx,
                                      "maxScore",
                                      e.target.value,
                                      isEdit,
                                    )
                                  }
                                  style={{
                                    width: "60px",
                                    padding: "4px 6px",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: "6px",
                                    fontSize: "0.82rem",
                                    textAlign: "center",
                                  }}
                                  disabled={isSubmitting}
                                />
                              </div>

                              {/* Per-question duration (advanced mode only) */}
                              {test.isAdvancedOnlineExam && test.advancedOnlineExamOptions?.isTimedExam && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.3rem",
                                  }}
                                >
                                  <label
                                    style={{
                                      fontSize: "0.78rem",
                                      color: "#64748b",
                                      fontWeight: 500,
                                      margin: 0,
                                    }}
                                  >
                                    Duration:
                                  </label>
                                  <input
                                    type="number"
                                    value={q.duration != null && q.duration !== "" ? roundTo2(q.duration) : ""}
                                    min="0.5"
                                    step="0.5"
                                    placeholder="min"
                                    onChange={(e) =>
                                      handleOnlineExamQuestionChange(
                                        idx,
                                        "duration",
                                        e.target.value,
                                        isEdit,
                                      )
                                    }
                                    style={{
                                      width: "65px",
                                      padding: "4px 6px",
                                      border: "1px solid #cbd5e1",
                                      borderRadius: "6px",
                                      fontSize: "0.82rem",
                                      textAlign: "center",
                                    }}
                                    disabled={isSubmitting}
                                  />
                                  <span
                                    style={{
                                      fontSize: "0.72rem",
                                      color: "#94a3b8",
                                    }}
                                  >
                                    min
                                  </span>
                                </div>
                              )}

                              {isHigherEd && (
                                <>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.3rem",
                                    }}
                                  >
                                    <label
                                      style={{
                                        fontSize: "0.78rem",
                                        color: "#64748b",
                                        fontWeight: 500,
                                        margin: 0,
                                      }}
                                    >
                                      CO:
                                    </label>
                                    <select
                                      value={q.courseOutcome || ""}
                                      onChange={(e) =>
                                        handleOnlineExamQuestionChange(
                                          idx,
                                          "courseOutcome",
                                          e.target.value,
                                          isEdit,
                                        )
                                      }
                                      style={{
                                        padding: "4px 6px",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "6px",
                                        fontSize: "0.82rem",
                                        minWidth: "70px",
                                      }}
                                      disabled={isSubmitting}
                                    >
                                      <option value="">-</option>
                                      {courseOutcomes.map((co) => (
                                        <option
                                          key={co.value}
                                          value={co.value}
                                        >
                                          {co.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.3rem",
                                    }}
                                  >
                                    <label
                                      style={{
                                        fontSize: "0.78rem",
                                        color: "#64748b",
                                        fontWeight: 500,
                                        margin: 0,
                                      }}
                                    >
                                      BL:
                                    </label>
                                    <select
                                      value={q.bloomsLevel || ""}
                                      onChange={(e) =>
                                        handleOnlineExamQuestionChange(
                                          idx,
                                          "bloomsLevel",
                                          e.target.value,
                                          isEdit,
                                        )
                                      }
                                      style={{
                                        padding: "4px 6px",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "6px",
                                        fontSize: "0.82rem",
                                        minWidth: "70px",
                                      }}
                                      disabled={isSubmitting}
                                    >
                                      <option value="">-</option>
                                      {bloomsLevelOptions.map((bl) => (
                                        <option
                                          key={bl.value}
                                          value={bl.value}
                                        >
                                          {bl.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </>
                              )}

                              {/* Question level metadata tags */}
                              {(q.subject ||
                                q.topic ||
                                q.subTopic ||
                                q.difficulty ||
                                q.isPastExam) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "0.4rem",
                                      alignItems: "center",
                                      marginLeft: "0.5rem",
                                    }}
                                  >
                                    {q.subject && (
                                      <span
                                        style={{
                                          background: "#eff6ff",
                                          color: "#2563eb",
                                          border: "1px solid #bfdbfe",
                                          borderRadius: "16px",
                                          padding: "2px 8px",
                                          fontSize: "0.72rem",
                                          fontWeight: "600",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                        title="Subject"
                                      >
                                        {q.subject}
                                      </span>
                                    )}
                                    {q.topic && (
                                      <span
                                        style={{
                                          background: "#faf5ff",
                                          color: "#7c3aed",
                                          border: "1px solid #e9d5ff",
                                          borderRadius: "16px",
                                          padding: "2px 8px",
                                          fontSize: "0.72rem",
                                          fontWeight: "600",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                        title="Topic"
                                      >
                                        {q.topic}
                                      </span>
                                    )}
                                    {q.subTopic && (
                                      <span
                                        style={{
                                          background: "#ecfeff",
                                          color: "#0891b2",
                                          border: "1px solid #cffafe",
                                          borderRadius: "16px",
                                          padding: "2px 8px",
                                          fontSize: "0.72rem",
                                          fontWeight: "600",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                        title="Subtopic"
                                      >
                                        {q.subTopic}
                                      </span>
                                    )}
                                    {q.difficulty &&
                                      (() => {
                                        const diff = q.difficulty
                                          .toLowerCase()
                                          .trim();
                                        const isEasy = diff === "easy";
                                        const isHard = diff === "hard";
                                        const bg = isEasy
                                          ? "#f0fdf4"
                                          : isHard
                                            ? "#fef2f2"
                                            : "#fff7ed";
                                        const color = isEasy
                                          ? "#16a34a"
                                          : isHard
                                            ? "#dc2626"
                                            : "#ea580c";
                                        const border = isEasy
                                          ? "#bbf7d0"
                                          : isHard
                                            ? "#fecaca"
                                            : "#ffedd5";
                                        return (
                                          <span
                                            style={{
                                              background: bg,
                                              color: color,
                                              border: `1px solid ${border}`,
                                              borderRadius: "16px",
                                              padding: "2px 8px",
                                              fontSize: "0.72rem",
                                              fontWeight: "600",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px",
                                            }}
                                            title="Difficulty"
                                          >
                                            {q.difficulty.charAt(0).toUpperCase() +
                                              q.difficulty.slice(1)}
                                          </span>
                                        );
                                      })()}
                                    {q.isPastExam && (
                                      <span
                                        style={{
                                          background: "#fef3c7",
                                          color: "#b45309",
                                          border: "1px solid #fde68a",
                                          borderRadius: "16px",
                                          padding: "2px 8px",
                                          fontSize: "0.72rem",
                                          fontWeight: "600",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                        title="Past Exam"
                                      >
                                        {`${q.libraryName || ""} ${q.year || ""}`.trim() ||
                                          "Past Exam"}
                                      </span>
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handleOnlineExamQuestionRemove(idx, isEdit)
                            }
                            className="modal-table-remove-btn"
                            style={{
                              flexShrink: 0,
                              width: "32px",
                              height: "32px",
                            }}
                            disabled={isSubmitting}
                          >
                            <i
                              className="fa-solid fa-trash"
                              style={{ fontSize: "0.75rem" }}
                            ></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: "0.75rem 1rem",
                      background: "#f1f5f9",
                      borderRadius: "8px",
                      marginTop: "0.5rem",
                      gap: "1.5rem",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: "#1e293b",
                        fontSize: "0.95rem",
                      }}
                    >
                      Total Marks:{" "}
                      <span style={{ color: "var(--color-1)" }}>
                        {roundTo2(totalMarks)}
                      </span>
                    </span>
                    {test.isAdvancedOnlineExam && test.advancedOnlineExamOptions?.isTimedExam && (
                      <span
                        style={{
                          fontWeight: 700,
                          color: "#1e293b",
                          fontSize: "0.95rem",
                        }}
                      >
                        Total Duration:{" "}
                        <span style={{ color: "var(--color-1)" }}>
                          {roundTo2(totalDuration)} min
                        </span>
                      </span>
                    )}
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>
    );
  };

  const renderMcqPickerModal = () => {
    const isEdit = mcqPickerFor === "edit";
    const currentTestId = isEdit ? editTest?.id : null;
    const test = isEdit ? editTest : newTest;
    const existingSourceIds = new Set(
      (test.onlineExamQuestions || []).map((q) => q._sourceId).filter(Boolean),
    );
    // Use available question banks for the dropdown
    const QBs = availableQuestionBanks.map((qb) => ({
      id: qb._id,
      name: qb.name || qb._id,
    }));
    const topics = [
      ...new Set(mcqQuestions.map((q) => q.topic).filter(Boolean)),
    ];
    const difficulties = [
      ...new Set(mcqQuestions.map((q) => q.difficulty).filter(Boolean)),
    ];

    // Compute which questions are used in other tests
    const usedInOtherTests = {};
    tests.forEach((t) => {
      // Skip the current test being edited
      if (currentTestId && t._id === currentTestId) return;
      if (t.onlineExamQuestions && t.onlineExamQuestions.length > 0) {
        t.onlineExamQuestions.forEach((q) => {
          if (q.questionId) {
            if (!usedInOtherTests[q.questionId]) {
              usedInOtherTests[q.questionId] = { count: 0, testNames: [] };
            }
            usedInOtherTests[q.questionId].count += 1;
            usedInOtherTests[q.questionId].testNames.push(t.testName);
          }
        });
      }
    });

    const filtered = mcqQuestions.filter((q) => {
      if (
        mcqSearchTerm &&
        !q.questionText.toLowerCase().includes(mcqSearchTerm.toLowerCase())
      )
        return false;
      if (
        mcqFilterQB &&
        mcqFilterQB !== "ALL" &&
        q.questionBankId !== mcqFilterQB
      )
        return false;
      if (mcqFilterTopic && q.topic !== mcqFilterTopic) return false;
      if (mcqFilterDifficulty && q.difficulty !== mcqFilterDifficulty)
        return false;
      return true;
    });

    const selectableFiltered = filtered.filter(
      (q) => !existingSourceIds.has(q._id),
    );
    const allSelectableSelected =
      selectableFiltered.length > 0 &&
      selectableFiltered.every((q) => mcqSelectedIds.has(q._id));
    const someSelectableSelected = selectableFiltered.some((q) =>
      mcqSelectedIds.has(q._id),
    );

    const handleSelectAllToggle = () => {
      if (allSelectableSelected) {
        // Deselect all filtered questions
        setMcqSelectedIds((prev) => {
          const next = new Set(prev);
          selectableFiltered.forEach((q) => next.delete(q._id));
          return next;
        });
      } else {
        // Select all filtered questions
        setMcqSelectedIds((prev) => {
          const next = new Set(prev);
          selectableFiltered.forEach((q) => next.add(q._id));
          return next;
        });
      }
    };

    return (
      <Modal
        isOpen={isMcqPickerOpen}
        onClose={() => {
          setIsMcqPickerOpen(false);
          setMcqSelectedIds(new Set());
          setMcqSearchTerm("");
          setMcqFilterQB("");
          setMcqFilterTopic("");
          setMcqFilterDifficulty("");
          setMcqQuestions([]);
        }}
        maxWidth="95%"
      >
        <h2 style={{ marginBottom: "0.25rem" }}>
          <i
            className="fa-solid fa-database"
            style={{ marginRight: "0.5rem", color: "var(--color-1)" }}
          ></i>
          Select Objective Questions
        </h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Choose questions from your Question Bank to add to this exam.
        </p>

        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            marginBottom: "1rem",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder="Search questions..."
            value={mcqSearchTerm}
            onChange={(e) => setMcqSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: "200px",
              padding: "0.5rem 0.75rem",
              border: "2px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "0.9rem",
            }}
          />
          <select
            value={mcqFilterQB}
            onChange={(e) => {
              const val = e.target.value;
              setMcqFilterQB(val);
              if (val === "ALL") {
                fetchMcqQuestions(null);
              } else if (val !== "") {
                fetchMcqQuestions(val);
              } else {
                setMcqQuestions([]);
              }
            }}
            style={{
              padding: "0.5rem",
              border: "2px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}
          >
            <option value="" disabled>
              Select Question Bank
            </option>
            <option value="ALL">All Question Banks</option>
            {QBs.map((qb) => (
              <option key={qb.id} value={qb.id}>
                {qb.name}
              </option>
            ))}
          </select>
          <select
            value={mcqFilterTopic}
            onChange={(e) => setMcqFilterTopic(e.target.value)}
            style={{
              padding: "0.5rem",
              border: "2px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}
          >
            <option value="">All Topics</option>
            {topics.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={mcqFilterDifficulty}
            onChange={(e) => setMcqFilterDifficulty(e.target.value)}
            style={{
              padding: "0.5rem",
              border: "2px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}
          >
            <option value="">All Difficulties</option>
            {difficulties.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        {isMcqLoading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Loader fontSize="40px" style={{ left: "0", top: "0" }} />
          </div>
        ) : !mcqFilterQB ? (
          <div
            style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}
          >
            <i
              className="fa-solid fa-hand-pointer"
              style={{
                fontSize: "2rem",
                marginBottom: "0.5rem",
                display: "block",
              }}
            ></i>
            Please select a Question Bank from the dropdown above to view its
            questions.
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}
          >
            <i
              className="fa-solid fa-search"
              style={{
                fontSize: "2rem",
                marginBottom: "0.5rem",
                display: "block",
              }}
            ></i>
            No objective questions found for this selection.
          </div>
        ) : (
          <>
            {selectableFiltered.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem 1rem",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderBottom: "none",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  fontSize: "0.875rem",
                  color: "#475569",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    fontWeight: "600",
                    userSelect: "none",
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          someSelectableSelected && !allSelectableSelected;
                      }
                    }}
                    onChange={handleSelectAllToggle}
                    style={{
                      accentColor: "var(--color-1)",
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ color: "#1e293b" }}>
                    {allSelectableSelected ? "Deselect All" : `Select All`}
                  </span>
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                    {
                      selectableFiltered.filter((q) =>
                        mcqSelectedIds.has(q._id),
                      ).length
                    }{" "}
                    of {selectableFiltered.length} selected
                  </span>
                  {someSelectableSelected && (
                    <button
                      type="button"
                      onClick={() => {
                        setMcqSelectedIds((prev) => {
                          const next = new Set(prev);
                          selectableFiltered.forEach((q) => next.delete(q._id));
                          return next;
                        });
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#ef4444",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        fontWeight: "600",
                        padding: "0 4px",
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}

            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderTopLeftRadius:
                  selectableFiltered.length > 0 ? "0px" : "8px",
                borderTopRightRadius:
                  selectableFiltered.length > 0 ? "0px" : "8px",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
            >
              {filtered.map((q) => {
                const isSelected = mcqSelectedIds.has(q._id);
                const alreadyAdded = existingSourceIds.has(q._id);
                return (
                  <div
                    key={q._id}
                    onClick={() => {
                      if (alreadyAdded) return;
                      setMcqSelectedIds((prev) => {
                        const n = new Set(prev);
                        if (n.has(q._id)) n.delete(q._id);
                        else n.add(q._id);
                        return n;
                      });
                    }}
                    style={{
                      padding: "0.75rem 1rem",
                      borderBottom: "1px solid #f1f5f9",
                      cursor: alreadyAdded ? "not-allowed" : "pointer",
                      background: alreadyAdded
                        ? "#f8fafc"
                        : isSelected
                          ? "#eef2ff"
                          : "#fff",
                      opacity: alreadyAdded ? 0.5 : 1,
                      transition: "all 0.15s ease",
                      display: "flex",
                      gap: "0.75rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={alreadyAdded}
                      readOnly
                      style={{
                        marginTop: "4px",
                        accentColor: "var(--color-1)",
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: "#1e293b",
                          lineHeight: 1.5,
                          marginBottom: "0.25rem",
                        }}
                      >
                        <LazyMarkdown>
                          {formatLatexText(
                            q.questionText.length > 150
                              ? q.questionText.slice(0, 150) + "..."
                              : q.questionText,
                          )}
                        </LazyMarkdown>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          flexWrap: "wrap",
                        }}
                      >
                        {q.type && (
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background:
                                q.type.toUpperCase() === "MCQ"
                                  ? "#e0e7ff"
                                  : q.type.toUpperCase() === "NUMERICAL"
                                    ? "#fef3c7"
                                    : "#dcfce7",
                              color:
                                q.type.toUpperCase() === "MCQ"
                                  ? "#3730a3"
                                  : q.type.toUpperCase() === "NUMERICAL"
                                    ? "#92400e"
                                    : "#166534",
                              fontWeight: 600,
                              textTransform: "uppercase",
                            }}
                          >
                            {q.type}
                          </span>
                        )}
                        {q.topic && (
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background: "#e0e7ff",
                              color: "#3730a3",
                              fontWeight: 500,
                            }}
                          >
                            {q.topic}
                          </span>
                        )}
                        {q.subTopic && (
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background: "#fef3c7",
                              color: "#92400e",
                              fontWeight: 500,
                            }}
                          >
                            {q.subTopic}
                          </span>
                        )}
                        {q.difficulty && (
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background:
                                q.difficulty === "Hard"
                                  ? "#fee2e2"
                                  : q.difficulty === "Easy"
                                    ? "#dcfce7"
                                    : "#fef9c3",
                              color:
                                q.difficulty === "Hard"
                                  ? "#991b1b"
                                  : q.difficulty === "Easy"
                                    ? "#166534"
                                    : "#854d0e",
                              fontWeight: 500,
                            }}
                          >
                            {q.difficulty}
                          </span>
                        )}
                        {alreadyAdded && (
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background: "#e2e8f0",
                              color: "#475569",
                              fontWeight: 500,
                            }}
                          >
                            Already Added
                          </span>
                        )}
                        {!alreadyAdded && usedInOtherTests[q._id] && (
                          <span
                            title={`Used in: ${usedInOtherTests[q._id].testNames.join(", ")}`}
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              background: "#f1f5f9",
                              color: "#64748b",
                              border: "1px solid #cbd5e1",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              cursor: "help",
                            }}
                          >
                            <i className="fa-solid fa-code-branch"></i>
                            Used in {usedInOtherTests[q._id].count} test(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "1rem",
          }}
        >
          <span
            style={{
              fontSize: "0.9rem",
              color: "var(--color-1)",
              fontWeight: 600,
            }}
          >
            {mcqSelectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={() => handleAddSelectedMcqs(isEdit)}
            className="btn-submit"
            style={{ width: "auto", padding: "0.75rem 2rem" }}
            disabled={mcqSelectedIds.size === 0}
          >
            <i className="fa-solid fa-plus"></i> Add {mcqSelectedIds.size}{" "}
            Question{mcqSelectedIds.size !== 1 ? "s" : ""}
          </button>
        </div>
      </Modal>
    );
  };

  const renderCustomQuestionModal = (isEdit = false) => {
    const isHigherEd = instituteData?.category === "HIGHER_EDUCATION";

    // Determine if the current test has a exam template selected
    const currentTest = isEdit ? editTest : newTest;
    const selectedStdTestOpt = standardizedTestOptions.find(
      (opt) => opt.value === currentTest.standardizedTestId,
    );
    const isStdTestMode = !!selectedStdTestOpt;
    const stdSubjects = selectedStdTestOpt?.subjects || [];

    // Helpers to update stdTestEntries rows
    const updateStdEntry = (rowIndex, field, value) => {
      setStdTestEntries((prev) => {
        const updated = [...prev];
        updated[rowIndex] = { ...updated[rowIndex], [field]: value };
        // When subject changes, reset topic
        if (field === "subject") updated[rowIndex].topic = "";
        return updated;
      });
    };
    const addStdEntry = () =>
      setStdTestEntries((prev) => [
        ...prev,
        {
          subject: "",
          topic: "",
          difficulty: "",
          questionType: "MCQ",
          questionCount: 5,
        },
      ]);
    const removeStdEntry = (rowIndex) =>
      setStdTestEntries((prev) => prev.filter((_, i) => i !== rowIndex));

    const totalStdQuestions = stdTestEntries.reduce(
      (sum, e) => sum + (parseInt(e.questionCount) || 0),
      0,
    );

    const isGenerateDisabled =
      isGeneratingQuestions ||
      (isStdTestMode
        ? stdTestEntries.every((e) => !e.subject)
        : !generateQForm.topic.trim());

    return (
      <>
        {/* Generate Questions Modal */}
        <Modal
          isOpen={isGenerateQuestionsOpen}
          onClose={() => {
            if (!isGeneratingQuestions) setIsGenerateQuestionsOpen(false);
          }}
          maxWidth="100%"
        >
          <div className="oeb-generate-modal">
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.25rem 1.5rem",
                background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                borderRadius: "14px",
                marginBottom: "1.5rem",
                border: "1px solid #ddd6fe",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  flexShrink: 0,
                  background: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(139,92,246,0.35)",
                }}
              >
                <i
                  className="fa-solid fa-wand-magic-sparkles"
                  style={{ color: "#fff", fontSize: "1.2rem" }}
                ></i>
              </div>
              <div style={{ flex: 1 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Generate Questions with AI
                </h2>
                <p
                  style={{
                    margin: "0.2rem 0 0",
                    fontSize: "0.88rem",
                    color: "#7c3aed",
                  }}
                >
                  {isStdTestMode
                    ? `Template: ${selectedStdTestOpt.label} — configure subject entries below.`
                    : "Auto-generate MCQs by topic. Questions are saved to your Question Bank."}
                </p>
              </div>
              {currentTest.standardizedTestId && (
                <span
                  style={{
                    flexShrink: 0,
                    fontSize: "0.75rem",
                    color: "#7c3aed",
                    background: "#f5f3ff",
                    border: "1px solid #ddd6fe",
                    borderRadius: "20px",
                    padding: "4px 12px",
                    fontWeight: 700,
                  }}
                >
                  <i
                    className="fa-solid fa-check-circle"
                    style={{ marginRight: "4px" }}
                  />
                  Template mode
                </span>
              )}
            </div>

            {/* Exam Template selector */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                alignItems: "center",
                gap: "1rem",
                padding: "0.9rem 1.25rem",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                marginBottom: "1.5rem",
              }}
            >
              <label
                style={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: "#475569",
                  whiteSpace: "nowrap",
                  margin: 0,
                }}
              >
                <i
                  className="fa-solid fa-graduation-cap"
                  style={{ marginRight: "0.45rem", color: "#8b5cf6" }}
                />
                Exam Template
              </label>
              <Select
                menuPosition="fixed"
                options={filteredStandardizedTestOptions}
                value={
                  filteredStandardizedTestOptions.find(
                    (opt) => currentTest.standardizedTestId === opt.value,
                  ) || null
                }
                onChange={(selected) => {
                  if (isEdit) {
                    setEditTest((prev) => ({
                      ...prev,
                      standardizedTestId: selected ? selected.value : null,
                    }));
                    setIsEditChanged(true);
                  } else {
                    setNewTest((prev) => ({
                      ...prev,
                      standardizedTestId: selected ? selected.value : null,
                    }));
                  }
                }}
                isClearable
                isDisabled={isGeneratingQuestions}
                placeholder="Choose a template to switch to per-subject mode…"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "8px",
                    borderColor: "#cbd5e1",
                    minHeight: "40px",
                    boxShadow: "none",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? "#8b5cf6"
                      : state.isFocused
                        ? "#f5f3ff"
                        : "white",
                    color: state.isSelected ? "white" : "#334155",
                  }),
                }}
              />
            </div>

            {isStdTestMode ? (
              /* ── Standardized-test mode: multi-entry cards ── */
              <div style={{ marginBottom: "1.25rem" }}>
                <ModalTableInput
                  layout="card"
                  title="Question Entry"
                  cardGridColumns="repeat(auto-fit, minmax(220px, 1fr))"
                  headers={[
                    {
                      key: "subject",
                      label: "Subject",
                      width: "minmax(180px, 1.5fr)",
                    },
                    {
                      key: "topic",
                      label: "Topic",
                      width: "minmax(180px, 1.5fr)",
                    },
                    {
                      key: "difficulty",
                      label: "Difficulty",
                      width: "minmax(140px, 1fr)",
                    },
                    {
                      key: "questionType",
                      label: "Question Type",
                      width: "minmax(140px, 1fr)",
                    },
                    {
                      key: "questionCount",
                      label: "Count",
                      width: "minmax(90px, 0.8fr)",
                      required: true,
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      width: "minmax(70px, 0.6fr)",
                    },
                  ]}
                  rows={stdTestEntries}
                  onRowChange={updateStdEntry}
                  onRowRemove={removeStdEntry}
                  onRowAdd={isGeneratingQuestions ? null : addStdEntry}
                  canRemove={true}
                  addButtonText="Add Another Entry"
                  renderCell={(header, row, rowIndex) => {
                    const subjectOptions = stdSubjects.map((s) => ({
                      value: s.name,
                      label: s.name,
                    }));
                    const selectedSubjectObj = stdSubjects.find(
                      (s) => s.name === row.subject,
                    );
                    const topicOptions = (selectedSubjectObj?.topics || []).map(
                      (t) => ({
                        value: t,
                        label: t,
                      }),
                    );

                    switch (header.key) {
                      case "subject":
                        return (
                          <Select
                            menuPosition="fixed"
                            options={subjectOptions}
                            value={
                              row.subject
                                ? { value: row.subject, label: row.subject }
                                : null
                            }
                            onChange={(sel) =>
                              updateStdEntry(
                                rowIndex,
                                "subject",
                                sel ? sel.value : "",
                              )
                            }
                            placeholder="Select subject..."
                            isClearable
                            isDisabled={isGeneratingQuestions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        );
                      case "topic":
                        return (
                          <Select
                            menuPosition="fixed"
                            options={topicOptions}
                            value={
                              row.topic
                                ? { value: row.topic, label: row.topic }
                                : null
                            }
                            onChange={(sel) =>
                              updateStdEntry(
                                rowIndex,
                                "topic",
                                sel ? sel.value : "",
                              )
                            }
                            placeholder={
                              row.subject
                                ? "Select topic..."
                                : "Select subject first"
                            }
                            isClearable
                            isDisabled={isGeneratingQuestions || !row.subject}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        );
                      case "difficulty":
                        return (
                          <Select
                            menuPosition="fixed"
                            options={difficultyOptions}
                            value={
                              row.difficulty
                                ? {
                                  value: row.difficulty,
                                  label: row.difficulty,
                                }
                                : null
                            }
                            onChange={(sel) =>
                              updateStdEntry(
                                rowIndex,
                                "difficulty",
                                sel ? sel.value : "",
                              )
                            }
                            placeholder="Any difficulty..."
                            isClearable
                            isDisabled={isGeneratingQuestions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        );
                      case "questionType":
                        return (
                          <Select
                            menuPosition="fixed"
                            options={[{ value: "MCQ", label: "MCQ" }]}
                            value={{
                              value: row.questionType || "MCQ",
                              label: row.questionType || "MCQ",
                            }}
                            onChange={(sel) =>
                              updateStdEntry(
                                rowIndex,
                                "questionType",
                                sel ? sel.value : "MCQ",
                              )
                            }
                            isDisabled={isGeneratingQuestions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        );
                      case "questionCount":
                        return (
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={row.questionCount}
                            required
                            disabled={isGeneratingQuestions}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                updateStdEntry(rowIndex, "questionCount", "");
                              } else {
                                const n = parseInt(val);
                                if (!isNaN(n) && n >= 1 && n <= 50) {
                                  updateStdEntry(rowIndex, "questionCount", n);
                                }
                              }
                            }}
                            className="modal-table-input"
                            style={{
                              width: "100%",
                              padding: "0.4rem",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                            }}
                          />
                        );
                      case "actions":
                        return stdTestEntries.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => removeStdEntry(rowIndex)}
                            disabled={isGeneratingQuestions}
                            style={{
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              width: "28px",
                              height: "28px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                            title="Remove this entry"
                          >
                            <i
                              className="fa-solid fa-trash"
                              style={{ fontSize: "0.85rem" }}
                            ></i>
                          </button>
                        ) : null;
                      default:
                        return null;
                    }
                  }}
                />

                {/* Total Questions counter */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.6rem 1rem",
                    borderRadius: "8px",
                    background: totalStdQuestions > 50 ? "#fef2f2" : "#eff6ff",
                    border: `1.5px solid ${totalStdQuestions > 50 ? "#fca5a5" : "#bfdbfe"}`,
                    marginTop: "0.75rem",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: totalStdQuestions > 50 ? "#b91c1c" : "#1d4ed8",
                    gap: "0.5rem",
                  }}
                >
                  <i className="fa-solid fa-sigma" />
                  Total Questions: {totalStdQuestions}
                  {totalStdQuestions > 50 && (
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: "0.8rem",
                        marginLeft: "0.5rem",
                      }}
                    >
                      (exceeds recommended 50)
                    </span>
                  )}
                </div>
              </div>
            ) : (
              /* ── Standard mode: topic/difficulty/count in a clean row ── */
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: "1.25rem",
                  marginBottom: "1.25rem",
                  alignItems: "end",
                }}
              >
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Topic <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Data Structures, Organic Chemistry…"
                    value={generateQForm.topic}
                    onChange={(e) =>
                      setGenerateQForm((prev) => ({
                        ...prev,
                        topic: e.target.value,
                      }))
                    }
                    disabled={isGeneratingQuestions}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Difficulty</label>
                  <Select
                    menuPosition="fixed"
                    isMulti
                    isClearable
                    name="difficulty"
                    options={difficultyOptions}
                    value={generateQForm.difficulty}
                    onChange={(selectedOptions) =>
                      setGenerateQForm((prev) => ({
                        ...prev,
                        difficulty: selectedOptions || [],
                      }))
                    }
                    placeholder="Any..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isDisabled={isGeneratingQuestions}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">No. of Questions</label>
                  <input
                    type="number"
                    className="form-input"
                    min="1"
                    max="30"
                    value={generateQForm.questionCount}
                    onChange={(e) =>
                      setGenerateQForm((prev) => ({
                        ...prev,
                        questionCount: Math.min(
                          30,
                          Math.max(1, parseInt(e.target.value) || 1),
                        ),
                      }))
                    }
                    disabled={isGeneratingQuestions}
                  />
                </div>
              </div>
            )}

            {isGeneratingQuestions && (
              <div className="oeb-generate-progress">
                <div className="oeb-generate-progress-bar">
                  <div className="oeb-generate-progress-fill"></div>
                </div>
                <span>{generateQProgress}</span>
              </div>
            )}

            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <button
                type="button"
                className="btn-submit"
                onClick={() => handleGenerateQuestions(generateQFor === "edit")}
                disabled={isGenerateDisabled}
              >
                {isGeneratingQuestions ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>{" "}
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-wand-magic-sparkles"></i> Generate
                    Questions
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>

        {/* Custom Question Modal */}
        <Modal
          isOpen={isCustomQuestionOpen}
          onClose={() => setIsCustomQuestionOpen(false)}
          maxWidth="600px"
        >
          <div className="oeb-generate-modal">
            <div className="oeb-generate-modal-header">
              <div
                className="oeb-generate-modal-icon"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(5, 150, 105, 0.1), rgba(52, 211, 153, 0.2))",
                  color: "#059669",
                }}
              >
                <i className="fa-solid fa-pen-fancy"></i>
              </div>
              <div>
                <h2>Create Custom Question</h2>
                <p className="modal-description">
                  Write your own MCQ question to add to the exam.
                </p>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Question Text <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                value={customQuestion.questionText}
                onChange={(e) =>
                  setCustomQuestion((prev) => ({
                    ...prev,
                    questionText: e.target.value,
                  }))
                }
                placeholder="Enter your question... (Markdown & Math supported)"
                className="form-input"
                rows={3}
                style={{
                  resize: "vertical",
                  fontFamily: "inherit",
                  height: "100px",
                }}
              />
            </div>

            <div style={{ marginBottom: "1.25rem" }}>
              <label
                className="form-label"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                Answer Options <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                {["a", "b", "c", "d"].map((label, i) => {
                  const isCorrect = customQuestion.correctOptions === label;
                  return (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        background: isCorrect ? "#f0fdf4" : "#fff",
                        border: isCorrect
                          ? "1px solid #bbf7d0"
                          : "1px solid #e2e8f0",
                        borderRadius: "8px",
                        padding: "0.5rem",
                        transition: "all 0.2s",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: isCorrect ? "#16a34a" : "#475569",
                          width: "25px",
                          textAlign: "center",
                        }}
                      >
                        {label.toUpperCase()})
                      </span>
                      <input
                        type="text"
                        value={customQuestion.options[i]}
                        onChange={(e) => {
                          const opts = [...customQuestion.options];
                          opts[i] = e.target.value;
                          setCustomQuestion((prev) => ({
                            ...prev,
                            options: opts,
                          }));
                        }}
                        placeholder={`Enter text for option ${label.toUpperCase()}`}
                        style={{
                          flex: 1,
                          border: "none",
                          padding: "0.25rem",
                          outline: "none",
                          background: "transparent",
                          fontSize: "0.9rem",
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Dedicated Correct Answer Pills */}
              <div style={{ marginTop: "1rem" }}>
                <label
                  style={{
                    fontSize: "0.85rem",
                    color: "#475569",
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    display: "block",
                  }}
                >
                  Correct Answer:
                  {!customQuestion.correctOptions && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontStyle: "italic",
                        marginLeft: "0.5rem",
                        fontWeight: "normal",
                      }}
                    >
                      *Required
                    </span>
                  )}
                </label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {["a", "b", "c", "d"].map((label) => {
                    const isCorrect = customQuestion.correctOptions === label;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() =>
                          setCustomQuestion((prev) => ({
                            ...prev,
                            correctOptions: label,
                          }))
                        }
                        style={{
                          padding: "6px 14px",
                          borderRadius: "20px",
                          border: isCorrect
                            ? "1px solid #16a34a"
                            : "1px solid #cbd5e1",
                          background: isCorrect ? "#16a34a" : "#fff",
                          color: isCorrect ? "#fff" : "#475569",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                      >
                        {label.toUpperCase()}
                        {isCorrect && (
                          <i
                            className="fa-solid fa-check"
                            style={{ fontSize: "0.7rem" }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isHigherEd
                  ? "repeat(auto-fit, minmax(120px, 1fr))"
                  : "1fr",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <div className="form-group" style={{ marginBottom: "0" }}>
                <label className="form-label">
                  Marks <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="number"
                  value={customQuestion.maxScore}
                  min="0.5"
                  step="0.5"
                  onChange={(e) =>
                    setCustomQuestion((prev) => ({
                      ...prev,
                      maxScore: e.target.value,
                    }))
                  }
                  className="form-input"
                />
              </div>
              {isHigherEd && (
                <>
                  <div className="form-group">
                    <label className="form-label">Course Outcome</label>
                    <select
                      value={customQuestion.courseOutcome}
                      onChange={(e) =>
                        setCustomQuestion((prev) => ({
                          ...prev,
                          courseOutcome: e.target.value,
                        }))
                      }
                      className="form-input"
                    >
                      <option value="">None</option>
                      {courseOutcomes.map((co) => (
                        <option key={co.value} value={co.value}>
                          {co.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bloom's Level</label>
                    <select
                      value={customQuestion.bloomsLevel}
                      onChange={(e) =>
                        setCustomQuestion((prev) => ({
                          ...prev,
                          bloomsLevel: e.target.value,
                        }))
                      }
                      className="form-input"
                    >
                      <option value="">None</option>
                      {bloomsLevelOptions.map((bl) => (
                        <option key={bl.value} value={bl.value}>
                          {bl.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                className="btn-submit"
                onClick={() => handleAddCustomQuestion(isEdit)}
                disabled={
                  !customQuestion.questionText.trim() ||
                  !customQuestion.options.some((opt) => opt.trim() !== "") ||
                  !customQuestion.correctOptions
                }
              >
                <i className="fa-solid fa-check"></i> Add to Exam
              </button>
            </div>
          </div>
        </Modal>

        {/* Review Answer Key Modal */}
        {(() => {
          const answerKeyPageSize = 25;
          const totalAnswerKeyPages = Math.ceil(extractedAnswerKeys.length / answerKeyPageSize) || 1;
          const currentAKPage = Math.min(Math.max(1, answerKeyPage), totalAnswerKeyPages);

          const startIdx = (currentAKPage - 1) * answerKeyPageSize;
          const endIdx = Math.min(startIdx + answerKeyPageSize, extractedAnswerKeys.length);
          const pageItems = [];
          for (let i = startIdx; i < endIdx; i++) {
            pageItems.push({
              ...extractedAnswerKeys[i],
              originalIndex: i
            });
          }

          let displayItems = pageItems;
          if (isAnswerKeyTransposed && pageItems.length > 0) {
            const numCols = 5;
            const numRows = Math.ceil(pageItems.length / numCols);
            const transposed = [];
            for (let r = 0; r < numRows; r++) {
              for (let c = 0; c < numCols; c++) {
                const idx = c * numRows + r;
                if (idx < pageItems.length) {
                  transposed.push(pageItems[idx]);
                }
              }
            }
            displayItems = transposed;
          }

          const rawFile = extractPdfForm.answerKeysFile;
          const fileName = rawFile ? rawFile.name : "";
          const isPdfFile = answerKeyPreviewUrl && (rawFile?.type === "application/pdf" || fileName.toLowerCase().endsWith(".pdf"));

          return (
            <Modal
              isOpen={isExtractAnswerKeyModalOpen}
              onClose={handleCloseExtractAnswerKeyModal}
              maxWidth="95vw"
              zIndex={1050}
            >
              <div className="modal-body" style={{ padding: "0.5rem", display: "flex", flexDirection: "column", height: "82vh", maxHeight: "800px" }}>
                <div style={{ display: "flex", flex: 1, gap: "1.25rem", overflow: "hidden" }}>
                  {/* LEFT PANE: 50% - PDF Reference Document Viewer */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", padding: "0.75rem", overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem", paddingBottom: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", overflow: "hidden" }}>
                        <i className={`fa-solid ${isPdfFile ? 'fa-file-pdf' : 'fa-file'}`} style={{ color: "#3b82f6", fontSize: "1.1rem" }}></i>
                        <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1e293b", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                          {fileName || "Uploaded Document"}
                        </span>
                      </div>
                      {answerKeyPreviewUrl && (
                        <a
                          href={answerKeyPreviewUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: "0.8rem", color: "#3b82f6", fontWeight: "600", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <i className="fa-solid fa-arrow-up-right-from-square"></i> Open Fullscreen
                        </a>
                      )}
                    </div>

                    <div style={{ flex: 1, height: "100%", minHeight: 0, position: "relative" }}>
                      {isPdfFile ? (
                        <iframe
                          src={answerKeyPreviewUrl}
                          width="100%"
                          height="100%"
                          style={{ border: "none", borderRadius: "6px", background: "#fff" }}
                          title="Answer Key PDF Document"
                        />
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#64748b", gap: "0.5rem" }}>
                          <i className="fa-solid fa-file-circle-exclamation" style={{ fontSize: "2.5rem" }}></i>
                          <p style={{ margin: 0, fontSize: "0.9rem" }}>No document preview available.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RIGHT PANE: 50% - Interactive Answer Keys Editor */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {/* Warning Alert Banner */}
                    <div style={{ backgroundColor: "#eff6ff", borderLeft: "4px solid #3b82f6", padding: "0.6rem 0.85rem", borderRadius: "6px", marginBottom: "0.75rem", display: "flex", alignItems: "center" }}>
                      <i className="fa-solid fa-info-circle" style={{ marginRight: "10px", color: "#3b82f6", fontSize: "1.1rem", flexShrink: 0 }}></i>
                      <p style={{ margin: 0, color: "#1e3a8a", fontSize: "0.85rem", lineHeight: "1.3" }}>
                        Please review the AI extracted answer keys below. You can quickly modify them directly if there are any mistakes.
                      </p>
                    </div>

                    {/* Toolbar: Transpose Toggle + Pagination */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", padding: "0.5rem 0.75rem", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                      {/* Segmented Pill Toggle Switch */}
                      <div
                        onClick={() => setIsAnswerKeyTransposed(prev => !prev)}
                        title="Click to toggle between Row-wise and Column-wise layout ordering"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          background: "#e2e8f0",
                          borderRadius: "20px",
                          padding: "3px",
                          cursor: "pointer",
                          userSelect: "none",
                          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.08)",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "5px 12px",
                            borderRadius: "16px",
                            fontSize: "0.78rem",
                            fontWeight: "600",
                            transition: "all 0.2s ease",
                            background: !isAnswerKeyTransposed ? "#ffffff" : "transparent",
                            color: !isAnswerKeyTransposed ? "#1d4ed8" : "#64748b",
                            boxShadow: !isAnswerKeyTransposed ? "0 1px 3px rgba(0,0,0,0.12)" : "none"
                          }}
                        >
                          <i className="fa-solid fa-list-ol" style={{ fontSize: "0.85rem" }}></i>
                          Row-wise
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "5px 12px",
                            borderRadius: "16px",
                            fontSize: "0.78rem",
                            fontWeight: "600",
                            transition: "all 0.2s ease",
                            background: isAnswerKeyTransposed ? "#2563eb" : "transparent",
                            color: isAnswerKeyTransposed ? "#ffffff" : "#64748b",
                            boxShadow: isAnswerKeyTransposed ? "0 1px 4px rgba(37,99,235,0.3)" : "none"
                          }}
                        >
                          <i className="fa-solid fa-table-columns" style={{ fontSize: "0.85rem" }}></i>
                          Column-wise
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <button
                          type="button"
                          disabled={currentAKPage <= 1}
                          onClick={() => setAnswerKeyPage(prev => Math.max(1, prev - 1))}
                          style={{
                            padding: "0.35rem 0.7rem",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            background: currentAKPage <= 1 ? "#f1f5f9" : "#fff",
                            color: currentAKPage <= 1 ? "#94a3b8" : "#1e293b",
                            cursor: currentAKPage <= 1 ? "not-allowed" : "pointer",
                            fontWeight: 600,
                            fontSize: "0.8rem"
                          }}
                        >
                          <i className="fa-solid fa-chevron-left" style={{ marginRight: "4px" }}></i> Prev
                        </button>

                        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>
                          Page {currentAKPage} of {totalAnswerKeyPages}
                          <span style={{ color: "#64748b", fontWeight: 400, marginLeft: "6px" }}>
                            (Q{startIdx + 1} - Q{endIdx} of Q{extractedAnswerKeys.length})
                          </span>
                        </span>

                        <button
                          type="button"
                          disabled={currentAKPage >= totalAnswerKeyPages}
                          onClick={() => setAnswerKeyPage(prev => Math.min(totalAnswerKeyPages, prev + 1))}
                          style={{
                            padding: "0.35rem 0.7rem",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            background: currentAKPage >= totalAnswerKeyPages ? "#f1f5f9" : "#fff",
                            color: currentAKPage >= totalAnswerKeyPages ? "#94a3b8" : "#1e293b",
                            cursor: currentAKPage >= totalAnswerKeyPages ? "not-allowed" : "pointer",
                            fontWeight: 600,
                            fontSize: "0.8rem"
                          }}
                        >
                          Next <i className="fa-solid fa-chevron-right" style={{ marginLeft: "4px" }}></i>
                        </button>
                      </div>
                    </div>

                    {/* Inputs Grid (40 items per page in 5 columns) */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "2px" }}>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                        gap: "8px"
                      }}>
                        {displayItems.map((item) => (
                          <div key={item.originalIndex} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            background: "#fff",
                            padding: "6px 8px",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                            transition: "all 0.2s ease",
                            minWidth: 0
                          }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.boxShadow = "0 2px 4px rgba(59,130,246,0.1)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.02)"; }}
                          >
                            <span style={{ fontWeight: 600, width: "32px", color: "#64748b", fontSize: "0.85rem", flexShrink: 0, textAlign: "right", paddingRight: "4px" }}>
                              {item.questionNumber}.
                            </span>
                            <input
                              type="text"
                              value={item.answerKey}
                              onChange={(e) => {
                                const newVal = e.target.value;
                                setExtractedAnswerKeys(prev => {
                                  const updated = [...prev];
                                  updated[item.originalIndex].answerKey = newVal;
                                  return updated;
                                });
                              }}
                              style={{
                                flex: 1,
                                padding: "6px 4px",
                                border: "1px solid #cbd5e1",
                                borderRadius: "6px",
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "#0f172a",
                                fontSize: "0.95rem",
                                outline: "none",
                                transition: "border-color 0.2s",
                                minWidth: 0
                              }}
                              onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.boxShadow = "0 0 0 2px rgba(59,130,246,0.1)"; }}
                              onBlur={(e) => { e.target.style.borderColor = "#cbd5e1"; e.target.style.boxShadow = "none"; }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="modal-actions" style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => {
                          if (extractedAnswerKeys.length === 0) {
                            swalError("No answer keys to download.");
                            return;
                          }
                          let csvContent = "questionNumber,answerKey\n";
                          extractedAnswerKeys.forEach((item) => {
                            csvContent += `${item.questionNumber},${item.answerKey}\n`;
                          });
                          const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "extracted_answer_key.csv";
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        style={{ width: "auto", flex: "none", padding: "0.5rem 1.25rem", display: "flex", alignItems: "center", gap: "0.5rem", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}
                      >
                        <i className="fa-solid fa-download"></i> Download CSV
                      </button>
                      <button
                        type="button"
                        className="btn-submit"
                        onClick={handleSaveExtractedAnswerKeys}
                        style={{ width: "auto", flex: "none", padding: "0.5rem 1.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}
                      >
                        <i className="fa-solid fa-save"></i> Save & Confirm
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            </Modal>
          );
        })()}


        {/* Extract from PDF Modal */}
        <Modal
          isOpen={isExtractPdfOpen}
          onClose={() => {
            if (!isExtractingPdf) {
              setIsExtractPdfOpen(false);
              setExtractPdfForm({
                questionBankName: "",
                sourceTestPaper: null,
                answerKeysFile: null,
                customInstructions: "",
                numberOfQs: "",
              });
            }
          }}
        >
          <div className="oeb-generate-modal">
            <div className="oeb-generate-modal-header">
              <div
                className="oeb-generate-modal-icon"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(249, 115, 22, 0.2))",
                  color: "#dc2626",
                }}
              >
                <i className="fa-solid fa-file-pdf"></i>
              </div>
              <div>
                <h2>Convert Test Paper to Objective Exam</h2>
                <p className="modal-description">
                  Upload a physical test paper and we&apos;ll extract questions
                  automatically.
                </p>
              </div>
            </div>

            {/* Exam Template dropdown inside Extract from PDF modal */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label
                className="form-label"
                style={{ fontWeight: "600", fontSize: "0.9rem" }}
              >
                Exam Template{" "}
                <span
                  style={{
                    fontWeight: "normal",
                    color: "gray",
                    fontSize: "0.8em",
                  }}
                >
                  (Optional)
                </span>
              </label>
              {(() => {
                const isPdfEdit = extractPdfFor === "edit";
                const pdfCurrentTest = isPdfEdit ? editTest : newTest;
                return (
                  <Select
                    options={filteredStandardizedTestOptions}
                    value={
                      filteredStandardizedTestOptions.find(
                        (opt) =>
                          pdfCurrentTest.standardizedTestId === opt.value,
                      ) || null
                    }
                    onChange={(selected) => {
                      if (isPdfEdit) {
                        setEditTest((prev) => ({
                          ...prev,
                          standardizedTestId: selected ? selected.value : null,
                        }));
                        setIsEditChanged(true);
                      } else {
                        setNewTest((prev) => ({
                          ...prev,
                          standardizedTestId: selected ? selected.value : null,
                        }));
                      }
                    }}
                    isClearable
                    isDisabled={isExtractingPdf}
                    placeholder="Choose an exam template..."
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "8px",
                        borderColor: "#cbd5e1",
                        minHeight: "40px",
                        boxShadow: "none",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "var(--primary, #4f46e5)"
                          : state.isFocused
                            ? "#eef2ff"
                            : "white",
                        color: state.isSelected ? "white" : "#334155",
                      }),
                    }}
                  />
                );
              })()}
              <small
                className="helper-text"
                style={{
                  display: "block",
                  marginTop: "0.4rem",
                  color: "#64748b",
                }}
              >
                Optionally associate this extraction with an exam template.
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Number of Questions <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="number"
                className="form-input"
                min="1"
                placeholder="Enter number of questions you want to extract"
                value={extractPdfForm.numberOfQs}
                onChange={(e) =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    numberOfQs: e.target.value,
                  }))
                }
                disabled={isExtractingPdf}
              />
              <small
                className="helper-text"
                style={{ color: "#666", marginTop: "0.5rem", display: "block" }}
              >
                Specify how many questions you want the AI to extract from the
                test paper.
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Test Paper <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <FileUploadSection
                currentFileUrl={null}
                currentFileName={
                  extractPdfForm.sourceTestPaper
                    ? extractPdfForm.sourceTestPaper.name
                    : null
                }
                isSubmitting={isExtractingPdf}
                accept=".pdf"
                onFileChange={(file, isCaptured) => {
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    sourceTestPaper: file,
                  }));
                }}
                onCapture={(callback) => {
                  setCaptureFor(
                    isEdit ? "edit-test-paper" : "create-test-paper",
                  );
                  setIsCapturing(true);
                  window.__fileUploadCallback = callback;
                }}
                onRemoveSelectedFile={() => {
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    sourceTestPaper: null,
                  }));
                }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Answer Key{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                  (Optional)
                </span>
              </label>
              <FileUploadSection
                key={`answer-key-${extractPdfForm.answerKeysFile ? 'has-file' : 'empty'}`}
                selectedFile={extractPdfForm.answerKeysFile || null}
                currentFileUrl={null}
                currentFileName={
                  extractPdfForm.answerKeysFile
                    ? extractPdfForm.answerKeysFile.name
                    : null
                }
                isSubmitting={isExtractingPdf || isExtractingAnswerKey}
                accept=".csv, .pdf"
                browseDescription="Select CSV or PDF from device"
                showCapture={true}
                onFileChange={(file, isCaptured) => {
                  if (file) {
                    setExtractPdfForm((prev) => ({
                      ...prev,
                      answerKeysFile: file,
                    }));
                    if (!file.name.toLowerCase().endsWith(".csv")) {
                      handleExtractAnswerKey(file);
                    }
                  }
                }}
                onCapture={(callback) => {
                  setCaptureFor("answer-key-extraction");
                  setIsCapturing(true);
                  window.__fileUploadCallback = callback;
                }}
                onRemoveSelectedFile={() => {
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    answerKeysFile: null,
                  }));
                }}
                onPreviewSelectedFile={(file) => {
                  if (file && file.name.toLowerCase().endsWith('.csv')) {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = file.name;
                    a.click();
                    URL.revokeObjectURL(url);
                  }
                }}
              />

              {isExtractingAnswerKey && (
                <div style={{ marginTop: "1rem", padding: "0.75rem", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ color: "#16a34a", fontSize: "1.25rem" }}></i>
                  <div>
                    <h4 style={{ margin: 0, color: "#166534", fontSize: "0.95rem" }}>Extracting Answer Keys...</h4>
                    <p style={{ margin: "0.25rem 0 0 0", color: "#15803d", fontSize: "0.85rem" }}>AI is reading your Answer Key document. Please wait.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Instructions */}
            <div className="form-group" style={{ marginBottom: "1.25rem" }}>
              <label className="form-label">
                Custom Instructions{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                  (Optional)
                </span>
              </label>
              <textarea
                value={extractPdfForm.customInstructions}
                onChange={(e) =>
                  setExtractPdfForm((prev) => ({
                    ...prev,
                    customInstructions: e.target.value,
                  }))
                }
                placeholder="e.g., Focus on conceptual questions, include diagram-based questions..."
                disabled={isExtractingPdf}
                style={{ width: "100%", resize: "vertical", height: "100px" }}
              />
              <small
                className="helper-text"
                style={{ color: "#666", marginTop: "0.5rem", display: "block" }}
              >
                Provide any specific instructions for the AI when extracting
                questions.
              </small>
            </div>

            {isExtractingPdf && (
              <div className="extract-pdf-progress">
                <div className="extract-pdf-progress-visual">
                  <div className="extract-pdf-progress-icon">
                    <i className="fa-solid fa-gears fa-spin"></i>
                  </div>
                  <div className="extract-pdf-progress-bar-container">
                    <div className="oeb-generate-progress-bar">
                      <div
                        className="oeb-generate-progress-fill"
                        style={{
                          background:
                            "linear-gradient(90deg, #dc2626, #f97316)",
                        }}
                      ></div>
                    </div>
                    <span>{extractPdfProgress}</span>
                  </div>
                </div>
                <p className="extract-pdf-progress-hint">
                  <i className="fa-solid fa-info-circle"></i>
                  Please don&apos;t close this window. AI is reading your test
                  paper and converting questions.
                </p>
              </div>
            )}

            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <button
                type="button"
                className="btn-submit"
                onClick={() => handleExtractFromPdf(extractPdfFor === "edit")}
                disabled={
                  isExtractingPdf ||
                  !extractPdfForm.questionBankName.trim() ||
                  !extractPdfForm.sourceTestPaper ||
                  !extractPdfForm.numberOfQs
                }
              >
                {isExtractingPdf ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>{" "}
                    Extracting...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-import"></i> Extract & Add
                    Questions
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  };

  // Handle Create Input Change
  const handleCreateInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    const updatedValue =
      type === "file"
        ? files
          ? files[0]
          : null
        : type === "checkbox"
          ? checked
          : value;

    setNewTest((prev) => ({
      ...prev,
      [name]: updatedValue,
      ...(name === "testPaperURL" && files && { capturedFile: null }), // Reset captured file when new file is selected
    }));
    setFormError("");
  };

  const getRubricsNameById = (rubricId) => {
    const rubric = rubrics.find((rubric) => rubric._id === rubricId);
    return rubric ? rubric.name : null;
  };

  const handleCloseCapture = () => {
    setIsCapturing(false);
  };

  // Callback when capture is done
  const onCaptureComplete = (pdfFile) => {
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(pdfFile);
      window.__fileUploadCallback = null;
    }

    if (captureFor === "create") {
      setNewTest((prev) => ({
        ...prev,
        testPaperURL: pdfFile,
        capturedFile: pdfFile,
      }));
    } else if (captureFor === "edit") {
      setEditTest((prev) => ({
        ...prev,
        testPaperURL: pdfFile,
        capturedFile: pdfFile,
        removeTestPaper: false,
      }));
      setIsEditChanged(true);
    } else if (captureFor === "create-answerkey") {
      // Add this
      setNewTest((prev) => ({
        ...prev,
        answerKeyFile: pdfFile,
        answerKeyCapturedFile: pdfFile,
      }));
    } else if (captureFor === "edit-answerkey") {
      // Add this
      setEditTest((prev) => ({
        ...prev,
        answerKeyFile: pdfFile,
        answerKeyCapturedFile: pdfFile,
        removeAnswerKey: false,
      }));
      setIsEditChanged(true);
    } else if (captureFor === "create-rubric" || captureFor === "edit-rubric") {
      setNewRubricData((prev) => ({
        ...prev,
        file: pdfFile,
        capturedFile: pdfFile,
        text: "",
      }));
    } else if (captureFor === "create-homework") {
      setNewHomework((prev) => ({
        ...prev,
        testPaperURL: pdfFile,
        capturedFile: pdfFile,
      }));
    } else if (captureFor === "create-homework-answerkey") {
      setNewHomework((prev) => ({
        ...prev,
        answerKeyFile: pdfFile,
        answerKeyCapturedFile: pdfFile,
      }));
    } else if (captureFor === "edit-homework") {
      setEditHomework((prev) => ({
        ...prev,
        testPaperURL: pdfFile,
        capturedFile: pdfFile,
        removeTestPaper: false,
      }));
    } else if (captureFor === "edit-homework-answerkey") {
      setEditHomework((prev) => ({
        ...prev,
        answerKeyFile: pdfFile,
        answerKeyCapturedFile: pdfFile,
        removeAnswerKey: false,
      }));
    }
    setIsCapturing(false);
    setCaptureFor(null);
  };

  const syncGoogleClassroomTest = async (testId) => {
    setSyncingTests((prev) => new Set(prev).add(testId));
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/google-classroom/sync-data/test/${testId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to sync test with Google Classroom.",
        );
      }
      swalSuccess("Test synced successfully with Google Classroom.");
      fetchTests(); // Refresh tests after sync
    } catch (err) {
      swalError(err.message || "Failed to sync test with Google Classroom.");
    } finally {
      setSyncingTests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(testId);
        return newSet;
      });
    }
  };

  const syncCanvasTest = async (testId) => {
    setSyncingTests((prev) => new Set(prev).add(testId));
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/canvas/sync-data/test/${testId}`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to sync test with Canvas.",
        );
      }
      swalSuccess("Test synced successfully with Canvas.");
      fetchTests(); // Refresh tests after sync
    } catch (err) {
      swalError(err.message || "Failed to sync test with Canvas.");
    } finally {
      setSyncingTests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(testId);
        return newSet;
      });
    }
  };

  const sourceSystemTestSyncMap = {
    "Google Classroom": syncGoogleClassroomTest,
    Canvas: syncCanvasTest,
  };

  const handleSyncTest = async (testId) => {
    const refreshFn = sourceSystemTestSyncMap[user.sourceSystem];
    if (!refreshFn) return;
    return refreshFn(testId);
  };

  const handleEditClick = async (test) => {
    if (checkIsHomeworkTest(test)) {
      handleOpenEditHomework(test);
      return;
    }

    let populatedQuestions = [...(test.onlineExamQuestions || [])];

    if (test.isOnlineExamination) {
      setEditingTestId(test._id || test.id);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get/${test._id || test.id}?returnAnswers=true`,
          { credentials: "include" },
        );
        if (response && response.ok) {
          const fullTest = await response.json();
          populatedQuestions = (fullTest.onlineExamQuestions || []).map((q) => {
            const questionId = q.questionId || q._sourceId || q._id;
            const optionsArr = (q.options || []).filter(Boolean).map((o) => ({
              key: o.key || "",
              text: o.text || "",
            }));
            const correctOpts = Array.isArray(q.correctOptions)
              ? q.correctOptions
              : [];
            return {
              ...q,
              questionText: q.questionText || "",
              type: q.type || "MCQ",
              options: optionsArr,
              correctOptions: correctOpts,
              correctAnswer: q.correctAnswer || "",
              _sourceId: questionId,
              questionId: questionId,
              _sourceSolution: q.solution || q._sourceSolution || "",
              _isCustom: q._isCustom || false,
              diagramFileURL: q.diagramFileURL || "",
              diagramPageFileURL: q.diagramPageFileURL || "",
              diagramBoundingBox: q.diagramBoundingBox || null,
              svgCode: q.svgCode || "",
            };
          });
        }
      } catch (err) {
        console.error("Failed to fetch test details:", err);
      } finally {
        setEditingTestId(null);
      }
    }

    setEditTest({
      id: test._id || test.id,
      questionBankId: test.questionBankId,
      status: test.status || "",
      testType: test.testType
        ? { value: test.testType, label: test.testType }
        : null,
      testName: test.testName,
      maxScore:
        roundTo2(test.maxScore) ||
        roundTo2(
          populatedQuestions.reduce(
            (sum, q) => sum + (parseFloat(q.maxScore) || 0),
            0,
          ),
        ),
      testPaperURL: null,
      rubricId: test.rubricId,
      scheduledDate: test.scheduledDate
        ? convertToLocal(test.scheduledDate).slice(0, 16)
        : "",
      dueDate: test.dueDate ? convertToLocal(test.dueDate).slice(0, 16) : "",
      duration:
        (test.advancedOnlineExamOptions && test.advancedOnlineExamOptions.length > 0 && populatedQuestions.some((q) => q.duration != null && q.duration !== "" && q.duration !== 0))
          ? String(
            roundTo2(
              populatedQuestions.reduce(
                (sum, q) => sum + (parseFloat(q.duration) || 0),
                0,
              ),
            ),
          )
          : (test.duration != null && test.duration !== "" && !isNaN(Number(test.duration))) ? String(roundTo2(test.duration)) : (test.duration || ""),
      removeTestPaper: false,
      capturedFile: null,
      isExternal: !!test.isExternal,
      resultsAvailableImmediately: !!test.scorePublishedDate,
      isCalculatorAllowed: !!test.isCalculatorAllowed,
      verifyAnswerPaperOnUpload: !!test.verifyAnswerPaperOnUpload,
      isManualEntryQuestions: !!test.isManualEntryQuestions,
      manualEntryQuestions: test.manualEntryQuestions || [],
      isOnlineExamination: !!test.isOnlineExamination,
      isAdvancedOnlineExam: !!(test.advancedOnlineExamOptions && test.advancedOnlineExamOptions.length > 0),
      advancedOnlineExamOptions: {
        videoProctoring: !!test.advancedOnlineExamOptions?.[0]?.videoProctoring,
        navigationBackToQuestion: test.advancedOnlineExamOptions?.[0]?.navigationBackToQuestion !== false,
        shuffleQuestions: !!test.advancedOnlineExamOptions?.[0]?.shuffleQuestions,
        shuffleOptions: !!test.advancedOnlineExamOptions?.[0]?.shuffleOptions,
        isTimedExam: populatedQuestions.some((q) => q.duration != null && q.duration !== "" && q.duration !== 0),
      },
      onlineExamQuestions: populatedQuestions,
      omrTemplateId: test.omrTemplateId || null,
      standardizedTestId: test.standardizedTestId || null,
      certificateTemplateId: test.certificateTemplateId
        ? typeof test.certificateTemplateId === "object"
          ? test.certificateTemplateId._id
          : test.certificateTemplateId
        : null,
      annotationPrintingOffsetConfigId:
        test.annotationPrintingOffsetConfigId || null,
      gradingPolicy: test.gradingPolicy || null,
      answerKeyFile: null,
      answerKeyCapturedFile: null,
      removeAnswerKey: false,
      original: {
        status: test.status || "",
        testType: test.testType,
        testName: test.testName,
        maxScore: roundTo2(test.maxScore),
        rubricId: test.rubricId,
        scheduledDate: test.scheduledDate
          ? convertToLocal(test.scheduledDate).slice(0, 16)
          : "",
        dueDate: test.dueDate ? convertToLocal(test.dueDate).slice(0, 16) : "",
        duration: (test.duration != null && test.duration !== "" && !isNaN(Number(test.duration))) ? String(roundTo2(test.duration)) : (test.duration || ""),
        testPaperURL: test.testPaperURL,
        testPaperFileName: test.testPaperFileName,
        rubricFileName: test.rubricFileName,
        isManualEntryQuestions: test.isManualEntryQuestions,
        verifyAnswerPaperOnUpload: !!test.verifyAnswerPaperOnUpload,
        gradingPolicy: test.gradingPolicy,
        answerKeysFileURL: test.answerKeysFileURL,
        answerKeysFileName: test.answerKeysFileName,
        standardizedTestId: test.standardizedTestId || null,
        certificateTemplateId: test.certificateTemplateId
          ? typeof test.certificateTemplateId === "object"
            ? test.certificateTemplateId._id
            : test.certificateTemplateId
          : null,
        annotationPrintingOffsetConfigId:
          test.annotationPrintingOffsetConfigId || null,
      },
    });
    setInitialEditTest({
      status: test.status,
      testType: test.testType,
      testName: test.testName,
      maxScore: roundTo2(test.maxScore),
      rubricId: test.rubricId,
      scheduledDate: test.scheduledDate
        ? convertToLocal(test.scheduledDate).slice(0, 16)
        : "",
      dueDate: test.dueDate ? convertToLocal(test.dueDate).slice(0, 16) : "",
      duration:
        (test.advancedOnlineExamOptions && test.advancedOnlineExamOptions.length > 0 && populatedQuestions.some((q) => q.duration != null && q.duration !== "" && q.duration !== 0))
          ? String(
            roundTo2(
              populatedQuestions.reduce(
                (sum, q) => sum + (parseFloat(q.duration) || 0),
                0,
              ),
            ),
          )
          : (test.duration != null && test.duration !== "" && !isNaN(Number(test.duration))) ? String(roundTo2(test.duration)) : (test.duration || ""),
      testPaperURL: test.testPaperURL,
      rubricURL: test.rubricURL,
      isExternal: !!test.isExternal,
      resultsAvailableImmediately: !!test.scorePublishedDate,
      isCalculatorAllowed: !!test.isCalculatorAllowed,
      verifyAnswerPaperOnUpload: !!test.verifyAnswerPaperOnUpload,
      isManualEntryQuestions: !!test.isManualEntryQuestions,
      manualEntryQuestions: test.manualEntryQuestions || [],
      isOnlineExamination: !!test.isOnlineExamination,
      isAdvancedOnlineExam: !!(test.advancedOnlineExamOptions && test.advancedOnlineExamOptions.length > 0),
      advancedOnlineExamOptions: {
        videoProctoring: !!test.advancedOnlineExamOptions?.[0]?.videoProctoring,
        navigationBackToQuestion: test.advancedOnlineExamOptions?.[0]?.navigationBackToQuestion !== false,
        shuffleQuestions: !!test.advancedOnlineExamOptions?.[0]?.shuffleQuestions,
        shuffleOptions: !!test.advancedOnlineExamOptions?.[0]?.shuffleOptions,
        isTimedExam: populatedQuestions.some((q) => q.duration != null && q.duration !== "" && q.duration !== 0),
      },
      onlineExamQuestions: populatedQuestions.map((q) => ({ ...q })),
      omrTemplateId: test.omrTemplateId || null,
      standardizedTestId: test.standardizedTestId || null,
      certificateTemplateId: test.certificateTemplateId
        ? typeof test.certificateTemplateId === "object"
          ? test.certificateTemplateId._id
          : test.certificateTemplateId
        : null,
      annotationPrintingOffsetConfigId:
        test.annotationPrintingOffsetConfigId || null,
      gradingPolicy: test.gradingPolicy,
      answerKeysFileURL: test.answerKeysFileURL,
      answerKeysFileName: test.answerKeysFileName,
    });
    // If the test is already a manual-entry test, fetch outcomes (once)
    if (test.isManualEntryQuestions && courseOutcomes.length === 0) {
      fetchCourseOutcomes();
    }
    // If the test is an online exam, fetch outcomes (once)
    if (test.isOnlineExamination) {
      if (courseOutcomes.length === 0) fetchCourseOutcomes();
      if (omrTemplateOptions.length === 0) fetchOmrTemplateLOVs();
    }
    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  // Prepare cards data for CardList with updated title and onClick
  const isHomeworkTab = activeTab === "homeworks";
  const cardsData = [
    ...(!user?.sourceSystem
      ? [
        {
          id: "action-card",
          title: isHomeworkTab
            ? "New Homework"
            : isLanguageInst
              ? "New Assessment"
              : "New Test",
          onAction: isHomeworkTab
            ? () => setIsCreateHomeworkModalOpen(true)
            : handleOpenCreateFlow,
          isActionCard: true,
        },
      ]
      : []),
    ...displayedTests.map((test) => {
      const qbObj =
        typeof test.questionBankId === "object" && test.questionBankId !== null
          ? test.questionBankId
          : null;
      const qbStatus = qbObj?.status;
      const testStatus = test.status;

      let badgeText = null;

      if (testStatus === "Review" && (qbStatus === "Requested" || qbStatus === "Processing")) {
        badgeText = "Extraction in Progress";
      } else if (testStatus === "Review" && qbStatus === "Completed") {
        badgeText = "Pending Review";
      } else if (qbStatus === "Failed") {
        badgeText = "Extraction Failed";
      } else if (testStatus === "Review") {
        badgeText = "In Review";
      }

      const isHomework =
        checkIsHomeworkTest(test) || isHomeworkTab;

      const hasButtons =
        Boolean(test.testPaperURL && !test.isManualEntryQuestions) ||
        Boolean(test.answerKeysFileURL) ||
        Boolean(test.rubricId);

      return {
        id: test._id,
        title: `${test.testName}`,
        badgeText: badgeText,
        description: (!isHomework || hasButtons || test.formattedDueDate) ? (
          <>
            {!isHomework && <p>Max Score: {test.maxScore || ""}</p>}
            {!isHomework && test.testType && (
              <p>
                Test Type:{" "}
                {isLanguageInst && test.testType ? (
                  test.testType === "Mid-Term" ? (
                    <span
                      className="cefr-badge cefr-b1-1"
                      style={{ fontSize: "11px", padding: "2px 8px" }}
                    >
                      <i className="fa-solid fa-file-lines"></i> Mid-Term
                    </span>
                  ) : test.testType === "End-Term" ? (
                    <span
                      className="cefr-badge cefr-a1"
                      style={{ fontSize: "11px", padding: "2px 8px" }}
                    >
                      <i className="fa-solid fa-graduation-cap"></i> End-Term
                    </span>
                  ) : test.testType === "Quiz" ? (
                    <span
                      className="cefr-badge cefr-c1-1"
                      style={{ fontSize: "11px", padding: "2px 8px" }}
                    >
                      <i className="fa-solid fa-bolt"></i> Quiz
                    </span>
                  ) : (
                    test.testType
                  )
                ) : (
                  test.testType || "-"
                )}
              </p>
            )}
            {!isHomework && (
              <p>
                Grading Type :
                {test.isOnlineExamination
                  ? " Objective Exam"
                  : test.isManualEntryQuestions
                    ? " Manual Test"
                    : " AI Grading"}
              </p>
            )}
            {instituteData?.category === "HIGHER_EDUCATION" && !isHomework && (
              <p>Status : {test.isExternal ? "External" : "Internal"}</p>
            )}
            {!isHomework && test.formattedScheduledDate && (
              <p>Start Date: {test.formattedScheduledDate}</p>
            )}
            {test.formattedDueDate && <p>Due Date: {test.formattedDueDate}</p>}
            {test.testPaperURL && !test.isManualEntryQuestions && (
              <p>
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (test.testPaperURL && test.testPaperURL !== "") {
                      window.open(test.testPaperURL, "_blank");
                    } else {
                      swalError(`${checkIsHomeworkTest(test) ? "Homework" : "Test"} paper file not available.`);
                    }
                  }}
                  className="btn-test-paper"
                >
                  <i className="fa-regular fa-eye"></i> {checkIsHomeworkTest(test) ? "Homework" : "Test Paper"}
                </a>
              </p>
            )}
            {test.answerKeysFileURL && (
              <p>
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(test.answerKeysFileURL, "_blank");
                  }}
                  className="btn-answer-key"
                >
                  <i className="fa-regular fa-eye"></i> Answer Key
                </a>
              </p>
            )}
            {test.rubricId && (
              <p>
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRubricPreview(test.rubricId);
                  }}
                  className="btn-rubric-paper"
                >
                  <i className="fa-regular fa-eye"></i> Rubric
                </a>
              </p>
            )}
          </>
        ) : null,
        onClick: () =>
          navigate(`/instructor/test-grading/${test._id}`, {
            state: {
              isHomeworkTest: checkIsHomeworkTest(test) || isHomeworkTab,
            },
          }),
        children: (
          <div className="card__actions">
            {!user?.sourceSystem ? (
              <button
                onClick={() => handleEditClick(test)}
                className="btn-edit"
                aria-label={checkIsHomeworkTest(test) ? "Edit Homework" : "Edit Test"}
                title={
                  checkIsHomeworkTest(test)
                    ? "Edit Homework"
                    : testStatus === "Review"
                      ? "Review Test"
                      : "Edit Test"
                }
                disabled={editingTestId === (test._id || test.id)}
              >
                {editingTestId === (test._id || test.id) ? (
                  <i
                    className="fa-solid fa-spinner fa-spin"
                    style={{ fontSize: "16px", color: "var(--primary-color)" }}
                  ></i>
                ) : testStatus === "Review" ? (
                  <i
                    className="fa-solid fa-circle-question"
                    style={{ fontSize: "20px" }}
                    title="Review Test"
                  ></i>
                ) : (
                  <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={25} />
                )}
              </button>
            ) : (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const button = e.currentTarget;
                  button.classList.add("syncing");
                  await handleSyncTest(test._id);
                  button.classList.remove("syncing");
                  button.classList.add("sync-success");
                  setTimeout(() => {
                    button.classList.remove("sync-success");
                  }, 600);
                }}
                className="btn-edit"
                aria-label="Sync Test"
                title={`Sync from ${user.sourceSystem}`}
                disabled={
                  isSubmitting ||
                  deletingTests.has(test._id) ||
                  syncingTests.has(test._id)
                }
              >
                <div className="sync-icon-wrapper">
                  <img
                    src={
                      user.sourceSystem === "Canvas"
                        ? "/imgs/canvas_logo.png"
                        : "/imgs/google_classroom_logo.png"
                    }
                    alt={`${user.sourceSystem} logo`}
                    className={`platform-logo ${syncingTests.has(test._id) ? "syncing" : ""
                      }`}
                  />
                  {syncingTests.has(test._id) ? (
                    <div className="sync-spinner">
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                    </div>
                  ) : (
                    <div className="sync-icon">
                      <i className="fa-solid fa-arrows-rotate"></i>
                    </div>
                  )}
                </div>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTest(test._id);
              }}
              className="btn-delete"
              aria-label={checkIsHomeworkTest(test) ? "Delete Homework" : "Delete Test"}
              title={checkIsHomeworkTest(test) ? "Delete Homework" : "Delete Test"}
              disabled={deletingTests.has(test._id)}
            >
              {deletingTests.has(test._id) ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <img src="/imgs/trash_icon.svg" alt="delete-btn" width={25} />
              )}
            </button>
          </div>
        ),
      };
    }),
  ];

  const columns = [
    { key: "testName", label: "Test Name", sortable: true, filter: true },
    { key: "testType", label: "Test Type", sortable: true, filter: true },
    { key: "maxScore", label: "Max Score", sortable: true },
    {
      key: "formattedScheduledDate",
      label: "Scheduled Date",
      sortable: true,
    },
    { key: "formattedDueDate", label: "Due Date", sortable: true },
    { key: "view", label: "Test Paper", sortable: false },
    { key: "answerKeyView", label: "Answer Key", sortable: false },
    { key: "rubricView", label: "Rubric", sortable: false },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  const enhancedTests = displayedTests.map((test) => {
    const formattedScheduledDate = userDate(test.scheduledDate);
    const formattedDueDate = userDate(test.dueDate);

    const qbObj =
      typeof test.questionBankId === "object" && test.questionBankId !== null
        ? test.questionBankId
        : null;
    const qbStatus = qbObj?.status;
    const testStatus = test.status;

    let badgeText = null;
    if (testStatus === "Review" && (qbStatus === "Requested" || qbStatus === "Processing")) {
      badgeText = "Extracting";
    } else if (testStatus === "Review" && qbStatus === "Completed") {
      badgeText = "Pending Review";
    } else if (qbStatus === "Failed") {
      badgeText = "Extraction Failed";
    } else if (testStatus === "Review") {
      badgeText = "In Review";
    }

    const badgeSpan = badgeText ? (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "11px",
          fontWeight: "600",
          backgroundColor: qbStatus === "Failed" ? "#fee2e2" : (badgeText === "Pending Review" ? "#dcfce7" : "#fef3c7"),
          color: qbStatus === "Failed" ? "#b91c1c" : (badgeText === "Pending Review" ? "#15803d" : "#b45309"),
          marginLeft: "6px",
        }}
      >
        {qbStatus === "Failed" ? <i className="fa-solid fa-circle-xmark"></i> :
          badgeText === "Pending Review" ? <i className="fa-solid fa-circle-check"></i> :
          badgeText === "In Review" ? <i className="fa-solid fa-eye-slash"></i> :
            <i className="fa-solid fa-spinner fa-spin"></i>} {badgeText}
      </span>
    ) : null;

    return {
      ...test,
      testName: (
        <div>
          <span>{test.testName}</span>
          {badgeSpan}
        </div>
      ),
      formattedScheduledDate,
      formattedDueDate,
      testType:
        isLanguageInst && test.testType ? (
          test.testType === "Mid-Term" ? (
            <span
              className="cefr-badge cefr-b1-1"
              style={{ fontSize: "11px", padding: "2px 8px" }}
            >
              <i className="fa-solid fa-file-lines"></i> Mid-Term
            </span>
          ) : test.testType === "End-Term" ? (
            <span
              className="cefr-badge cefr-a1"
              style={{ fontSize: "11px", padding: "2px 8px" }}
            >
              <i className="fa-solid fa-graduation-cap"></i> End-Term
            </span>
          ) : test.testType === "Quiz" ? (
            <span
              className="cefr-badge cefr-c1-1"
              style={{ fontSize: "11px", padding: "2px 8px" }}
            >
              <i className="fa-solid fa-bolt"></i> Quiz
            </span>
          ) : (
            test.testType
          )
        ) : (
          test.testType || "-"
        ),
      view: test.testPaperURL ? (
        <button
          className="preview-btn"
          onClick={() => window.open(test.testPaperURL, "_blank")}
          disabled={isSubmitting || deletingTests.has(test._id)}
          title="View Test Paper"
        >
          <i className="fa-solid fa-eye"></i>
        </button>
      ) : (
        "-"
      ),
      answerKeyView:
        test.answerKeysFileURL || test.answerKeyURL ? (
          <button
            className="preview-btn"
            onClick={() =>
              window.open(test.answerKeysFileURL || test.answerKeyURL, "_blank")
            }
            disabled={isSubmitting || deletingTests.has(test._id)}
            title="View Answer Key"
          >
            <i className="fa-solid fa-eye"></i>
          </button>
        ) : (
          "-"
        ),
      rubricView: (
        <span
          style={{
            display: "inline-block",
            maxWidth: "150px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "middle",
          }}
          title={getRubricsNameById(test.rubricId) || "N/A"}
        >
          {getRubricsNameById(test.rubricId) || "N/A"}
        </span>
      ),
      edit: (
        <button
          className="edit-btn"
          onClick={() => handleEditClick(test)}
          disabled={
            isSubmitting ||
            deletingTests.has(test._id) ||
            editingTestId === (test._id || test.id)
          }
          title={checkIsHomeworkTest(test) ? "Edit Homework" : "Edit"}
        >
          {editingTestId === (test._id || test.id) ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-pen-to-square"></i>
          )}
        </button>
      ),
      delete: (
        <button
          className="delete-btn"
          onClick={() => handleDeleteTest(test._id || test.id)}
          disabled={isSubmitting || deletingTests.has(test._id)}
          title="Delete"
        >
          {deletingTests.has(test._id) ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash"></i>
          )}
        </button>
      ),
    };
  });

  return (
    <>
      {isCapturing ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "white",
          }}
        >
          <MobileCapture
            onComplete={onCaptureComplete}
            onClose={handleCloseCapture}
          />
        </div>
      ) : (
        <>
          {role === "admin" && isHomeworkGradingAllowed && (
            <div
              className="manage-academics-container"
              style={{
                margin: "0 auto 1.5rem auto",
                background: "white",
                borderRadius: "16px",
                padding: "0.5rem",
              }}
            >
              <div className="tabs" style={{ borderRadius: "12px" }}>
                <button
                  type="button"
                  id="tab-tests-btn"
                  className={`tab-btn ${activeTab === "tests" ? "active" : ""}`}
                  onClick={() => handleTabChange("tests")}
                >
                  {isLanguageInst ? "Exams & Quizzes" : "Tests"}
                </button>
                <button
                  type="button"
                  id="tab-homeworks-btn"
                  className={`tab-btn ${activeTab === "homeworks" ? "active" : ""}`}
                  onClick={() => handleTabChange("homeworks")}
                >
                  Homeworks
                </button>
              </div>
            </div>
          )}
          {role === "admin" && (
            <div className="manage-container">
              <div
                className="header"
                style={{
                  display: "flex",
                  justifyContent: classDetails ? "space-between" : "flex-end",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                {classDetails}
                <button
                  className="create-btn"
                  onClick={
                    isHomeworkTab
                      ? () => setIsCreateHomeworkModalOpen(true)
                      : handleOpenCreateFlow
                  }
                  style={{
                    marginTop: classDetails
                      ? "0"
                      : isHomeworkGradingAllowed
                        ? "0.5rem"
                        : "3rem",
                  }}
                >
                  {isHomeworkTab
                    ? "Add Homework +"
                    : isLanguageInst
                      ? "Add Assessment +"
                      : "Add Test +"}
                </button>
              </div>
            </div>
          )}
          <div className="manage-container">
            {loading && <Loader />}
            {error && <div className="error-message">{error}</div>}
            {!loading && !error && (
              <>
                {role === "admin" ? (
                  <Table
                    data={enhancedTests}
                    columns={columns}
                    caption={
                      isHomeworkTab
                        ? "Homeworks"
                        : isLanguageInst
                          ? "Exams & Quizzes"
                          : "Tests"
                    }
                  />
                ) : (
                  <CardList cards={cardsData} />
                )}
              </>
            )}
          </div>

          {/* Add Test Modal */}
          <Modal
            isOpen={isCreateModalOpen}
            onClose={async () => {
              if (
                (newTest.isOnlineExamination &&
                  newTest.onlineExamQuestions &&
                  newTest.onlineExamQuestions.length > 0) ||
                (newTest.isManualEntryQuestions &&
                  newTest.manualEntryQuestions &&
                  newTest.manualEntryQuestions.length > 0)
              ) {
                const result = await swalConfirm(
                  "Unsaved Changes",
                  "You have added questions to this test. Closing will discard your progress. Are you sure you want to close?",
                  "Yes, Discard",
                  "No, Keep Editing",
                );
                if (!result.isConfirmed) return;
              }
              setIsCreateModalOpen(false);
              setNewTest({
                testType: null,
                testName: "",
                testPaperURL: null,
                maxScore: "",
                rubricURL: null,
                scheduledDate: "",
                dueDate: "",
                duration: "",
                capturedFile: null,
                isExternal: false,
                isManualEntryQuestions: false,
                manualEntryQuestions: [],
                isOnlineExamination: false,
                isAdvancedOnlineExam: false,
                advancedOnlineExamOptions: {
                  videoProctoring: false,
                  navigationBackToQuestion: true,
                  shuffleQuestions: false,
                  shuffleOptions: false,
                },
                onlineExamQuestions: [],
                omrTemplate: [],
                gradingPolicy: null,
                answerKeyFile: null,
                answerKeyCapturedFile: null,
                resultsAvailableImmediately: false,
                isCalculatorAllowed: false,
                verifyAnswerPaperOnUpload: false,
              });

              setShowNewRubricForm(false);
              setNewRubricData({
                name: "",
                file: null,
                text: "",
                capturedFile: null,
              });
              setGenerateAIPrompt(true);
              setFormError("");
              setUserPromptProgress("");
              setUserPromptPhase("");
              setSelectedQuizSkills([]);
              setSelectedQuizFormats([]);
            }}
            maxWidth={
              (newTest.isManualEntryQuestions && !isK12School) ||
                newTest.isOnlineExamination
                ? "100%"
                : undefined
            }
          >
            <h2>{isLanguageInst ? "Create Assessment / Exam" : "Add New Test"}</h2>
            <p className="modal-description" style={{ marginBottom: "0.5rem" }}>
              {isLanguageInst
                ? "Choose a quick preset or enter details below to schedule an exam or quiz for your batch."
                : "Fill in the details below to create a new test for your class."}
            </p>

            <form onSubmit={handleCreateTest} className="test-form">
              {/* Quick Assessment Presets for Language Learning Institutes */}
              {isLanguageInst && (
                <div style={{ marginBottom: "1.25rem" }}>
                  <label
                    style={{
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      color: "#334155",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Quick Assessment Presets
                  </label>
                  <div
                    className="lang-action-grid"
                    style={{
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(170px, 1fr))",
                      gap: "10px",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <div
                      className={`lang-action-card ${newTest.testType?.value === "Mid-Term" ? "active" : ""
                        }`}
                      onClick={() => {
                        const batchPrefix = selectedClass?.name
                          ? `${selectedClass.name} - `
                          : "";
                        setNewTest((prev) => ({
                          ...prev,
                          testType: { value: "Mid-Term", label: "Mid-Term Exam" },
                          testName:
                            prev.testName &&
                              !prev.testName.includes("Exam") &&
                              !prev.testName.includes("Quiz")
                              ? prev.testName
                              : `${batchPrefix}Mid-Term Exam`,
                        }));
                      }}
                      style={{ padding: "10px 12px" }}
                    >
                      <div
                        className="lang-action-icon"
                        style={{
                          background: "#eff6ff",
                          color: "#2563eb",
                          width: "32px",
                          height: "32px",
                          fontSize: "1rem",
                        }}
                      >
                        <i className="fa-solid fa-file-lines"></i>
                      </div>
                      <div className="lang-action-card-content">
                        <h4 style={{ fontSize: "0.88rem" }}>Mid-Term Exam</h4>
                        <p style={{ fontSize: "0.74rem" }}>Mid-course evaluation</p>
                      </div>
                    </div>

                    <div
                      className={`lang-action-card ${newTest.testType?.value === "End-Term" ? "active" : ""
                        }`}
                      onClick={() => {
                        const batchPrefix = selectedClass?.name
                          ? `${selectedClass.name} - `
                          : "";
                        setNewTest((prev) => ({
                          ...prev,
                          testType: { value: "End-Term", label: "End-Term Exam" },
                          testName:
                            prev.testName &&
                              !prev.testName.includes("Exam") &&
                              !prev.testName.includes("Quiz")
                              ? prev.testName
                              : `${batchPrefix}End-Term Exam`,
                        }));
                      }}
                      style={{ padding: "10px 12px" }}
                    >
                      <div
                        className="lang-action-icon"
                        style={{
                          background: "#ecfdf5",
                          color: "#059669",
                          width: "32px",
                          height: "32px",
                          fontSize: "1rem",
                        }}
                      >
                        <i className="fa-solid fa-graduation-cap"></i>
                      </div>
                      <div className="lang-action-card-content">
                        <h4 style={{ fontSize: "0.88rem" }}>End-Term Exam</h4>
                        <p style={{ fontSize: "0.74rem" }}>Final CEFR milestone</p>
                      </div>
                    </div>

                    <div
                      className={`lang-action-card ${newTest.testType?.value === "Quiz" ? "active" : ""
                        }`}
                      onClick={() => {
                        const batchPrefix = selectedClass?.name
                          ? `${selectedClass.name} - `
                          : "";
                        setNewTest((prev) => ({
                          ...prev,
                          testType: { value: "Quiz", label: "Quiz" },
                          testName:
                            prev.testName &&
                              !prev.testName.includes("Exam") &&
                              !prev.testName.includes("Quiz")
                              ? prev.testName
                              : `${batchPrefix}Quiz`,
                        }));
                      }}
                      style={{ padding: "10px 12px" }}
                    >
                      <div
                        className="lang-action-icon"
                        style={{
                          background: "#fdf4ff",
                          color: "#a21caf",
                          width: "32px",
                          height: "32px",
                          fontSize: "1rem",
                        }}
                      >
                        <i className="fa-solid fa-bolt"></i>
                      </div>
                      <div className="lang-action-card-content">
                        <h4 style={{ fontSize: "0.88rem" }}>Quiz</h4>
                        <p style={{ fontSize: "0.74rem" }}>Skill & topic checks</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Test Name */}
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                  {isLanguageInst ? "Assessment / Exam Name" : "Test Name"}
                </label>
                <input
                  id="testNameInput"
                  type="text"
                  name="testName"
                  value={newTest.testName}
                  onChange={handleCreateInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder={
                    isLanguageInst
                      ? "e.g. German A1 - Mid-Term Exam"
                      : "Enter a descriptive test name"
                  }
                />
              </div>

              {/* Advanced Online Exam Toggle - only when online exam + institute allows */}
              {newTest.isOnlineExamination &&
                instituteData?.isAdvancedOnlineExamAllowed && (
                  <div
                    style={{
                      padding: "1rem 1.25rem",
                      border: "1px solid #e0e7ff",
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
                      marginBottom: "1rem",
                    }}
                  >
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>
                        <i
                          className="fa-solid fa-shield-halved"
                          style={{
                            marginRight: "0.4rem",
                            color: "var(--color-1)",
                          }}
                        />
                        Advanced Online Exam
                      </label>
                      <label className="solution-toggle">
                        <input
                          type="checkbox"
                          name="isAdvancedOnlineExam"
                          checked={!!newTest.isAdvancedOnlineExam}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setNewTest((prev) => ({
                              ...prev,
                              isAdvancedOnlineExam: checked,
                              advancedOnlineExamOptions: {
                                videoProctoring: checked
                                  ? prev.advancedOnlineExamOptions?.videoProctoring || false
                                  : false,
                                navigationBackToQuestion: checked
                                  ? prev.advancedOnlineExamOptions?.navigationBackToQuestion !== false
                                  : true,
                                shuffleQuestions: checked
                                  ? prev.advancedOnlineExamOptions?.shuffleQuestions || false
                                  : false,
                                shuffleOptions: checked
                                  ? prev.advancedOnlineExamOptions?.shuffleOptions || false
                                  : false,
                              },
                            }));
                          }}
                          disabled={isSubmitting}
                        />
                        <span className="toggle-track">
                          <span className="toggle-indicator"></span>
                        </span>
                        <span className="toggle-label-text">
                          {newTest.isAdvancedOnlineExam
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </label>
                      <small
                        className="helper-text"
                        style={{ color: "#666" }}
                      >
                        <i
                          className="fa-solid fa-circle-info"
                          style={{ marginRight: "0.3rem" }}
                        />
                        Enable advanced features like video proctoring,
                        navigation control, shuffling, and per-question duration
                        tracking.
                      </small>
                    </div>

                    {/* Sub-toggles */}
                    {newTest.isAdvancedOnlineExam && (
                      <div
                        style={{
                          display: "flex",
                          gap: "1.5rem",
                          flexWrap: "wrap",
                          marginTop: "1rem",
                          paddingTop: "0.75rem",
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        {/* Video Proctoring */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-video"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Video Proctoring
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!newTest.advancedOnlineExamOptions?.videoProctoring}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setNewTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    videoProctoring: checked,
                                  },
                                }));
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {newTest.advancedOnlineExamOptions?.videoProctoring
                                ? "Enabled"
                                : "Disabled"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {newTest.advancedOnlineExamOptions?.videoProctoring
                              ? "Students will be monitored via webcam during the exam."
                              : "No video monitoring during the exam."}
                          </small>
                        </div>

                        {/* Navigation Back to Question */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-arrow-left"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Navigation Back to Question
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={newTest.advancedOnlineExamOptions?.navigationBackToQuestion !== false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setNewTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    navigationBackToQuestion: checked,
                                  },
                                }));
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {newTest.advancedOnlineExamOptions?.navigationBackToQuestion !== false
                                ? "Allowed"
                                : "Not Allowed"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {newTest.advancedOnlineExamOptions?.navigationBackToQuestion !== false
                              ? "Students can go back to previous questions."
                              : "Students cannot navigate back to previous questions."}
                          </small>
                        </div>

                        {/* Shuffle Questions */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-shuffle"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Shuffle Questions
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!newTest.advancedOnlineExamOptions?.shuffleQuestions}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setNewTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    shuffleQuestions: checked,
                                  },
                                }));
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {newTest.advancedOnlineExamOptions?.shuffleQuestions
                                ? "Enabled"
                                : "Disabled"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {newTest.advancedOnlineExamOptions?.shuffleQuestions
                              ? "Questions will be randomly shuffled."
                              : "Questions appear in fixed sequence."}
                          </small>
                        </div>

                        {/* Shuffle Options */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-random"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Shuffle Options
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!newTest.advancedOnlineExamOptions?.shuffleOptions}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setNewTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    shuffleOptions: checked,
                                  },
                                }));
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {newTest.advancedOnlineExamOptions?.shuffleOptions
                                ? "Enabled"
                                : "Disabled"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {newTest.advancedOnlineExamOptions?.shuffleOptions
                              ? "Option order will be randomized for each student."
                              : "Options appear in standard order."}
                          </small>
                        </div>

                        {/* Allow Duration Per Question */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-regular fa-clock"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Allow Duration Per Question
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!newTest.advancedOnlineExamOptions?.isTimedExam}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setNewTest((prev) => {
                                  let dur = prev.duration;
                                  if (checked && prev.onlineExamQuestions?.length > 0) {
                                    dur = String(
                                      roundTo2(
                                        prev.onlineExamQuestions.reduce(
                                          (sum, q) => sum + (parseFloat(q.duration) || 0),
                                          0,
                                        ),
                                      ),
                                    );
                                  }
                                  return {
                                    ...prev,
                                    duration: dur,
                                    advancedOnlineExamOptions: {
                                      ...prev.advancedOnlineExamOptions,
                                      isTimedExam: checked,
                                    },
                                  };
                                });
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {newTest.advancedOnlineExamOptions?.isTimedExam
                                ? "Yes"
                                : "No"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {newTest.advancedOnlineExamOptions?.isTimedExam
                              ? "Exam has a timer per question."
                              : "Exam has an overall timer."}
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* Max Score - Hidden for Online Exam (auto-calculated) */}
              {!newTest.isOnlineExamination && (
                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i> Max
                    Score
                  </label>
                  <input
                    type="number"
                    name="maxScore"
                    value={newTest.maxScore}
                    onChange={handleCreateInputChange}
                    required
                    min="0"
                    step="1"
                    disabled={isSubmitting}
                    placeholder="Enter the maximum score"
                  />
                  <small className="helper-text">
                    Enter the total marks for this test (whole numbers only)
                  </small>
                </div>
              )}

              {/* Test Paper - Hide when Manual Entry or Online Exam is selected */}
              {!newTest.isManualEntryQuestions &&
                !newTest.isOnlineExamination && (
                  <div>{renderTestPaperUpload(false)}</div>
                )}

              {/* Generate AI Grading Instructions */}
              {(newTest.testPaperURL || newTest.capturedFile) && (
                <div className="form-group">
                  <label>Generate AI Grading Instructions</label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      checked={generateAIPrompt}
                      onChange={(e) => {
                        setGenerateAIPrompt(e.target.checked);
                      }}
                      disabled={isSubmitting}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {generateAIPrompt ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                  <small
                    className="helper-text"
                    style={{
                      color: "#666",
                      marginTop: "0.5rem",
                      display: "block",
                    }}
                  >
                    We will analyze your test paper and generate custom
                    instructions to ensure accurate and consistent AI grading.
                  </small>
                </div>
              )}

              {/* Conditional Manual Entry Table */}
              {newTest.isManualEntryQuestions && !isK12School && (
                <div className="table-container">
                  <h3
                    style={{
                      marginBottom: "1rem",
                      marginTop: "1rem",
                      color: "#333",
                    }}
                  >
                    Manual Entry Questions
                  </h3>
                  {renderManualEntryQuestionsTable(false)}
                </div>
              )}

              {/* Online Exam Builder - active for both tabs */}
              {newTest.isOnlineExamination && renderOnlineExamBuilder(false)}

              {newTest.isOnlineExamination && (
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginTop: "1.25rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  {/* Start Date & Time */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-solid fa-calendar-day"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Start Date & Time{" "}
                      <span
                        style={{
                          fontWeight: "normal",
                          color: "gray",
                          fontSize: "0.85em",
                        }}
                      >
                        (Optional)
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      name="scheduledDate"
                      value={newTest.scheduledDate}
                      onChange={handleCreateInputChange}
                      step="900"
                      disabled={isSubmitting}
                    />
                    <small className="helper-text">
                      Exam opens for students at this time.
                    </small>
                  </div>

                  {/* End Date & Time */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-solid fa-calendar-xmark"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-2)",
                        }}
                      />
                      End Date & Time{" "}
                      <span
                        style={{
                          fontWeight: "normal",
                          color: "gray",
                          fontSize: "0.85em",
                        }}
                      >
                        (Optional)
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      name="dueDate"
                      value={newTest.dueDate}
                      onChange={handleCreateInputChange}
                      step="900"
                      disabled={isSubmitting}
                    />
                    <small className="helper-text">
                      Exam auto-submits at this time.
                    </small>
                  </div>

                  {/* Duration */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-regular fa-clock"
                        style={{
                          marginRight: "0.5rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Duration (minutes){" "}
                      {!newTest.isAdvancedOnlineExam && (
                        <span
                          style={{
                            fontWeight: "normal",
                            color: "gray",
                            fontSize: "0.85em",
                          }}
                        >
                          (Optional)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="duration"
                      value={
                        (newTest.isAdvancedOnlineExam && newTest.advancedOnlineExamOptions?.isTimedExam)
                          ? (newTest.duration !== "" && newTest.duration != null ? roundTo2(newTest.duration) : "")
                          : newTest.duration
                      }
                      onChange={handleCreateInputChange}
                      min="1"
                      disabled={isSubmitting || (newTest.isAdvancedOnlineExam && newTest.advancedOnlineExamOptions?.isTimedExam)}
                      placeholder="e.g. 60"
                      style={{
                        padding: "0.75rem 1rem",
                        border: "2px solid #eee",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        backgroundColor: (newTest.isAdvancedOnlineExam && newTest.advancedOnlineExamOptions?.isTimedExam)
                          ? "#f5f5f5"
                          : "white",
                        cursor: (newTest.isAdvancedOnlineExam && newTest.advancedOnlineExamOptions?.isTimedExam)
                          ? "not-allowed"
                          : "text",
                      }}
                    />
                    <small className="helper-text">
                      {(newTest.isAdvancedOnlineExam && newTest.advancedOnlineExamOptions?.isTimedExam)
                        ? "Duration is auto-calculated based on per-question durations."
                        : "Time limit for the exam."}
                    </small>
                  </div>

                  {/* 4-Digit PIN */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-solid fa-lock"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      4-Digit Exam PIN{" "}
                      <span
                        style={{
                          fontWeight: "normal",
                          color: "gray",
                          fontSize: "0.85em",
                        }}
                      >
                        (Optional)
                      </span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="onlineExamPasscode"
                      value={newTest.onlineExamPasscode}
                      onChange={(e) => {
                        const val = e.target.value
                          .replace(/[^0-9]/g, "")
                          .slice(0, 4);
                        setNewTest((prev) => ({
                          ...prev,
                          onlineExamPasscode: val,
                        }));
                      }}
                      pattern="\d{4}"
                      disabled={isSubmitting}
                      placeholder="Enter 4 digits"
                      style={{
                        letterSpacing: "0.2rem",
                      }}
                    />
                    <small className="helper-text">
                      <i
                        className="fa-solid fa-circle-info"
                        style={{ marginRight: "0.3rem" }}
                      />
                      Students must enter this PIN to start the exam. Keep it
                      secure!
                    </small>
                  </div>
                </div>
              )}

              {/* Answer Key */}
              {!newTest.isManualEntryQuestions &&
                !newTest.isOnlineExamination && (
                  <div>{renderAnswerKeyUpload(false)}</div>
                )}

              {/* Rubric */}
              {!newTest.isManualEntryQuestions &&
                !newTest.isOnlineExamination && (
                  <div>{renderRubricSection(false)}</div>
                )}

              {/* Grading Policy */}
              {!newTest.isManualEntryQuestions &&
                !newTest.isOnlineExamination && (
                  <div>{renderGradingPolicySection(false)}</div>
                )}

              {newTest.isOnlineExamination && (
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginTop: "1.25rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  {/* Calculator Allowed */}
                  <div className="form-group">
                    <label>
                      <i
                        className="fa-solid fa-calculator"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Calculator Allowed
                    </label>
                    <label className="solution-toggle">
                      <input
                        type="checkbox"
                        name="isCalculatorAllowed"
                        checked={!!newTest.isCalculatorAllowed}
                        onChange={handleCreateInputChange}
                        disabled={isSubmitting}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                      <span className="toggle-label-text">
                        {newTest.isCalculatorAllowed
                          ? "Allowed"
                          : "Not Allowed"}
                      </span>
                    </label>
                    <small className="helper-text" style={{ color: "#666" }}>
                      <i
                        className="fa-solid fa-circle-info"
                        style={{ marginRight: "0.3rem" }}
                      />
                      {newTest.isCalculatorAllowed
                        ? "Students will be allowed to use calculator during the exam."
                        : "Calculator will not be accessible to students during the exam."}
                    </small>
                  </div>

                  {/* Results Available Immediately */}
                  {user?.isPublishResultAllowed && (
                    <div className="form-group">
                      <label>
                        <i
                          className="fa-solid fa-bolt"
                          style={{
                            marginRight: "0.4rem",
                            color: "var(--color-1)",
                          }}
                        />
                        Results Available Immediately
                      </label>
                      <label className="solution-toggle">
                        <input
                          type="checkbox"
                          name="resultsAvailableImmediately"
                          checked={!!newTest.resultsAvailableImmediately}
                          onChange={handleCreateInputChange}
                          disabled={isSubmitting}
                        />
                        <span className="toggle-track">
                          <span className="toggle-indicator"></span>
                        </span>
                        <span className="toggle-label-text">
                          {newTest.resultsAvailableImmediately
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </label>
                      <small className="helper-text" style={{ color: "#666" }}>
                        <i
                          className="fa-solid fa-circle-info"
                          style={{ marginRight: "0.3rem" }}
                        />
                        {newTest.resultsAvailableImmediately
                          ? "Students will see their scores immediately after submitting the exam."
                          : "Students will only see results after you publish them manually."}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {/* OMR Template & Grading Template Selection */}
              {newTest.isOnlineExamination && (() => {
                const selectedOmr = filteredOmrTemplateOptions.find(
                  (opt) => opt.value === newTest.omrTemplateId,
                );

                return (
                  <div
                    style={{
                      marginBottom: "1rem",
                    }}
                  >
                    {/* If institute has OMR templates, show Exam Template dropdown ONLY */}
                    {filteredOmrTemplateOptions.length > 0 ? (
                      <div
                        style={{
                          padding: "1.25rem",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <h3
                          style={{
                            marginBottom: "0.75rem",
                            color: "#334155",
                            fontSize: "1.05rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <i
                            className="fa-solid fa-sheet-plastic"
                            style={{ color: "var(--primary, #4f46e5)" }}
                          ></i>
                          Exam Template
                          <span
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: "normal",
                              color: "gray",
                              marginLeft: "0.25rem",
                            }}
                          >
                            (Optional)
                          </span>
                        </h3>

                        {selectedOmr && (
                          <div
                            style={{
                              marginBottom: "12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0.6rem 0.85rem",
                              background:
                                "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
                              borderRadius: "8px",
                              border: "1px solid #c7d2fe",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                fontSize: "0.85rem",
                                color: "#4338ca",
                                fontWeight: "500",
                              }}
                            >
                              <i
                                className="fa-solid fa-circle-check"
                                style={{ color: "#4f46e5" }}
                              ></i>
                              Supports OMR Sheet Scanning
                            </span>
                            {selectedOmr.templateURL && (
                              <a
                                href={selectedOmr.templateURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                  color: "var(--primary, #4f46e5)",
                                  textDecoration: "none",
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                  padding: "0.3rem 0.65rem",
                                  borderRadius: "6px",
                                  backgroundColor: "white",
                                  border: "1px solid #c7d2fe",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#eef2ff";
                                  e.currentTarget.style.borderColor = "#818cf8";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "white";
                                  e.currentTarget.style.borderColor = "#c7d2fe";
                                }}
                              >
                                <i
                                  className="fa-solid fa-eye"
                                  style={{ fontSize: "0.8rem" }}
                                ></i>
                                View OMR
                              </a>
                            )}
                          </div>
                        )}

                        <Select
                          options={filteredOmrTemplateOptions}
                          value={selectedOmr || null}
                          onChange={(selected) => {
                            const linkedStdTestId =
                              selected?.standardizedTestId || null;
                            setNewTest((prev) => ({
                              ...prev,
                              omrTemplateId: selected ? selected.value : null,
                              standardizedTestId: selected ? linkedStdTestId : null,
                            }));
                          }}
                          isClearable
                          isDisabled={isSubmitting}
                          placeholder="Choose an exam template..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: "10px",
                              borderColor: "#cbd5e1",
                              minHeight: "44px",
                              boxShadow: "none",
                              "&:hover": { borderColor: "var(--primary, #4f46e5)" },
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? "var(--primary, #4f46e5)"
                                : state.isFocused
                                  ? "var(--primary-100, #eef2ff)"
                                  : "white",
                              color: state.isSelected ? "white" : "#334155",
                            }),
                          }}
                        />
                        <small
                          className="helper-text"
                          style={{ marginTop: "0.5rem", display: "block" }}
                        >
                          Select a pre-configured exam template. This template supports
                          OMR sheet scanning for automatic grading.
                        </small>
                      </div>
                    ) : (
                      /* Grading Template Selection - shown if no OMR templates available */
                      <div
                        style={{
                          padding: "1.25rem",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <h3
                          style={{
                            marginBottom: "0.75rem",
                            color: "#334155",
                            fontSize: "1.05rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <i
                            className="fa-solid fa-graduation-cap"
                            style={{ color: "var(--primary, #4f46e5)" }}
                          ></i>
                          Grading Template
                          <span
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: "normal",
                              color: "gray",
                              marginLeft: "0.25rem",
                            }}
                          >
                            (Optional)
                          </span>
                        </h3>

                        <Select
                          options={filteredStandardizedTestOptions}
                          value={
                            filteredStandardizedTestOptions.find(
                              (opt) => newTest.standardizedTestId === opt.value,
                            ) || null
                          }
                          onChange={(selected) => {
                            setNewTest((prev) => ({
                              ...prev,
                              standardizedTestId: selected ? selected.value : null,
                            }));
                          }}
                          isClearable
                          isDisabled={isSubmitting}
                          placeholder="Choose a grading template..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: "10px",
                              borderColor: "#cbd5e1",
                              minHeight: "44px",
                              boxShadow: "none",
                              "&:hover": { borderColor: "var(--primary, #4f46e5)" },
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? "var(--primary, #4f46e5)"
                                : state.isFocused
                                  ? "var(--primary-100, #eef2ff)"
                                  : "white",
                              color: state.isSelected ? "white" : "#334155",
                            }),
                          }}
                        />
                        <small
                          className="helper-text"
                          style={{ marginTop: "0.5rem", display: "block" }}
                        >
                          Select a grading template if this test follows grading
                          criteria.
                        </small>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Test Type */}
              <div className="form-group">
                <label>
                  {isLanguageInst ? "Assessment / Exam Type" : "Test Type"}{" "}
                  <span className="optional-text">(Optional)</span>
                </label>
                <Select
                  options={testTypeOptions}
                  value={newTest.testType}
                  onChange={(selected) =>
                    setNewTest((prev) => ({ ...prev, testType: selected }))
                  }
                  placeholder={
                    isLanguageInst
                      ? "Select Exam / Quiz"
                      : "Select Test Type"
                  }
                  isClearable
                  isDisabled={isSubmitting}
                />
              </div>

              {/* Language Quiz Configuration (Skills & Question Formats) */}
              {isLanguageInst && newTest.testType?.value === "Quiz" && (
                <div
                  style={{
                    padding: "1rem 1.25rem",
                    borderRadius: "12px",
                    background: "#fdf4ff",
                    border: "1.5px solid #f5d0fe",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "10px",
                    }}
                  >
                    <i
                      className="fa-solid fa-wand-magic-sparkles"
                      style={{ color: "#a21caf", fontSize: "1rem" }}
                    ></i>
                    <strong style={{ fontSize: "0.92rem", color: "#86198f" }}>
                      Language Quiz Skill & Format Combinations
                    </strong>
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#701a75",
                        display: "block",
                        marginBottom: "6px",
                      }}
                    >
                      Skill Focus
                    </label>
                    <div className="lang-chips-container">
                      {[
                        "Grammar",
                        "Vocabulary",
                        "Reading Comprehension",
                        "Writing",
                        "Listening",
                        "Mixed Skills",
                      ].map((skill) => {
                        const isSelected = selectedQuizSkills.includes(skill);
                        return (
                          <button
                            type="button"
                            key={skill}
                            className={`lang-chip ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              setSelectedQuizSkills((prev) =>
                                isSelected
                                  ? prev.filter((s) => s !== skill)
                                  : [...prev, skill]
                              );
                            }}
                            style={{ fontSize: "0.78rem", padding: "4px 10px" }}
                          >
                            <i
                              className={`fa-solid ${isSelected ? "fa-check" : "fa-plus"
                                }`}
                              style={{ fontSize: "0.7rem" }}
                            ></i>
                            {skill}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#701a75",
                        display: "block",
                        marginBottom: "6px",
                      }}
                    >
                      Question Formats
                    </label>
                    <div className="lang-chips-container">
                      {[
                        "Multiple Choice (MCQ)",
                        "Fill in the Blanks",
                        "Sentence Transformation",
                        "Matching",
                        "Short Translation",
                      ].map((fmt) => {
                        const isSelected = selectedQuizFormats.includes(fmt);
                        return (
                          <button
                            type="button"
                            key={fmt}
                            className={`lang-chip ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              setSelectedQuizFormats((prev) =>
                                isSelected
                                  ? prev.filter((f) => f !== fmt)
                                  : [...prev, fmt]
                              );
                            }}
                            style={{ fontSize: "0.78rem", padding: "4px 10px" }}
                          >
                            <i
                              className={`fa-solid ${isSelected ? "fa-check" : "fa-plus"
                                }`}
                              style={{ fontSize: "0.7rem" }}
                            ></i>
                            {fmt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Detect Mismatched Papers on Upload (Beta) */}
              {(newTest.testPaperURL || newTest.capturedFile) &&
                !newTest.isManualEntryQuestions &&
                !newTest.isOnlineExamination && (
                  <div className="form-group">
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>Detect Mismatched Papers on Upload</span>
                      <span className="beta-badge" style={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        padding: "2px 7px",
                        borderRadius: "6px",
                        background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                        color: "#ffffff",
                        lineHeight: 1.2,
                        display: "inline-flex",
                        alignItems: "center",
                        boxShadow: "0 1px 3px rgba(234, 88, 12, 0.25)",
                      }}>
                        Beta
                      </span>
                    </label>
                    <label className="solution-toggle">
                      <input
                        type="checkbox"
                        checked={!!newTest.verifyAnswerPaperOnUpload}
                        onChange={(e) => {
                          setNewTest((prev) => ({
                            ...prev,
                            verifyAnswerPaperOnUpload: e.target.checked,
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                      <span className="toggle-label-text">
                        {newTest.verifyAnswerPaperOnUpload
                          ? "Enabled"
                          : "Disabled"}
                      </span>
                    </label>
                    <small
                      className="helper-text"
                      style={{
                        color: "#666",
                        marginTop: "0.5rem",
                        display: "block",
                      }}
                    >
                      Checks if uploaded sheets match this exam paper before processing, preventing wrong uploads.
                    </small>
                  </div>
                )}

              {/* Certificate Template */}
              {certificateTemplateOptions &&
                certificateTemplateOptions.length > 0 && (
                  <div className="form-group">
                    <label>
                      <i
                        className="fa-solid fa-award"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Certificate Template{" "}
                      <span className="optional-text">(Optional)</span>
                    </label>
                    <Select
                      options={certificateTemplateOptions}
                      value={
                        certificateTemplateOptions.find(
                          (opt) => opt.value === newTest.certificateTemplateId,
                        ) || null
                      }
                      onChange={(selected) =>
                        setNewTest((prev) => ({
                          ...prev,
                          certificateTemplateId: selected
                            ? selected.value
                            : null,
                        }))
                      }
                      placeholder="Select Certificate Template"
                      isClearable
                      isDisabled={isSubmitting}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "8px",
                          borderColor: "#cbd5e1",
                          minHeight: "40px",
                        }),
                      }}
                    />
                    <small className="helper-text" style={{ color: "#666" }}>
                      Link a certificate template to automatically allow
                      issuance of verified certificates for this test.
                    </small>
                  </div>
                )}

              {/* Annotation Printing Offset */}
              {isAnnotationMode &&
                annotationPrintingOffsetOptions &&
                annotationPrintingOffsetOptions.length > 0 && (
                  <div className="form-group">
                    <label>
                      <i
                        className="fa-solid fa-arrows-up-down-left-right"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Annotation Printing Offset{" "}
                      <span className="optional-text">(Optional)</span>
                    </label>
                    <Select
                      options={annotationPrintingOffsetOptions}
                      value={
                        annotationPrintingOffsetOptions.find(
                          (opt) =>
                            opt.value ===
                            newTest.annotationPrintingOffsetConfigId,
                        ) || null
                      }
                      onChange={(selected) =>
                        setNewTest((prev) => ({
                          ...prev,
                          annotationPrintingOffsetConfigId: selected
                            ? selected.value
                            : null,
                        }))
                      }
                      placeholder="Select Annotation Printing Offset"
                      isClearable
                      isDisabled={isSubmitting}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "8px",
                          borderColor: "#cbd5e1",
                          minHeight: "40px",
                        }),
                      }}
                    />
                    <small className="helper-text" style={{ color: "#666" }}>
                      Select predefined margin offsets for blank paper
                      annotation printing.
                    </small>
                  </div>
                )}

              {/* External Test */}
              {testHasProgram && (
                <div className="form-group">
                  <label>External Test</label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="isExternal"
                      checked={!!newTest.isExternal}
                      onChange={handleCreateInputChange}
                      disabled={isSubmitting}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {newTest.isExternal ? "External" : "Internal"}
                    </span>
                  </label>
                  <small className="helper-text" style={{ color: "#666" }}>
                    Toggle to mark this test as{" "}
                    {!newTest.isExternal ? "external" : "internal"}.
                  </small>
                </div>
              )}
              {formError && (
                <div
                  className="form-error"
                  style={{
                    color: "red",
                    fontSize: "0.9rem",
                  }}
                >
                  {formError}
                </div>
              )}

              {/* Submit Button */}
              {!isSubmitting ? (
                <button type="submit" className="btn-submit">
                  <i className="fa-solid fa-plus"></i> Create Test
                </button>
              ) : userPromptProgress ? (
                <div
                  ref={progressIndicatorRef}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    marginTop: "2rem",
                    padding: "2rem",
                    borderRadius: "12px",
                    background:
                      userPromptPhase === "analyzing"
                        ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                        : userPromptPhase === "generating"
                          ? "linear-gradient(135deg, #ffe8c8ff 0%, #ffe9caff 100%)"
                          : userPromptPhase === "completed"
                            ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                            : "#f8f9fa",
                    border: `2px solid ${userPromptPhase === "analyzing"
                        ? "#2196f3"
                        : userPromptPhase === "generating"
                          ? "#f09a21"
                          : userPromptPhase === "completed"
                            ? "#4caf50"
                            : "#e0e0e0"
                      }`,
                    transition: "all 0.5s ease-in-out",
                    boxShadow:
                      userPromptPhase === "analyzing"
                        ? "0 8px 32px rgba(33, 150, 243, 0.3)"
                        : userPromptPhase === "generating"
                          ? "0 8px 32px rgba(240, 154, 33, 0.3)"
                          : userPromptPhase === "completed"
                            ? "0 8px 32px rgba(76, 175, 80, 0.3)"
                            : "0 4px 16px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Loader
                    fontSize="50px"
                    style={{
                      left: "0",
                      color:
                        userPromptPhase === "analyzing"
                          ? "#1976d2"
                          : userPromptPhase === "generating"
                            ? "#f09a21"
                            : userPromptPhase === "completed"
                              ? "#388e3c"
                              : "#666",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color:
                        userPromptPhase === "analyzing"
                          ? "#1565c0"
                          : userPromptPhase === "generating"
                            ? "#f09a21"
                            : userPromptPhase === "completed"
                              ? "#2e7d32"
                              : "#1e40af",
                      textAlign: "center",
                      animation: "pulse 2s infinite",
                      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    {userPromptProgress}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color:
                        userPromptPhase === "analyzing"
                          ? "#1976d2"
                          : userPromptPhase === "generating"
                            ? "#f09a21"
                            : userPromptPhase === "completed"
                              ? "#388e3c"
                              : "#6b7280",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    {userPromptProgress.includes("PDF")
                      ? "Please wait while we format and render your question paper..."
                      : userPromptProgress.includes("Analyzing")
                        ? "Please wait while we analyze your test paper..."
                        : userPromptProgress.includes("Understanding")
                          ? "Identifying key patterns and requirements..."
                          : userPromptProgress.includes("Generating")
                            ? "Creating personalized grading instructions..."
                            : userPromptProgress.includes("successfully")
                              ? "Almost there! Preparing verification..."
                              : "Processing..."}
                  </div>
                </div>
              ) : (
                <Loader fontSize="50px" />
              )}
            </form>
          </Modal>

          {/* Edit Test Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            maxWidth={
              editTest.isManualEntryQuestions || editTest.isOnlineExamination
                ? "100%"
                : undefined
            }
          >
            <div style={{ position: "relative" }}>
              <h2>Edit Test</h2>
              <p className="modal-description" style={{ marginBottom: "0.5rem" }}>
                Update the details below to modify your test.
              </p>
            </div>

            <form onSubmit={handleEditTest} className="test-form">
              {/* Extraction Status Banner */}
              {(() => {
                const qbObj =
                  typeof editTest.questionBankId === "object" &&
                    editTest.questionBankId !== null
                    ? editTest.questionBankId
                    : null;
                const qbStatus = qbObj?.status;
                const testStatus = editTest.status;
                if (testStatus === "Review" && (qbStatus === "Requested" || qbStatus === "Processing")) {
                  return (
                    <div
                      style={{
                        padding: "0.75rem 1rem",
                        backgroundColor: "#fef3c7",
                        border: "1px solid #fde68a",
                        borderRadius: "8px",
                        color: "#92400e",
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "0.9rem",
                      }}
                    >
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      <span>
                        Questions are being extracted from the test paper in the background. They will appear here once processing completes.
                      </span>
                    </div>
                  );
                }
                if (qbStatus === "Failed") {
                  return (
                    <div
                      style={{
                        padding: "0.75rem 1rem",
                        backgroundColor: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "8px",
                        color: "#991b1b",
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "0.9rem",
                      }}
                    >
                      <i className="fa-solid fa-triangle-exclamation"></i>
                      <span>
                        PDF Question extraction failed. You can click &quot;From Test Paper&quot; below to try again.
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Test Name */}
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Test
                  Name
                </label>
                <input
                  id="editTestNameInput"
                  type="text"
                  name="testName"
                  value={editTest.testName}
                  onChange={handleEditInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder="Edit test name"
                />
              </div>


              {/* Advanced Online Exam Toggle - only when online exam + institute allows */}
              {editTest.isOnlineExamination &&
                instituteData?.isAdvancedOnlineExamAllowed && (
                  <div
                    style={{
                      padding: "1rem 1.25rem",
                      border: "1px solid #e0e7ff",
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
                      marginBottom: "1rem",
                    }}
                  >
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>
                        <i
                          className="fa-solid fa-shield-halved"
                          style={{
                            marginRight: "0.4rem",
                            color: "var(--color-1)",
                          }}
                        />
                        Advanced Online Exam
                      </label>
                      <label className="solution-toggle">
                        <input
                          type="checkbox"
                          name="isAdvancedOnlineExam"
                          checked={!!editTest.isAdvancedOnlineExam}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setEditTest((prev) => ({
                              ...prev,
                              isAdvancedOnlineExam: checked,
                              advancedOnlineExamOptions: {
                                videoProctoring: checked
                                  ? prev.advancedOnlineExamOptions?.videoProctoring || false
                                  : false,
                                navigationBackToQuestion: checked
                                  ? prev.advancedOnlineExamOptions?.navigationBackToQuestion !== false
                                  : true,
                                shuffleQuestions: checked
                                  ? prev.advancedOnlineExamOptions?.shuffleQuestions || false
                                  : false,
                                shuffleOptions: checked
                                  ? prev.advancedOnlineExamOptions?.shuffleOptions || false
                                  : false,
                              },
                            }));
                            setIsEditChanged(true);
                          }}
                          disabled={isSubmitting}
                        />
                        <span className="toggle-track">
                          <span className="toggle-indicator"></span>
                        </span>
                        <span className="toggle-label-text">
                          {editTest.isAdvancedOnlineExam
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </label>
                      <small
                        className="helper-text"
                        style={{ color: "#666" }}
                      >
                        <i
                          className="fa-solid fa-circle-info"
                          style={{ marginRight: "0.3rem" }}
                        />
                        Enable advanced features like video proctoring,
                        navigation control, shuffling, and per-question duration
                        tracking.
                      </small>
                    </div>

                    {/* Sub-toggles */}
                    {editTest.isAdvancedOnlineExam && (
                      <div
                        style={{
                          display: "flex",
                          gap: "1.5rem",
                          flexWrap: "wrap",
                          marginTop: "1rem",
                          paddingTop: "0.75rem",
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        {/* Video Proctoring */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-video"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Video Proctoring
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!editTest.advancedOnlineExamOptions?.videoProctoring}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    videoProctoring: checked,
                                  },
                                }));
                                setIsEditChanged(true);
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {editTest.advancedOnlineExamOptions?.videoProctoring
                                ? "Enabled"
                                : "Disabled"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {editTest.advancedOnlineExamOptions?.videoProctoring
                              ? "Students will be monitored via webcam during the exam."
                              : "No video monitoring during the exam."}
                          </small>
                        </div>

                        {/* Navigation Back to Question */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-arrow-left"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Navigation Back to Question
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={editTest.advancedOnlineExamOptions?.navigationBackToQuestion !== false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    navigationBackToQuestion: checked,
                                  },
                                }));
                                setIsEditChanged(true);
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {editTest.advancedOnlineExamOptions?.navigationBackToQuestion !== false
                                ? "Allowed"
                                : "Not Allowed"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {editTest.advancedOnlineExamOptions?.navigationBackToQuestion !== false
                              ? "Students can go back to previous questions."
                              : "Students cannot navigate back to previous questions."}
                          </small>
                        </div>

                        {/* Shuffle Questions */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-shuffle"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Shuffle Questions
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!editTest.advancedOnlineExamOptions?.shuffleQuestions}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    shuffleQuestions: checked,
                                  },
                                }));
                                setIsEditChanged(true);
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {editTest.advancedOnlineExamOptions?.shuffleQuestions
                                ? "Enabled"
                                : "Disabled"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {editTest.advancedOnlineExamOptions?.shuffleQuestions
                              ? "Questions will be randomly shuffled."
                              : "Questions appear in fixed sequence."}
                          </small>
                        </div>

                        {/* Shuffle Options */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-solid fa-random"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Shuffle Options
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!editTest.advancedOnlineExamOptions?.shuffleOptions}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditTest((prev) => ({
                                  ...prev,
                                  advancedOnlineExamOptions: {
                                    ...prev.advancedOnlineExamOptions,
                                    shuffleOptions: checked,
                                  },
                                }));
                                setIsEditChanged(true);
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {editTest.advancedOnlineExamOptions?.shuffleOptions
                                ? "Enabled"
                                : "Disabled"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {editTest.advancedOnlineExamOptions?.shuffleOptions
                              ? "Option order will be randomized for each student."
                              : "Options appear in standard order."}
                          </small>
                        </div>

                        {/* Allow Duration Per Question */}
                        <div className="form-group" style={{ marginBottom: 0, flex: "1 1 calc(50% - 0.75rem)", minWidth: "220px" }}>
                          <label>
                            <i
                              className="fa-regular fa-clock"
                              style={{
                                marginRight: "0.4rem",
                                color: "var(--color-1)",
                              }}
                            />
                            Allow Duration Per Question
                          </label>
                          <label className="solution-toggle">
                            <input
                              type="checkbox"
                              checked={!!editTest.advancedOnlineExamOptions?.isTimedExam}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditTest((prev) => {
                                  let dur = prev.duration;
                                  if (checked && prev.onlineExamQuestions?.length > 0) {
                                    dur = String(
                                      roundTo2(
                                        prev.onlineExamQuestions.reduce(
                                          (sum, q) => sum + (parseFloat(q.duration) || 0),
                                          0,
                                        ),
                                      ),
                                    );
                                  }
                                  return {
                                    ...prev,
                                    duration: dur,
                                    advancedOnlineExamOptions: {
                                      ...prev.advancedOnlineExamOptions,
                                      isTimedExam: checked,
                                    },
                                  };
                                });
                                setIsEditChanged(true);
                              }}
                              disabled={isSubmitting}
                            />
                            <span className="toggle-track">
                              <span className="toggle-indicator"></span>
                            </span>
                            <span className="toggle-label-text">
                              {editTest.advancedOnlineExamOptions?.isTimedExam
                                ? "Yes"
                                : "No"}
                            </span>
                          </label>
                          <small className="helper-text" style={{ color: "#666" }}>
                            {editTest.advancedOnlineExamOptions?.isTimedExam
                              ? "Exam has a timer per question."
                              : "Exam has an overall timer."}
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {editTest.isOnlineExamination && (
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginTop: "1.25rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  {/* Start Date & Time */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-solid fa-calendar-day"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Start Date & Time{" "}
                      <span
                        style={{
                          fontWeight: "normal",
                          color: "gray",
                          fontSize: "0.85em",
                        }}
                      >
                        (Optional)
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      name="scheduledDate"
                      value={editTest.scheduledDate}
                      onChange={handleEditInputChange}
                      step="900"
                      disabled={isSubmitting}
                    />
                    <small className="helper-text">
                      Exam opens for students at this time.
                    </small>
                  </div>

                  {/* End Date & Time */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-solid fa-calendar-xmark"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-2)",
                        }}
                      />
                      End Date & Time{" "}
                      <span
                        style={{
                          fontWeight: "normal",
                          color: "gray",
                          fontSize: "0.85em",
                        }}
                      >
                        (Optional)
                      </span>
                    </label>
                    <input
                      type="datetime-local"
                      name="dueDate"
                      value={editTest.dueDate}
                      onChange={handleEditInputChange}
                      step="900"
                      disabled={isSubmitting}
                    />
                    <small className="helper-text">
                      Exam auto-submits at this time.
                    </small>
                  </div>

                  {/* Duration */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-regular fa-clock"
                        style={{
                          marginRight: "0.5rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Duration (minutes){" "}
                      {!editTest.isAdvancedOnlineExam && (
                        <span
                          style={{
                            fontWeight: "normal",
                            color: "gray",
                            fontSize: "0.85em",
                          }}
                        >
                          (Optional)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="duration"
                      value={
                        (editTest.isAdvancedOnlineExam && editTest.advancedOnlineExamOptions?.isTimedExam)
                          ? (editTest.duration !== "" && editTest.duration != null ? roundTo2(editTest.duration) : "")
                          : editTest.duration
                      }
                      onChange={handleEditInputChange}
                      min="1"
                      disabled={isSubmitting || (editTest.isAdvancedOnlineExam && editTest.advancedOnlineExamOptions?.isTimedExam)}
                      placeholder="e.g. 60"
                      style={{
                        padding: "0.75rem 1rem",
                        border: "2px solid #eee",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        backgroundColor: (editTest.isAdvancedOnlineExam && editTest.advancedOnlineExamOptions?.isTimedExam)
                          ? "#f5f5f5"
                          : "white",
                        cursor: (editTest.isAdvancedOnlineExam && editTest.advancedOnlineExamOptions?.isTimedExam)
                          ? "not-allowed"
                          : "text",
                      }}
                    />
                    <small className="helper-text">
                      {(editTest.isAdvancedOnlineExam && editTest.advancedOnlineExamOptions?.isTimedExam)
                        ? "Duration is auto-calculated based on per-question durations."
                        : "Time limit for the exam."}
                    </small>
                  </div>

                  {/* 4-Digit PIN */}
                  <div
                    className="form-group"
                    style={{ flex: "1 1 200px", margin: 0 }}
                  >
                    <label>
                      <i
                        className="fa-solid fa-lock"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Change Exam PIN{" "}
                      <span className="optional-text">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="onlineExamPasscode"
                      value={editTest.onlineExamPasscode}
                      onChange={(e) => {
                        const val = e.target.value
                          .replace(/[^0-9]/g, "")
                          .slice(0, 4);
                        setEditTest((prev) => ({
                          ...prev,
                          onlineExamPasscode: val,
                        }));
                      }}
                      pattern="\d{4}"
                      disabled={isSubmitting}
                      style={{
                        letterSpacing: "0.2rem",
                      }}
                    />
                    <small className="helper-text">
                      <i
                        className="fa-solid fa-circle-info"
                        style={{ marginRight: "0.3rem" }}
                      />
                      Leave blank to keep the existing PIN. Students must enter
                      this to start the exam.
                    </small>
                  </div>
                </div>
              )}

              {/* Max Score - Hidden for Online Exam (auto-calculated) */}
              {!editTest.isOnlineExamination && (
                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i> Max
                    Score
                  </label>
                  <input
                    type="number"
                    name="maxScore"
                    value={editTest.maxScore}
                    onChange={handleEditInputChange}
                    required
                    min="0"
                    step="1"
                    disabled={isSubmitting}
                    placeholder="Edit maximum score"
                  />
                  <small className="helper-text">
                    Enter the total marks for this test (whole numbers only)
                  </small>
                </div>
              )}

              {/* Test Paper - Hide when Manual Entry or Online Exam is selected */}
              {!editTest.isManualEntryQuestions &&
                !editTest.isOnlineExamination && (
                  <div>{renderTestPaperUpload(true)}</div>
                )}

              {/* Generate AI Grading Instructions */}
              {(editTest.testPaperURL ||
                (editTest.original &&
                  editTest.original.testPaperFileName &&
                  !editTest.removeTestPaper)) &&
                !editTest.isManualEntryQuestions &&
                !editTest.isOnlineExamination && (
                  <div className="form-group">
                    <label>Generate AI Grading Instructions</label>
                    <label className="solution-toggle">
                      <input
                        type="checkbox"
                        checked={generateAIPrompt}
                        onChange={(e) => {
                          setGenerateAIPrompt(e.target.checked);
                          setIsEditChanged(true);
                        }}
                        disabled={isSubmitting}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                      <span className="toggle-label-text">
                        {generateAIPrompt ? "Enabled" : "Disabled"}
                      </span>
                    </label>
                    <small
                      className="helper-text"
                      style={{
                        color: "#666",
                        marginTop: "0.5rem",
                        display: "block",
                      }}
                    >
                      We will analyze your test paper and generate custom
                      instructions to ensure accurate and consistent AI grading.
                    </small>
                  </div>
                )}

              {/* Conditional Manual Entry Table */}
              {editTest.isManualEntryQuestions && !isK12School && (
                <div className="table-container">
                  <h3
                    style={{
                      marginBottom: "1rem",
                      marginTop: "1rem",
                      color: "#333",
                    }}
                  >
                    Manual Entry Questions
                  </h3>
                  {renderManualEntryQuestionsTable(true)}
                </div>
              )}

              {/* Online Exam Builder */}
              {editTest.isOnlineExamination && renderOnlineExamBuilder(true)}

              {/* Answer Key */}
              {!editTest.isOnlineExamination &&
                !editTest.isManualEntryQuestions && (
                  <div>{renderAnswerKeyUpload(true)}</div>
                )}
              {/* Rubric */}
              {!editTest.isOnlineExamination &&
                !editTest.isManualEntryQuestions && (
                  <div>{renderRubricSection(true)}</div>
                )}

              {/* Grading Policy */}
              {!editTest.isOnlineExamination &&
                !editTest.isManualEntryQuestions && (
                  <div>{renderGradingPolicySection(true)}</div>
                )}

              {/* OMR Template & Grading Template Selection */}
              {editTest.isOnlineExamination && (() => {
                const selectedOmr = filteredOmrTemplateOptions.find(
                  (opt) => opt.value === editTest.omrTemplateId,
                );

                return (
                  <div
                    style={{
                      marginBottom: "1rem",
                    }}
                  >
                    {/* If institute has OMR templates, show Exam Template dropdown ONLY */}
                    {filteredOmrTemplateOptions.length > 0 ? (
                      <div
                        style={{
                          padding: "1.25rem",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <h3
                          style={{
                            marginBottom: "0.75rem",
                            color: "#334155",
                            fontSize: "1.05rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <i
                            className="fa-solid fa-sheet-plastic"
                            style={{ color: "var(--primary, #4f46e5)" }}
                          ></i>
                          Exam Template
                          <span
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: "normal",
                              color: "gray",
                              marginLeft: "0.25rem",
                            }}
                          >
                            (Optional)
                          </span>
                        </h3>

                        {selectedOmr && (
                          <div
                            style={{
                              marginBottom: "12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0.6rem 0.85rem",
                              background:
                                "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
                              borderRadius: "8px",
                              border: "1px solid #c7d2fe",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                fontSize: "0.85rem",
                                color: "#4338ca",
                                fontWeight: "500",
                              }}
                            >
                              <i
                                className="fa-solid fa-circle-check"
                                style={{ color: "#4f46e5" }}
                              ></i>
                              Supports OMR Sheet Scanning
                            </span>
                            {selectedOmr.templateURL && (
                              <a
                                href={selectedOmr.templateURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                  color: "var(--primary, #4f46e5)",
                                  textDecoration: "none",
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                  padding: "0.3rem 0.65rem",
                                  borderRadius: "6px",
                                  backgroundColor: "white",
                                  border: "1px solid #c7d2fe",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#eef2ff";
                                  e.currentTarget.style.borderColor = "#818cf8";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "white";
                                  e.currentTarget.style.borderColor = "#c7d2fe";
                                }}
                              >
                                <i
                                  className="fa-solid fa-eye"
                                  style={{ fontSize: "0.8rem" }}
                                ></i>
                                View OMR
                              </a>
                            )}
                          </div>
                        )}

                        <Select
                          options={filteredOmrTemplateOptions}
                          value={selectedOmr || null}
                          onChange={(selected) => {
                            const linkedStdTestId =
                              selected?.standardizedTestId || null;
                            const newStdId = selected ? linkedStdTestId : null;
                            const newOmrId = selected ? selected.value : null;
                            setEditTest((prev) => ({
                              ...prev,
                              omrTemplateId: newOmrId,
                              standardizedTestId: newStdId,
                            }));
                            if (initialEditTest) {
                              const isOmrChanged =
                                newOmrId !== initialEditTest.omrTemplateId;
                              const isStdChanged =
                                newStdId !== initialEditTest.standardizedTestId;
                              setIsEditChanged(isOmrChanged || isStdChanged);
                            } else {
                              setIsEditChanged(true);
                            }
                          }}
                          isClearable
                          isDisabled={isSubmitting}
                          placeholder="Choose an exam template..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: "10px",
                              borderColor: "#cbd5e1",
                              minHeight: "44px",
                              boxShadow: "none",
                              "&:hover": { borderColor: "var(--primary, #4f46e5)" },
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? "var(--primary, #4f46e5)"
                                : state.isFocused
                                  ? "var(--primary-100, #eef2ff)"
                                  : "white",
                              color: state.isSelected ? "white" : "#334155",
                            }),
                          }}
                        />
                        <small
                          className="helper-text"
                          style={{ marginTop: "0.5rem", display: "block" }}
                        >
                          Select a pre-configured exam template. This template supports
                          OMR sheet scanning for automatic grading.
                        </small>
                      </div>
                    ) : (
                      /* Grading Template Selection - shown if no OMR templates available */
                      <div
                        style={{
                          padding: "1.25rem",
                          border: "1px solid #e2e8f0",
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <h3
                          style={{
                            marginBottom: "0.75rem",
                            color: "#334155",
                            fontSize: "1.05rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <i
                            className="fa-solid fa-graduation-cap"
                            style={{ color: "var(--primary, #4f46e5)" }}
                          ></i>
                          Grading Template
                          <span
                            style={{
                              fontSize: "0.7rem",
                              fontWeight: "normal",
                              color: "gray",
                              marginLeft: "0.25rem",
                            }}
                          >
                            (Optional)
                          </span>
                        </h3>

                        <Select
                          options={filteredStandardizedTestOptions}
                          value={
                            filteredStandardizedTestOptions.find(
                              (opt) => editTest.standardizedTestId === opt.value,
                            ) || null
                          }
                          onChange={(selected) => {
                            const newStdId = selected ? selected.value : null;
                            setEditTest((prev) => ({
                              ...prev,
                              standardizedTestId: newStdId,
                            }));
                            if (initialEditTest) {
                              const isChanged =
                                newStdId !==
                                initialEditTest.standardizedTestId;
                              setIsEditChanged(isChanged);
                            } else {
                              setIsEditChanged(true);
                            }
                          }}
                          isClearable
                          isDisabled={isSubmitting}
                          placeholder="Choose a grading template..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: "10px",
                              borderColor: "#cbd5e1",
                              minHeight: "44px",
                              boxShadow: "none",
                              "&:hover": { borderColor: "var(--primary, #4f46e5)" },
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? "var(--primary, #4f46e5)"
                                : state.isFocused
                                  ? "var(--primary-100, #eef2ff)"
                                  : "white",
                              color: state.isSelected ? "white" : "#334155",
                            }),
                          }}
                        />
                        <small
                          className="helper-text"
                          style={{ marginTop: "0.5rem", display: "block" }}
                        >
                          Select a grading template if this test follows standard
                          parameters.
                        </small>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Test Type */}
              <div className="form-group">
                <label>
                  Test Type <span className="optional-text">(Optional)</span>
                </label>
                <Select
                  options={testTypeOptions}
                  value={editTest.testType}
                  onChange={(selected) => {
                    setEditTest((prev) => ({
                      ...prev,
                      testType: selected,
                    }));
                    if (initialEditTest) {
                      const isChanged =
                        selected?.value !== initialEditTest.testType;
                      setIsEditChanged(isChanged);
                    } else {
                      setIsEditChanged(true);
                    }
                  }}
                  placeholder="Select Test Type"
                  isClearable
                  isDisabled={isSubmitting}
                />
              </div>

              {/* Detect Mismatched Papers on Upload (Beta) */}
              {(editTest.testPaperURL ||
                editTest.capturedFile ||
                (editTest.original &&
                  editTest.original.testPaperFileName &&
                  !editTest.removeTestPaper)) &&
                !editTest.isManualEntryQuestions &&
                !editTest.isOnlineExamination && (
                  <div className="form-group">
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>Detect Mismatched Papers on Upload</span>
                      <span className="beta-badge" style={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        padding: "2px 7px",
                        borderRadius: "6px",
                        background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                        color: "#ffffff",
                        lineHeight: 1.2,
                        display: "inline-flex",
                        alignItems: "center",
                        boxShadow: "0 1px 3px rgba(234, 88, 12, 0.25)",
                      }}>
                        Beta
                      </span>
                    </label>
                    <label className="solution-toggle">
                      <input
                        type="checkbox"
                        checked={!!editTest.verifyAnswerPaperOnUpload}
                        onChange={(e) => {
                          setEditTest((prev) => ({
                            ...prev,
                            verifyAnswerPaperOnUpload: e.target.checked,
                          }));
                          setIsEditChanged(true);
                        }}
                        disabled={isSubmitting}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                      <span className="toggle-label-text">
                        {editTest.verifyAnswerPaperOnUpload
                          ? "Enabled"
                          : "Disabled"}
                      </span>
                    </label>
                    <small
                      className="helper-text"
                      style={{
                        color: "#666",
                        marginTop: "0.5rem",
                        display: "block",
                      }}
                    >
                      Checks if uploaded sheets match this exam paper before processing, preventing wrong uploads.
                    </small>
                  </div>
                )}

              {/* Certificate Template */}
              {certificateTemplateOptions &&
                certificateTemplateOptions.length > 0 && (
                  <div className="form-group">
                    <label>
                      <i
                        className="fa-solid fa-award"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Certificate Template{" "}
                      <span className="optional-text">(Optional)</span>
                    </label>
                    <Select
                      options={certificateTemplateOptions}
                      value={
                        certificateTemplateOptions.find(
                          (opt) => opt.value === editTest.certificateTemplateId,
                        ) || null
                      }
                      onChange={(selected) => {
                        const val = selected ? selected.value : null;
                        setEditTest((prev) => ({
                          ...prev,
                          certificateTemplateId: val,
                        }));
                        if (initialEditTest) {
                          const isChanged =
                            val !== initialEditTest.certificateTemplateId;
                          setIsEditChanged(isChanged);
                        } else {
                          setIsEditChanged(true);
                        }
                      }}
                      placeholder="Select Certificate Template"
                      isClearable
                      isDisabled={isSubmitting}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "8px",
                          borderColor: "#cbd5e1",
                          minHeight: "40px",
                        }),
                      }}
                    />
                    <small className="helper-text" style={{ color: "#666" }}>
                      Link a certificate template to automatically allow
                      issuance of verified certificates for this test.
                    </small>
                  </div>
                )}

              {/* Annotation Printing Offset */}
              {isAnnotationMode &&
                annotationPrintingOffsetOptions &&
                annotationPrintingOffsetOptions.length > 0 && (
                  <div className="form-group">
                    <label>
                      Annotation Printing Offset{" "}
                      <span className="optional-text">(Optional)</span>
                    </label>
                    <Select
                      options={annotationPrintingOffsetOptions}
                      value={
                        annotationPrintingOffsetOptions.find(
                          (opt) =>
                            opt.value ===
                            editTest.annotationPrintingOffsetConfigId,
                        ) || null
                      }
                      onChange={(selected) => {
                        const val = selected ? selected.value : null;
                        setEditTest((prev) => ({
                          ...prev,
                          annotationPrintingOffsetConfigId: val,
                        }));
                        if (initialEditTest) {
                          setIsEditChanged(
                            val !==
                            initialEditTest.annotationPrintingOffsetConfigId,
                          );
                        } else {
                          setIsEditChanged(true);
                        }
                      }}
                      placeholder="Select Annotation Printing Offset"
                      isClearable
                      isDisabled={isSubmitting}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: "8px",
                          borderColor: "#cbd5e1",
                          minHeight: "40px",
                        }),
                      }}
                    />
                    <small className="helper-text" style={{ color: "#666" }}>
                      Select predefined margin offsets for blank paper
                      annotation printing.
                    </small>
                  </div>
                )}

              {/* External Test */}
              {testHasProgram && (
                <div className="form-group">
                  <label>External Test</label>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="isExternal"
                      checked={!!editTest.isExternal}
                      onChange={handleEditInputChange}
                      disabled={isSubmitting}
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    <span className="toggle-label-text">
                      {editTest.isExternal ? "External" : "Internal"}
                    </span>
                  </label>
                  <small className="helper-text" style={{ color: "#666" }}>
                    Toggle to mark this test as{" "}
                    {!editTest.isExternal ? "external" : "internal"}.
                  </small>
                </div>
              )}

              {editTest.isOnlineExamination && (
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginTop: "1.25rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  {/* Calculator Allowed */}
                  <div className="form-group">
                    <label>
                      <i
                        className="fa-solid fa-calculator"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Calculator Allowed
                    </label>
                    <label className="solution-toggle">
                      <input
                        type="checkbox"
                        name="isCalculatorAllowed"
                        checked={!!editTest.isCalculatorAllowed}
                        onChange={handleEditInputChange}
                        disabled={isSubmitting}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                      <span className="toggle-label-text">
                        {editTest.isCalculatorAllowed
                          ? "Allowed"
                          : "Not Allowed"}
                      </span>
                    </label>
                    <small className="helper-text" style={{ color: "#666" }}>
                      <i
                        className="fa-solid fa-circle-info"
                        style={{ marginRight: "0.3rem" }}
                      />
                      {editTest.isCalculatorAllowed
                        ? "Students will be allowed to use calculator during the exam."
                        : "Calculator will not be accessible to students during the exam."}
                    </small>
                  </div>

                  {/* Test Status */}

                  <div className="form-group">
                    <label>
                      <i
                        className="fa-solid fa-check-circle"
                        style={{
                          marginRight: "0.4rem",
                          color: "var(--color-1)",
                        }}
                      />
                      Mark Ready
                    </label>
                    <label className="solution-toggle">
                      <input
                        type="checkbox"
                        name="testStatus"
                        checked={editTest.status === "Ready"}
                        onChange={(e) => {
                          const newStatus = e.target.checked ? "Ready" : "Review";
                          setEditTest({
                            ...editTest,
                            status: newStatus,
                          });
                          if (initialEditTest) {
                            setIsEditChanged(newStatus !== initialEditTest.status);
                          } else {
                            setIsEditChanged(true);
                          }
                        }}
                        disabled={isSubmitting}
                      />
                      <span className="toggle-track">
                        <span className="toggle-indicator"></span>
                      </span>
                      <span className="toggle-label-text">
                        {editTest.status === "Ready" ? "Ready" : "Review"}
                      </span>
                    </label>
                    <small className="helper-text" style={{ color: "#666" }}>
                      <i
                        className="fa-solid fa-circle-info"
                        style={{ marginRight: "0.3rem" }}
                      />
                      {editTest.status === "Ready"
                        ? "Test is ready and visible to students."
                        : "Test is in review and currently hidden."}
                    </small>
                  </div>


                  {/* Results Available Immediately */}
                  {user?.isPublishResultAllowed && (
                    <div className="form-group">
                      <label>
                        <i
                          className="fa-solid fa-bolt"
                          style={{
                            marginRight: "0.4rem",
                            color: "var(--color-1)",
                          }}
                        />
                        Results Available Immediately
                      </label>
                      <label className="solution-toggle">
                        <input
                          type="checkbox"
                          name="resultsAvailableImmediately"
                          checked={!!editTest.resultsAvailableImmediately}
                          onChange={handleEditInputChange}
                          disabled={isSubmitting}
                        />
                        <span className="toggle-track">
                          <span className="toggle-indicator"></span>
                        </span>
                        <span className="toggle-label-text">
                          {editTest.resultsAvailableImmediately
                            ? "Enabled"
                            : "Disabled"}
                        </span>
                      </label>
                      <small className="helper-text" style={{ color: "#666" }}>
                        <i
                          className="fa-solid fa-circle-info"
                          style={{ marginRight: "0.3rem" }}
                        />
                        {editTest.resultsAvailableImmediately
                          ? "Students will see their scores immediately after submitting the exam."
                          : "Students will only see results after you publish them manually."}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {formError && (
                <div
                  className="form-error"
                  style={{
                    color: "red",
                    fontSize: "0.9rem",
                  }}
                >
                  {formError}
                </div>
              )}

              {/* Submit Button */}
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!isEditChanged}
                  onClick={(e) => {
                    if (!isEditChanged) {
                      e.preventDefault();
                      swalError(
                        "No changes detected. Please make changes to save.",
                      );
                    }
                  }}
                >
                  <i className="fa-solid fa-floppy-disk"></i> Update Test
                </button>
              ) : userPromptProgress ? (
                <div
                  ref={progressIndicatorRef}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    marginTop: "2rem",
                    padding: "2rem",
                    borderRadius: "12px",
                    background:
                      userPromptPhase === "analyzing"
                        ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                        : userPromptPhase === "generating"
                          ? "linear-gradient(135deg, #ffe8c8ff 0%, #ffe9caff 100%)"
                          : userPromptPhase === "completed"
                            ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                            : "#f8f9fa",
                    border: `2px solid ${userPromptPhase === "analyzing"
                        ? "#2196f3"
                        : userPromptPhase === "generating"
                          ? "#f09a21"
                          : userPromptPhase === "completed"
                            ? "#4caf50"
                            : "#e0e0e0"
                      }`,
                    transition: "all 0.5s ease-in-out",
                    boxShadow:
                      userPromptPhase === "analyzing"
                        ? "0 8px 32px rgba(33, 150, 243, 0.3)"
                        : userPromptPhase === "generating"
                          ? "0 8px 32px rgba(240, 154, 33, 0.3)"
                          : userPromptPhase === "completed"
                            ? "0 8px 32px rgba(76, 175, 80, 0.3)"
                            : "0 4px 16px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Loader
                    fontSize="50px"
                    style={{
                      left: "0",
                      color:
                        userPromptPhase === "analyzing"
                          ? "#1976d2"
                          : userPromptPhase === "generating"
                            ? "#f09a21"
                            : userPromptPhase === "completed"
                              ? "#388e3c"
                              : "#666",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color:
                        userPromptPhase === "analyzing"
                          ? "#1565c0"
                          : userPromptPhase === "generating"
                            ? "#f09a21"
                            : userPromptPhase === "completed"
                              ? "#2e7d32"
                              : "#1e40af",
                      textAlign: "center",
                      animation: "pulse 2s infinite",
                      textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    {userPromptProgress}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color:
                        userPromptPhase === "analyzing"
                          ? "#1976d2"
                          : userPromptPhase === "generating"
                            ? "#f09a21"
                            : userPromptPhase === "completed"
                              ? "#388e3c"
                              : "#6b7280",
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    {userPromptProgress.includes("PDF")
                      ? "Please wait while we format and render your question paper..."
                      : userPromptProgress.includes("Analyzing")
                        ? "Please wait while we analyze your test paper..."
                        : userPromptProgress.includes("Understanding")
                          ? "Identifying key patterns and requirements..."
                          : userPromptProgress.includes("Generating")
                            ? "Creating personalized grading instructions..."
                            : userPromptProgress.includes("successfully")
                              ? "Almost there! Preparing verification..."
                              : "Processing..."}
                  </div>
                </div>
              ) : (
                <Loader fontSize="50px" />
              )}
            </form>
          </Modal>

          {/* Create Homework Modal */}
          <Modal
            isOpen={isCreateHomeworkModalOpen}
            onClose={() => {
              if (isSubmitting) return;
              setIsCreateHomeworkModalOpen(false);
              setNewHomework({
                homeworkName: "",
                dueDate: "",
                status: "Ready",
                testPaperURL: null,
                capturedFile: null,
                answerKeyFile: null,
                answerKeyCapturedFile: null,
              });
            }}
          >
            <h2>Add New Homework</h2>
            <p className="modal-description" style={{ marginBottom: "1rem" }}>
              Fill in the details below to create new homework for your class.
            </p>

            <form onSubmit={handleCreateHomework} className="test-form">
              {/* Homework Name */}
              <div className="form-group">
                <label htmlFor="homeworkNameInput">
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Homework Name
                </label>
                <input
                  id="homeworkNameInput"
                  type="text"
                  name="homeworkName"
                  value={newHomework.homeworkName}
                  onChange={(e) =>
                    setNewHomework((prev) => ({
                      ...prev,
                      homeworkName: e.target.value,
                    }))
                  }
                  required
                  disabled={isSubmitting}
                  placeholder="e.g. Chapter 4 Homework"
                />
              </div>

              {/* Homework Paper Upload */}
              <div className="form-group">
                <label>
                  Homework Paper <span className="optional-text">(Optional)</span>
                </label>
                <FileUploadSection
                  currentFileUrl={null}
                  currentFileName={null}
                  isSubmitting={isSubmitting}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onFileChange={(file, isCaptured) => {
                    setNewHomework((prev) => ({
                      ...prev,
                      testPaperURL: file,
                      capturedFile: isCaptured ? file : null,
                    }));
                  }}
                  onCapture={(callback) => {
                    setCaptureFor("create-homework");
                    setIsCapturing(true);
                    window.__fileUploadCallback = callback;
                  }}
                  onRemoveSelectedFile={() => {
                    setNewHomework((prev) => ({
                      ...prev,
                      testPaperURL: null,
                      capturedFile: null,
                    }));
                  }}
                  onPreviewFile={(url) => {
                    window.open(url, "_blank");
                  }}
                  selectedFile={newHomework.testPaperURL}
                  isCaptured={newHomework.capturedFile !== null}
                  browseTitle="Choose Homework File"
                  browseDescription="Select PDF, Doc or image file from device"
                  captureTitle="Capture with Camera"
                  captureDescription="Take photos and convert to PDF"
                />
                <small className="helper-text">
                  Upload an optional homework question paper (pdf, doc, docx, jpg, png only) or capture with camera
                </small>
              </div>

              {/* Answer Key / Model Answer Upload */}
              <div className="form-group">
                <label>
                  Answer Key / Model Answer <span className="optional-text">(Optional)</span>
                </label>
                <FileUploadSection
                  currentFileUrl={null}
                  currentFileName={null}
                  isSubmitting={isSubmitting}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onFileChange={(file, isCaptured) => {
                    setNewHomework((prev) => ({
                      ...prev,
                      answerKeyFile: file,
                      answerKeyCapturedFile: isCaptured ? file : null,
                    }));
                  }}
                  onCapture={(callback) => {
                    setCaptureFor("create-homework-answerkey");
                    setIsCapturing(true);
                    window.__fileUploadCallback = callback;
                  }}
                  onRemoveSelectedFile={() => {
                    setNewHomework((prev) => ({
                      ...prev,
                      answerKeyFile: null,
                      answerKeyCapturedFile: null,
                    }));
                  }}
                  onPreviewFile={(url) => {
                    window.open(url, "_blank");
                  }}
                  selectedFile={newHomework.answerKeyFile}
                  isCaptured={newHomework.answerKeyCapturedFile !== null}
                  browseTitle="Choose Answer Key"
                  browseDescription="Select PDF, Doc, or image file from device"
                  captureTitle="Capture with Camera"
                  captureDescription="Take photos and convert to PDF"
                />
                <small className="helper-text">
                  Upload an optional answer key or model solution to assist AI grading
                </small>
              </div>

              {/* Due Date & Time */}
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="homeworkDueDateInput">
                  <i
                    className="fa-solid fa-calendar-xmark"
                    style={{
                      marginRight: "0.5rem",
                      color: "var(--color-1)",
                    }}
                  ></i>
                  Due Date & Time{" "}
                  <span
                    className="optional-text"
                  >
                    (Optional)
                  </span>
                </label>
                <input
                  id="homeworkDueDateInput"
                  type="datetime-local"
                  name="dueDate"
                  value={newHomework.dueDate}
                  onChange={(e) =>
                    setNewHomework((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  step="900"
                  disabled={isSubmitting}
                />
                <small className="helper-text">
                  Homework submissions close at this time.
                </small>
              </div>

              {/* Homework Status */}
              <div className="form-group">
                <label>
                  <i
                    className="fa-solid fa-check-circle"
                    style={{
                      marginRight: "0.4rem",
                      color: "var(--color-1)",
                    }}
                  />
                  Mark Ready
                </label>
                <label className="solution-toggle">
                  <input
                    type="checkbox"
                    name="homeworkStatus"
                    checked={newHomework.status === "Ready"}
                    onChange={(e) => {
                      const newStatus = e.target.checked ? "Ready" : "Review";
                      setNewHomework((prev) => ({
                        ...prev,
                        status: newStatus,
                      }));
                    }}
                    disabled={isSubmitting}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label-text">
                    {newHomework.status === "Ready" ? "Ready" : "Review"}
                  </span>
                </label>
                <small className="helper-text" style={{ color: "#666" }}>
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ marginRight: "0.3rem" }}
                  />
                  {newHomework.status === "Ready"
                    ? "Homework is ready and visible to students."
                    : "Homework is in review and currently hidden."}
                </small>
              </div>

              {/* Submit Button */}
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  style={{ marginTop: "1.5rem" }}
                >
                  <i className="fa-solid fa-plus"></i> Create Homework
                </button>
              ) : (
                <Loader fontSize="50px" />
              )}
            </form>
          </Modal>

          {/* Edit Homework Modal */}
          <Modal
            isOpen={isEditHomeworkModalOpen}
            onClose={() => {
              if (isSubmitting) return;
              setIsEditHomeworkModalOpen(false);
              setEditHomework({
                id: "",
                homeworkName: "",
                dueDate: "",
                initialDueDate: "",
                status: "Ready",
                initialStatus: "Ready",
                testPaperURL: null,
                capturedFile: null,
                removeTestPaper: false,
                originalTestPaperURL: null,
                originalTestPaperFileName: null,
                answerKeyFile: null,
                answerKeyCapturedFile: null,
                removeAnswerKey: false,
                originalAnswerKeysFileURL: null,
                originalAnswerKeysFileName: null,
              });
            }}
          >
            <h2>Edit Homework</h2>
            <p className="modal-description" style={{ marginBottom: "1rem" }}>
              Update the details below to modify your homework.
            </p>

            <form onSubmit={handleEditHomework} className="test-form">
              {/* Homework Name */}
              <div className="form-group">
                <label htmlFor="editHomeworkNameInput">
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Homework Name
                </label>
                <input
                  id="editHomeworkNameInput"
                  type="text"
                  name="homeworkName"
                  value={editHomework.homeworkName}
                  onChange={(e) =>
                    setEditHomework((prev) => ({
                      ...prev,
                      homeworkName: e.target.value,
                    }))
                  }
                  required
                  disabled={isSubmitting}
                  placeholder="e.g. Chapter 4 Homework"
                />
              </div>

              {/* Homework Paper Upload */}
              <div className="form-group">
                <label>
                  Homework Paper <span className="optional-text">(Optional)</span>
                </label>
                <FileUploadSection
                  currentFileUrl={
                    !editHomework.removeTestPaper && !editHomework.testPaperURL
                      ? editHomework.originalTestPaperURL
                      : null
                  }
                  currentFileName={
                    !editHomework.removeTestPaper && !editHomework.testPaperURL
                      ? editHomework.originalTestPaperFileName
                      : null
                  }
                  selectedFile={editHomework.testPaperURL}
                  isCaptured={editHomework.capturedFile !== null}
                  isSubmitting={isSubmitting}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onFileChange={(file, isCaptured) => {
                    setEditHomework((prev) => ({
                      ...prev,
                      testPaperURL: file,
                      capturedFile: isCaptured ? file : null,
                      removeTestPaper: false,
                    }));
                  }}
                  onCapture={(callback) => {
                    setCaptureFor("edit-homework");
                    setIsCapturing(true);
                    window.__fileUploadCallback = callback;
                  }}
                  onRemoveCurrentFile={() => {
                    setEditHomework((prev) => ({
                      ...prev,
                      removeTestPaper: true,
                      testPaperURL: null,
                      capturedFile: null,
                    }));
                  }}
                  onRemoveSelectedFile={() => {
                    setEditHomework((prev) => ({
                      ...prev,
                      testPaperURL: null,
                      capturedFile: null,
                    }));
                  }}
                  onPreviewFile={(url) => {
                    window.open(url, "_blank");
                  }}
                  browseTitle="Choose Homework File"
                  browseDescription="Select PDF, Doc or image file from device"
                  captureTitle="Capture with Camera"
                  captureDescription="Take photos and convert to PDF"
                />
                <small className="helper-text">
                  Upload an optional homework question paper (pdf, doc, docx, jpg, png only) or capture with camera
                </small>
              </div>

              {/* Answer Key / Model Answer Upload */}
              <div className="form-group">
                <label>
                  Answer Key / Model Answer <span className="optional-text">(Optional)</span>
                </label>
                <FileUploadSection
                  currentFileUrl={
                    !editHomework.removeAnswerKey && !editHomework.answerKeyFile
                      ? editHomework.originalAnswerKeysFileURL
                      : null
                  }
                  currentFileName={
                    !editHomework.removeAnswerKey && !editHomework.answerKeyFile
                      ? editHomework.originalAnswerKeysFileName
                      : null
                  }
                  selectedFile={editHomework.answerKeyFile}
                  isCaptured={editHomework.answerKeyCapturedFile !== null}
                  isSubmitting={isSubmitting}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onFileChange={(file, isCaptured) => {
                    setEditHomework((prev) => ({
                      ...prev,
                      answerKeyFile: file,
                      answerKeyCapturedFile: isCaptured ? file : null,
                      removeAnswerKey: false,
                    }));
                  }}
                  onCapture={(callback) => {
                    setCaptureFor("edit-homework-answerkey");
                    setIsCapturing(true);
                    window.__fileUploadCallback = callback;
                  }}
                  onRemoveCurrentFile={() => {
                    setEditHomework((prev) => ({
                      ...prev,
                      removeAnswerKey: true,
                      answerKeyFile: null,
                      answerKeyCapturedFile: null,
                    }));
                  }}
                  onRemoveSelectedFile={() => {
                    setEditHomework((prev) => ({
                      ...prev,
                      answerKeyFile: null,
                      answerKeyCapturedFile: null,
                    }));
                  }}
                  onPreviewFile={(url) => {
                    window.open(url, "_blank");
                  }}
                  browseTitle="Choose Answer Key"
                  browseDescription="Select PDF, Doc, or image file from device"
                  captureTitle="Capture with Camera"
                  captureDescription="Take photos and convert to PDF"
                />
                <small className="helper-text">
                  Upload an optional answer key or model solution to assist AI grading
                </small>
              </div>

              {/* Due Date & Time */}
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="editHomeworkDueDateInput">
                  <i
                    className="fa-solid fa-calendar-xmark"
                    style={{
                      marginRight: "0.5rem",
                      color: "var(--color-1)",
                    }}
                  ></i>
                  Due Date & Time{" "}
                  <span
                    className="optional-text"
                  >
                    (Optional)
                  </span>
                </label>
                <input
                  id="editHomeworkDueDateInput"
                  type="datetime-local"
                  name="dueDate"
                  value={editHomework.dueDate}
                  onChange={(e) =>
                    setEditHomework((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  step="900"
                  disabled={isSubmitting}
                />
                <small className="helper-text">
                  Homework submissions close at this time.
                </small>
              </div>

              {/* Homework Status */}
              <div className="form-group">
                <label>
                  <i
                    className="fa-solid fa-check-circle"
                    style={{
                      marginRight: "0.4rem",
                      color: "var(--color-1)",
                    }}
                  />
                  Mark Ready
                </label>
                <label className="solution-toggle">
                  <input
                    type="checkbox"
                    name="editHomeworkStatus"
                    checked={editHomework.status === "Ready"}
                    onChange={(e) => {
                      const newStatus = e.target.checked ? "Ready" : "Review";
                      setEditHomework((prev) => ({
                        ...prev,
                        status: newStatus,
                      }));
                    }}
                    disabled={isSubmitting}
                  />
                  <span className="toggle-track">
                    <span className="toggle-indicator"></span>
                  </span>
                  <span className="toggle-label-text">
                    {editHomework.status === "Ready" ? "Ready" : "Review"}
                  </span>
                </label>
                <small className="helper-text" style={{ color: "#666" }}>
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ marginRight: "0.3rem" }}
                  />
                  {editHomework.status === "Ready"
                    ? "Homework is ready and visible to students."
                    : "Homework is in review and currently hidden."}
                </small>
              </div>

              {/* Submit Button */}
              {!isSubmitting ? (
                <button
                  type="submit"
                  className="btn-submit"
                  style={{ marginTop: "1.5rem" }}
                >
                  <i className="fa-solid fa-floppy-disk"></i> Update Homework
                </button>
              ) : (
                <Loader fontSize="50px" />
              )}
            </form>
          </Modal>

          {/* Preview Text Modal */}
          <Modal
            isOpen={isPreviewTextOpen}
            onClose={() => setIsPreviewTextOpen(false)}
          >
            <h2>Rubric Text</h2>
            <div className="rubric-text-preview">
              <p>{previewText}</p>
            </div>
          </Modal>

          {/* Grading Type Selection Modal */}
          <Modal
            isOpen={isGradingTypeModalOpen}
            onClose={() => setIsGradingTypeModalOpen(false)}
          >
            <div className="gts-header">
              <h2>
                <i className="fa-solid fa-clipboard-list"></i>
                Choose Grading Type
              </h2>
              <p className="modal-description">
                Select how you want to grade this test.
              </p>
            </div>
            <div className="gts-options">
              {/* AI Grading */}
              <button
                type="button"
                className="gts-option gts-option--ai"
                onClick={() => {
                  setNewTest((prev) => ({
                    ...prev,
                    isManualEntryQuestions: false,
                    isOnlineExamination: false,
                    manualEntryQuestions: [],
                    onlineExamQuestions: [],
                  }));
                  setIsGradingTypeModalOpen(false);
                  setIsCreateModalOpen(true);
                }}
              >
                <div className="gts-option-icon">
                  <i className="fa-solid fa-robot"></i>
                </div>
                <div className="gts-option-content">
                  <div className="gts-option-title">AI Grading</div>
                  <div className="gts-option-desc">
                    Upload test papers and answer sheets. AI automatically
                    grades student responses with detailed feedback.
                  </div>
                </div>
                <i className="fa-solid fa-chevron-right gts-option-arrow"></i>
              </button>

              {/* Manual Grading */}
              {instituteData?.isManualGradingAllowed && (
                <button
                  type="button"
                  className="gts-option gts-option--manual"
                  onClick={() => {
                    setNewTest((prev) => ({
                      ...prev,
                      isManualEntryQuestions: true,
                      isOnlineExamination: false,
                      onlineExamQuestions: [],
                    }));
                    if (courseOutcomes.length === 0) fetchCourseOutcomes();
                    setIsGradingTypeModalOpen(false);
                    setIsCreateModalOpen(true);
                  }}
                >
                  <div className="gts-option-icon">
                    <i className="fa-solid fa-pen-to-square"></i>
                  </div>
                  <div className="gts-option-content">
                    <div className="gts-option-title">Manual Grading</div>
                    <div className="gts-option-desc">
                      {!isK12School && (
                        <span>
                          Define tasks with max scores and course outcomes.
                        </span>
                      )}{" "}
                      Enter scores manually for each student.
                    </div>
                  </div>
                  <i className="fa-solid fa-chevron-right gts-option-arrow"></i>
                </button>
              )}

              {/* Online Exam */}
              {instituteData?.isOnlineExamAllowed && (
                <button
                  type="button"
                  className="gts-option gts-option--online"
                  onClick={() => {
                    setNewTest((prev) => ({
                      ...prev,
                      isManualEntryQuestions: false,
                      isOnlineExamination: true,
                      manualEntryQuestions: [],
                    }));
                    if (courseOutcomes.length === 0) fetchCourseOutcomes();
                    if (availableQuestionBanks.length === 0)
                      fetchQuestionBanks();
                    if (omrTemplateOptions.length === 0) fetchOmrTemplateLOVs();
                    setIsGradingTypeModalOpen(false);
                    setIsCreateModalOpen(true);
                  }}
                >
                  <div className="gts-option-icon">
                    <i className="fa-solid fa-laptop"></i>
                  </div>
                  <div className="gts-option-content">
                    <div className="gts-option-title">Objective Exam</div>
                    <div className="gts-option-desc">
                      Create objective exams (MCQ, Fill in the Blanks,
                      Numerical). Students take them on their devices with
                      auto-grading.
                    </div>
                  </div>
                  <i className="fa-solid fa-chevron-right gts-option-arrow"></i>
                </button>
              )}
            </div>
          </Modal>

          {/* MCQ Question Picker Modal */}
          {renderMcqPickerModal()}

          {/* Question Diagram Editor Modal */}
          <QuestionDiagramResizeModal
            isOpen={isCropModalOpen}
            onClose={() => {
              setIsCropModalOpen(false);
              setCropQuestion(null);
            }}
            question={cropQuestion}
            serverUrl={serverUrl}
            onSaveSuccess={handleDiagramSaveSuccess}
          />

          {/* Custom Question Modal */}
          {renderCustomQuestionModal(mcqPickerFor === "edit")}

          {/* Marketing-Focused Enable Feature Modal for Locked Capabilities */}
          <EnableFeatureModal
            isOpen={isEnableFeatureModalOpen}
            onClose={() => setIsEnableFeatureModalOpen(false)}
            {...COMPETITIVE_EXAM_LIBRARY_FEATURE}
            instituteName={
              instituteData?.name ||
              user?.institute?.name ||
              user?.instituteName ||
              "Our Institute"
            }
            instituteCode={
              instituteData?.code ||
              user?.institute?.code ||
              user?.instituteCode ||
              ""
            }
            userName={
              user?.name ||
              user?.username ||
              `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
              "Instructor"
            }
            userEmail={user?.email || ""}
            userRole={user?.role || role || "instructor"}
            whatsappNumber="919381369640"
            supportEmail="support@grademe-ai.com"
          />

          {/* ── Library Bank Picker Modal ── */}
          <Modal
            isOpen={isLibraryBankModalOpen}
            onClose={() => {
              setIsLibraryBankModalOpen(false);
              setLibraryQuestionsLoaded(false);
              setAllLibraries([]);
              setLibraryMetadataCache({});
              setFetchingMetadataIds(new Set());
              setModalStandardizedTestId(null);
              setLibrarySelectedQuestions(new Map());
              setLibraryActiveSubject(null);
              setLibraryFilters({ topic: [], subTopic: [], type: [], difficulty: [] });
              setLibrarySearchTerm("");
              setLibraryRandomCount("");
              setLastPickedRandomCount(null);
              setLibraryRandomSortedPool(null);
            }}
            maxWidth="100%"
          >
            <div className="oeb-generate-modal" style={{ padding: "0 0.5rem" }}>
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1.25rem 1.5rem",
                  background:
                    "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                  borderRadius: "14px",
                  marginBottom: "1.5rem",
                  border: "1px solid #ddd6fe",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    flexShrink: 0,
                    background:
                      "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(109,40,217,0.35)",
                  }}
                >
                  <i
                    className="fa-solid fa-book-open"
                    style={{ color: "white", fontSize: "1.2rem" }}
                  ></i>
                </div>
                <div style={{ flex: 1 }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    Library Bank
                  </h2>
                  <p
                    style={{
                      margin: "0.2rem 0 0",
                      fontSize: "0.88rem",
                      color: "#7c3aed",
                    }}
                  >
                    Browse and select questions from the shared question
                    library.
                  </p>
                </div>
                {librarySelectedQuestions.size > 0 && (
                  <div
                    style={{
                      background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                      color: "white",
                      borderRadius: "20px",
                      padding: "0.35rem 1rem",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(109,40,217,0.3)",
                    }}
                  >
                    {librarySelectedQuestions.size} selected
                  </div>
                )}
              </div>

              {/* Exam Template Dropdown */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "0.9rem 1.25rem",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  marginBottom: "1.5rem",
                  width: "fit-content",
                }}
              >
                <label
                  style={{
                    fontSize: "0.88rem",
                    fontWeight: 700,
                    color: "#475569",
                    whiteSpace: "nowrap",
                    margin: 0,
                  }}
                >
                  <i
                    className="fa-solid fa-graduation-cap"
                    style={{ marginRight: "0.45rem", color: "#8b5cf6" }}
                  />
                  Exam Template
                </label>
                <Select
                  menuPosition="fixed"
                  options={filteredStandardizedTestOptions}
                  value={
                    filteredStandardizedTestOptions.find(
                      (opt) => modalStandardizedTestId === opt.value,
                    ) || null
                  }
                  onChange={(selected) => {
                    const testId = selected ? selected.value : null;
                    setModalStandardizedTestId(testId);
                    if (testId) {
                      fetchAllLibraryQuestions(libraryExamFor, testId);
                    } else {
                      setLibraryQuestionsLoaded(false);
                      setAllLibraries([]);
                      setLibraryMetadataCache({});
                      setLibrarySelectedQuestions(new Map());
                      setLibraryActiveSubject(null);
                      setLibraryFilters({
                        topic: [],
                        subTopic: [],
                        type: [],
                        difficulty: [],
                      });
                      setLibrarySearchTerm("");
                    }
                  }}
                  isClearable
                  placeholder="Select an exam template to load its question libraries…"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      borderColor: "#cbd5e1",
                      minHeight: "40px",
                      boxShadow: "none",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? "#8b5cf6"
                        : state.isFocused
                          ? "#f5f3ff"
                          : "white",
                      color: state.isSelected ? "white" : "#334155",
                    }),
                  }}
                />
              </div>

              {/* Loading spinner or Main content */}
              {isFetchingLibraryQuestions ||
                fetchingMetadataIds.has("global") ||
                (modalStandardizedTestId &&
                  (!libraryMetadataCache["global"] ||
                    !libraryMetadataCache["global"].subjects ||
                    libraryMetadataCache["global"].subjects.length === 0)) ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "250px",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Loader
                    style={{ left: "auto", top: "auto", position: "static" }}
                  />
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "0.95rem",
                      display: "block",
                      marginTop: "1rem",
                      fontWeight: "600",
                    }}
                  >
                    Loading library & subjects...
                  </span>
                </div>
              ) : (
                <>
                  {libraryQuestionsLoaded ? (
                    (() => {
                      // Derive all questions and subjects from the global cache
                      const globalCache = libraryMetadataCache["global"] || {};
                      const allQuestions = globalCache.questions || [];
                      const allSubjects = [
                        ...new Set(
                          allQuestions.map((q) => q.subject).filter(Boolean),
                        ),
                      ].sort();

                      // Auto-select first subject if none active
                      const activeSubject =
                        libraryActiveSubject ||
                        (allSubjects.length > 0 ? allSubjects[0] : null);

                      // Filter questions by active subject
                      const subjectQuestions = allQuestions.filter(
                        (q) => q.subject === activeSubject,
                      );

                      // Derive available filter options from subject-filtered questions
                      const availableTopics = [
                        ...new Set(
                          subjectQuestions.map((q) => q.topic).filter(Boolean),
                        ),
                      ].sort();
                      const availableTypes = [
                        ...new Set(
                          subjectQuestions.map((q) => q.type).filter(Boolean),
                        ),
                      ].sort();
                      const availableDifficulties = [
                        ...new Set(
                          subjectQuestions
                            .map((q) => q.difficulty)
                            .filter(Boolean),
                        ),
                      ].sort();
                      const availableSubtopics = [
                        ...new Set(
                          libraryTopicQuestions
                            .map((q) => q.subTopic)
                            .filter(Boolean),
                        ),
                      ].sort();

                      // Questions are now filtered and paginated by backend
                      const filteredQuestions = libraryTopicQuestions;

                      // Count selected per subject for badges
                      const selectedBySubject = {};
                      librarySelectedQuestions.forEach((q) => {
                        const sub = q.subject || "Other";
                        selectedBySubject[sub] =
                          (selectedBySubject[sub] || 0) + 1;
                      });

                      // Number of visible questions selected
                      const visibleSelectedCount = filteredQuestions.filter(
                        (q) => librarySelectedQuestions.has(q._id),
                      ).length;

                      // Check if all visible are selected
                      const allVisibleSelected =
                        filteredQuestions.length > 0 &&
                        visibleSelectedCount === filteredQuestions.length;

                      // Check if some (partial) visible are selected
                      const someVisibleSelected =
                        visibleSelectedCount > 0 && !allVisibleSelected;

                      // Total selected in current subject
                      const currentSubjectSelectedCount =
                        selectedBySubject[activeSubject] || 0;

                      // Are all questions in this topic selected?
                      const isAllTopicSelected =
                        libraryTopicTotalCount > 0 &&
                        currentSubjectSelectedCount >= libraryTopicTotalCount;

                      // Auto-picked questions count in current selection
                      const autoPickedCountInTopic = Array.from(
                        libraryAutoPickedIds,
                      ).filter((id) => librarySelectedQuestions.has(id)).length;

                      return (
                        <>
                          {/* Subject Tabs */}
                          <div
                            style={{
                              display: "flex",
                              gap: "0",
                              borderBottom: "2px solid #e2e8f0",
                              marginBottom: "1.25rem",
                              paddingBottom: "0",
                            }}
                          >
                            {allSubjects.map((subject) => {
                              const isActive = subject === activeSubject;
                              const subjectTotal = allQuestions.filter(
                                (q) => q.subject === subject,
                              ).length;
                              const subjectSelected =
                                selectedBySubject[subject] || 0;
                              return (
                                <button
                                  key={subject}
                                  type="button"
                                  onClick={() => {
                                    const currentSubj = activeSubject;

                                    setLibrarySubjectStates((prev) => ({
                                      ...prev,
                                      [currentSubj]: {
                                        filters: libraryFilters,
                                        searchTerm: librarySearchTerm,
                                        page: libraryTopicPage,
                                        questions: libraryTopicQuestions,
                                        totalPages: libraryTopicTotalPages,
                                        totalCount: libraryTopicTotalCount,
                                      },
                                    }));

                                    const saved = librarySubjectStates[subject];
                                    if (saved) {
                                      setLibraryFilters(saved.filters);
                                      setLibrarySearchTerm(saved.searchTerm);
                                      setLibraryTopicPage(saved.page);
                                      setLibraryTopicQuestions(saved.questions);
                                      setLibraryTopicTotalPages(saved.totalPages);
                                      setLibraryTopicTotalCount(saved.totalCount);
                                    } else {
                                      setLibraryFilters({
                                        topic: [],
                                        subTopic: [],
                                        type: [],
                                        difficulty: [],
                                      });
                                      setLibrarySearchTerm("");
                                      setLibraryTopicPage(1);
                                      setLibraryTopicQuestions([]);
                                      setLibraryTopicTotalPages(1);
                                      setLibraryTopicTotalCount(0);
                                      setLibraryRandomCount("");
                                    }
                                    setLibraryActiveSubject(subject);
                                  }}
                                  style={{
                                    padding: "0.7rem 1.25rem",
                                    border: "none",
                                    borderBottom: isActive
                                      ? "3px solid #8b5cf6"
                                      : "3px solid transparent",
                                    background: isActive
                                      ? "linear-gradient(180deg, #f5f3ff 0%, white 100%)"
                                      : "transparent",
                                    color: isActive ? "#7c3aed" : "#64748b",
                                    fontWeight: isActive ? 700 : 500,
                                    fontSize: "0.9rem",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    transition: "all 0.2s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    position: "relative",
                                    marginBottom: "-2px",
                                  }}
                                >
                                  {subject}
                                  <span
                                    style={{
                                      fontSize: "0.72rem",
                                      background: isActive
                                        ? "#ede9fe"
                                        : "#f1f5f9",
                                      color: isActive ? "#7c3aed" : "#94a3b8",
                                      borderRadius: "10px",
                                      padding: "1px 7px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {subjectTotal}
                                  </span>
                                  {subjectSelected > 0 && (
                                    <span
                                      style={{
                                        fontSize: "0.65rem",
                                        background:
                                          "linear-gradient(135deg, #10b981, #059669)",
                                        color: "white",
                                        borderRadius: "10px",
                                        padding: "1px 6px",
                                        fontWeight: 700,
                                        minWidth: "16px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {subjectSelected}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          {/* Filters Row */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr 1fr 1.2fr",
                              gap: "0.75rem",
                              marginBottom: "1rem",
                              padding: "0.75rem 1rem",
                              background: "#f8fafc",
                              borderRadius: "10px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  marginBottom: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Topic
                              </label>
                              <Select
                                menuPosition="fixed"
                                isMulti
                                options={availableTopics.map((t) => ({
                                  value: t,
                                  label: t,
                                }))}
                                value={libraryFilters.topic.map((t) => ({
                                  value: t,
                                  label: t,
                                }))}
                                onChange={(selected) => {
                                  setLibraryFilters((prev) => ({
                                    ...prev,
                                    topic: selected
                                      ? selected.map((o) => o.value)
                                      : [],
                                  }));
                                  setLibraryTopicPage(1);
                                }}
                                placeholder="All topics..."
                                isClearable
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    borderRadius: "8px",
                                    borderColor: "#cbd5e1",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                  }),
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  marginBottom: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Subtopic
                              </label>
                              <Select
                                menuPosition="fixed"
                                isMulti
                                options={availableSubtopics.map((t) => ({
                                  value: t,
                                  label: t,
                                }))}
                                value={libraryFilters.subTopic.map((t) => ({
                                  value: t,
                                  label: t,
                                }))}
                                onChange={(selected) => {
                                  setLibraryFilters((prev) => ({
                                    ...prev,
                                    subTopic: selected
                                      ? selected.map((o) => o.value)
                                      : [],
                                  }));
                                  setLibraryTopicPage(1);
                                }}
                                placeholder="All subtopics..."
                                isClearable
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    borderRadius: "8px",
                                    borderColor: "#cbd5e1",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                  }),
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  marginBottom: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Type
                              </label>
                              <Select
                                menuPosition="fixed"
                                isMulti
                                options={availableTypes.map((t) => ({
                                  value: t,
                                  label: t,
                                }))}
                                value={libraryFilters.type.map((t) => ({
                                  value: t,
                                  label: t,
                                }))}
                                onChange={(selected) => {
                                  setLibraryFilters((prev) => ({
                                    ...prev,
                                    type: selected
                                      ? selected.map((o) => o.value)
                                      : [],
                                  }));
                                  setLibraryTopicPage(1);
                                }}
                                placeholder="All types..."
                                isClearable
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    borderRadius: "8px",
                                    borderColor: "#cbd5e1",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                  }),
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  marginBottom: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Difficulty
                              </label>
                              <Select
                                menuPosition="fixed"
                                isMulti
                                options={availableDifficulties.map((d) => ({
                                  value: d,
                                  label: d,
                                }))}
                                value={libraryFilters.difficulty.map((d) => ({
                                  value: d,
                                  label: d,
                                }))}
                                onChange={(selected) => {
                                  setLibraryFilters((prev) => ({
                                    ...prev,
                                    difficulty: selected
                                      ? selected.map((o) => o.value)
                                      : [],
                                  }));
                                  setLibraryTopicPage(1);
                                }}
                                placeholder="All levels..."
                                isClearable
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "36px",
                                    borderRadius: "8px",
                                    borderColor: "#cbd5e1",
                                    boxShadow: "none",
                                    fontSize: "0.85rem",
                                  }),
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  marginBottom: "0.25rem",
                                  display: "block",
                                }}
                              >
                                Search
                              </label>
                              <div style={{ position: "relative" }}>
                                <i
                                  className="fa-solid fa-search"
                                  style={{
                                    position: "absolute",
                                    left: "10px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#94a3b8",
                                    fontSize: "0.8rem",
                                    pointerEvents: "none",
                                  }}
                                />
                                <input
                                  type="text"
                                  value={librarySearchTerm}
                                  onChange={(e) =>
                                    setLibrarySearchTerm(e.target.value)
                                  }
                                  placeholder="Search questions..."
                                  style={{
                                    width: "100%",
                                    height: "36px",
                                    padding: "0 0.6rem 0 2rem",
                                    borderRadius: "8px",
                                    border: "1px solid #cbd5e1",
                                    fontSize: "0.85rem",
                                    outline: "none",
                                    transition: "border-color 0.2s",
                                    boxSizing: "border-box",
                                  }}
                                  onFocus={(e) =>
                                    (e.target.style.borderColor = "#8b5cf6")
                                  }
                                  onBlur={(e) =>
                                    (e.target.style.borderColor = "#cbd5e1")
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          {/* ── Selection & Auto-Pick Control Toolbar ── */}
                          <div className="lqp-selection-toolbar">
                            {/* Left: Master Page Checkbox & Auto-Pick Trigger */}
                            <div className="lqp-toolbar-left">
                              {/* Simple, Non-Tech Friendly Master Checkbox */}
                              <label
                                className={`lqp-master-checkbox-label ${allVisibleSelected ? "active" : ""}`}
                                title={
                                  allVisibleSelected
                                    ? "Deselect all questions on this page"
                                    : `Select all ${filteredQuestions.length} questions on this page`
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={allVisibleSelected}
                                  ref={(el) => {
                                    if (el) el.indeterminate = someVisibleSelected;
                                  }}
                                  onChange={() =>
                                    handleSelectAllLibraryQuestions(
                                      filteredQuestions,
                                      !allVisibleSelected,
                                    )
                                  }
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    accentColor: "#7c3aed",
                                    cursor: "pointer",
                                    margin: 0,
                                  }}
                                />
                                <span>
                                  {allVisibleSelected
                                    ? `All ${filteredQuestions.length} on page selected`
                                    : `Select all ${filteredQuestions.length} on this page`}
                                </span>
                              </label>

                              {/* Separator */}
                              <div style={{ width: "1px", height: "20px", background: "#e2e8f0" }} />

                              {/* Friendly Auto-Pick Trigger Button */}
                              <button
                                type="button"
                                className={`lqp-autopick-trigger ${isAutoPickPanelOpen ? "open" : ""}`}
                                onClick={() => setIsAutoPickPanelOpen((prev) => !prev)}
                                title="Automatically choose questions for your test"
                              >
                                <i
                                  className="fa-solid fa-wand-magic-sparkles"
                                  style={{
                                    color: isAutoPickPanelOpen ? "#ffffff" : "#7c3aed",
                                    fontSize: "0.9rem",
                                  }}
                                />
                                <span>Auto-Pick Questions for me</span>
                                {autoPickedCountInTopic > 0 && (
                                  <span
                                    className="lqp-autopick-badge-pill"
                                    style={{
                                      background: isAutoPickPanelOpen ? "rgba(255,255,255,0.25)" : "#ede9fe",
                                      color: isAutoPickPanelOpen ? "#ffffff" : "#6d28d9",
                                    }}
                                  >
                                    {autoPickedCountInTopic} Picked
                                  </span>
                                )}
                                <i
                                  className={`fa-solid fa-chevron-${isAutoPickPanelOpen ? "up" : "down"}`}
                                  style={{
                                    fontSize: "0.65rem",
                                    marginLeft: "2px",
                                    opacity: 0.8,
                                  }}
                                />
                              </button>
                            </div>

                            {/* Right: Live Selection Status & Pagination Info */}
                            <div className="lqp-toolbar-right">
                              {currentSubjectSelectedCount > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.45rem",
                                    background: "#f5f3ff",
                                    border: "1px solid #ddd6fe",
                                    borderRadius: "8px",
                                    padding: "3px 10px",
                                    fontSize: "0.82rem",
                                    fontWeight: 600,
                                    color: "#6d28d9",
                                  }}
                                >
                                  <i className="fa-solid fa-circle-check" style={{ fontSize: "0.8rem" }} />
                                  <span>{currentSubjectSelectedCount} Selected</span>
                                  <button
                                    type="button"
                                    onClick={() => handleSelectAllTopicQuestions(false)}
                                    title="Clear all selections for this topic"
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: "#94a3b8",
                                      cursor: "pointer",
                                      padding: "0 2px",
                                      fontSize: "0.75rem",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                                  >
                                    <i className="fa-solid fa-xmark" />
                                  </button>
                                </div>
                              )}

                              <span
                                style={{
                                  fontSize: "0.82rem",
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                Page {libraryTopicPage} of {libraryTopicTotalPages} (Total{" "}
                                {libraryTopicTotalCount || 0})
                              </span>
                            </div>
                          </div>

                          {/* ── Expandable Smart Auto-Pick Panel ── */}
                          {isAutoPickPanelOpen && (
                            <div className="lqp-autopick-panel">
                              <div className="lqp-autopick-header">
                                <div>
                                  <h4 className="lqp-autopick-title">
                                    <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "#7c3aed" }} />
                                    Pick Questions for Me
                                  </h4>
                                  <p className="lqp-autopick-subtitle">
                                    Need questions quickly? Enter how many you need, and the system will automatically choose a random set from all{" "}
                                    <strong>{libraryTopicTotalCount || 0}</strong> questions in this topic.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className="lqp-autopick-close"
                                  onClick={() => setIsAutoPickPanelOpen(false)}
                                  title="Close panel"
                                >
                                  <i className="fa-solid fa-xmark" />
                                </button>
                              </div>

                              {/* Clean Question Count Input & Quick Presets */}
                              <div style={{ background: "#ffffff", padding: "1rem", borderRadius: "10px", border: "1px solid #f3e8ff", marginBottom: "0.85rem" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                                  <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#334155" }}>
                                    How many questions do you need?
                                  </label>
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <input
                                      type="number"
                                      min="1"
                                      max={libraryTopicTotalCount || 1}
                                      className="lqp-stepper-input"
                                      style={{ width: "85px", height: "40px", fontSize: "1.05rem", fontWeight: 700, borderRadius: "8px", textAlign: "center" }}
                                      value={libraryRandomCount}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || parseInt(val, 10) >= 0) {
                                          setLibraryRandomCount(val);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          if (libraryRandomCount && !isRandomSelecting) {
                                            handleRandomSelectLibraryQuestions(filteredQuestions);
                                          }
                                        }
                                      }}
                                      disabled={!libraryTopicTotalCount || isRandomSelecting}
                                    />
                                    <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "#475569" }}>
                                      questions
                                    </span>
                                  </div>
                                  <span style={{ fontSize: "0.82rem", color: "#64748b" }}>
                                    (out of <strong>{libraryTopicTotalCount || 0}</strong> available in this topic)
                                  </span>
                                </div>

                                {/* Common Exam Sizes (Quick Click) */}
                                {libraryTopicTotalCount > 5 && (
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#64748b" }}>
                                      Common test sizes:
                                    </span>
                                    {[10, 20, 25, 30, 50, 100]
                                      .filter((n) => n <= libraryTopicTotalCount)
                                      .map((n) => {
                                        const isSelected = parseInt(libraryRandomCount, 10) === n;
                                        return (
                                          <button
                                            key={n}
                                            type="button"
                                            onClick={() => setLibraryRandomCount(String(n))}
                                            style={{
                                              padding: "4px 12px",
                                              borderRadius: "16px",
                                              border: isSelected ? "1.5px solid #7c3aed" : "1px solid #cbd5e1",
                                              background: isSelected ? "#ede9fe" : "#f8fafc",
                                              color: isSelected ? "#6d28d9" : "#475569",
                                              fontWeight: 600,
                                              fontSize: "0.8rem",
                                              cursor: "pointer",
                                              transition: "all 0.15s ease",
                                            }}
                                          >
                                            {n} Qs
                                          </button>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>

                              {/* Action Buttons & Status */}
                              <div className="lqp-actions-row">
                                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                                  {(() => {
                                    const countNum = parseInt(libraryRandomCount, 10);
                                    const isAlreadyPicked =
                                      lastPickedRandomCount !== null &&
                                      String(libraryRandomCount).trim() ===
                                      String(lastPickedRandomCount).trim() &&
                                      autoPickedCountInTopic > 0;

                                    const isBtnDisabled =
                                      !libraryTopicTotalCount ||
                                      libraryTopicTotalCount === 0 ||
                                      !countNum ||
                                      countNum <= 0 ||
                                      countNum > libraryTopicTotalCount ||
                                      isRandomSelecting;

                                    if (isAlreadyPicked) {
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            className="lqp-btn-reshuffle"
                                            onClick={() =>
                                              handleRandomSelectLibraryQuestions(
                                                filteredQuestions,
                                                null,
                                                true,
                                              )
                                            }
                                            disabled={isRandomSelecting}
                                            title="Pick a different random set of questions"
                                          >
                                            {isRandomSelecting ? (
                                              <i className="fa-solid fa-spinner fa-spin" />
                                            ) : (
                                              <i className="fa-solid fa-rotate" />
                                            )}
                                            <span>Pick a Different {countNum} Questions</span>
                                          </button>
                                          <button
                                            type="button"
                                            className="lqp-btn-clear-pick"
                                            onClick={handleClearAutoPicked}
                                            title="Clear picked questions"
                                          >
                                            <i className="fa-solid fa-xmark" />
                                            Clear Selection
                                          </button>
                                        </>
                                      );
                                    }

                                    return (
                                      <>
                                        <button
                                          type="button"
                                          className="lqp-btn-pick-primary"
                                          onClick={() => handleRandomSelectLibraryQuestions(filteredQuestions)}
                                          disabled={isBtnDisabled}
                                        >
                                          {isRandomSelecting ? (
                                            <>
                                              <i className="fa-solid fa-spinner fa-spin" />
                                              <span>Picking {countNum || ""} questions...</span>
                                            </>
                                          ) : (
                                            <>
                                              <i className="fa-solid fa-wand-magic-sparkles" />
                                              <span>
                                                {countNum > 0
                                                  ? `Choose ${countNum} Questions for Me`
                                                  : "Choose Questions for Me"}
                                              </span>
                                            </>
                                          )}
                                        </button>
                                        {autoPickedCountInTopic > 0 && (
                                          <button
                                            type="button"
                                            className="lqp-btn-clear-pick"
                                            onClick={handleClearAutoPicked}
                                          >
                                            <i className="fa-solid fa-xmark" />
                                            Clear Selection ({autoPickedCountInTopic})
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>

                                {autoPickedCountInTopic > 0 && (
                                  <span className="lqp-status-feedback">
                                    <i className="fa-solid fa-circle-check" />
                                    {autoPickedCountInTopic} question{autoPickedCountInTopic > 1 ? "s" : ""} selected for your test
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* ── Contextual Bulk Page Banner (Gmail style) ── */}
                          {allVisibleSelected && libraryTopicTotalCount > filteredQuestions.length && !isAllTopicSelected && (
                            <div className="lqp-context-banner">
                              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <i className="fa-solid fa-circle-info" style={{ color: "#16a34a", fontSize: "1rem" }} />
                                <span>
                                  All <strong>{filteredQuestions.length}</strong> questions on this page are selected.
                                </span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                <button
                                  type="button"
                                  className="lqp-context-banner-action"
                                  onClick={() => handleSelectAllTopicQuestions(true)}
                                  disabled={isSelectingAllTopic}
                                >
                                  {isSelectingAllTopic ? (
                                    <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: "4px" }} />
                                  ) : null}
                                  Select all {libraryTopicTotalCount} questions in this topic
                                </button>
                                <button
                                  type="button"
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#6b7280",
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() => handleSelectAllLibraryQuestions(filteredQuestions, false)}
                                >
                                  Clear page
                                </button>
                              </div>
                            </div>
                          )}

                          {isAllTopicSelected && (
                            <div className="lqp-context-banner" style={{ background: "#ecfdf5", borderColor: "#a7f3d0" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <i className="fa-solid fa-circle-check" style={{ color: "#059669", fontSize: "1rem" }} />
                                <span>
                                  All <strong>{libraryTopicTotalCount}</strong> questions in this topic are selected for your test.
                                </span>
                              </div>
                              <button
                                type="button"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "#b91c1c",
                                  fontSize: "0.82rem",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontWeight: 600,
                                }}
                                onClick={() => handleSelectAllTopicQuestions(false)}
                              >
                                Clear topic selection
                              </button>
                            </div>
                          )}

                          {/* Questions List */}
                          <div
                            style={{
                              maxHeight: "400px",
                              overflowY: "auto",
                              border: "1px solid #e2e8f0",
                              borderRadius: "10px",
                              background: "white",
                            }}
                          >
                            {libraryFilters.topic.length === 0 ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "4rem 2rem",
                                  color: "#94a3b8",
                                }}
                              >
                                <i
                                  className="fa-solid fa-list-ul"
                                  style={{
                                    fontSize: "2.5rem",
                                    marginBottom: "1rem",
                                    display: "block",
                                    color: "#cbd5e1",
                                  }}
                                />
                                <p
                                  style={{
                                    fontSize: "1.05rem",
                                    fontWeight: 600,
                                    color: "#475569",
                                    margin: "0 0 0.4rem",
                                  }}
                                >
                                  Select a Topic
                                </p>
                                <p style={{ fontSize: "0.85rem", margin: 0 }}>
                                  Please select one or more topics from the
                                  dropdown above to view questions.
                                </p>
                              </div>
                            ) : isFetchingTopicQuestions ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "4rem 2rem",
                                }}
                              >
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    margin: "0 auto 1rem",
                                    border: "3px solid #f3f4f6",
                                    borderTopColor: "#8b5cf6",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                  }}
                                />
                                <p
                                  style={{ color: "#64748b", fontWeight: 500 }}
                                >
                                  Loading questions...
                                </p>
                              </div>
                            ) : filteredQuestions.length === 0 ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "3rem 2rem",
                                  color: "#94a3b8",
                                }}
                              >
                                <i
                                  className="fa-solid fa-filter-circle-xmark"
                                  style={{
                                    fontSize: "2rem",
                                    marginBottom: "0.75rem",
                                    display: "block",
                                    color: "#cbd5e1",
                                  }}
                                />
                                <p
                                  style={{
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                    color: "#64748b",
                                    margin: "0 0 0.3rem",
                                  }}
                                >
                                  No questions match the current filters
                                </p>
                                <p style={{ fontSize: "0.82rem", margin: 0 }}>
                                  Try adjusting your filters or search term
                                </p>
                              </div>
                            ) : (
                              filteredQuestions.map((q, idx) => {
                                const isSelected = librarySelectedQuestions.has(
                                  q._id,
                                );
                                const isAutoPicked = libraryAutoPickedIds.has(
                                  q._id,
                                );
                                const questionText =
                                  q.questionText || "No question text";
                                return (
                                  <div
                                    key={q._id || idx}
                                    onClick={() =>
                                      handleToggleLibraryQuestion(q)
                                    }
                                    className={`lqp-question-card ${isSelected
                                        ? isAutoPicked
                                          ? "lqp-question-card-autopicked"
                                          : "lqp-question-card-selected"
                                        : ""
                                      }`}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: "0.75rem",
                                      padding: "0.85rem 1rem",
                                      borderBottom:
                                        idx < filteredQuestions.length - 1
                                          ? "1px solid #f1f5f9"
                                          : "none",
                                      cursor: "pointer",
                                      background: isSelected
                                        ? isAutoPicked
                                          ? "linear-gradient(135deg, #fbf7ff 0%, #ffffff 100%)"
                                          : "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)"
                                        : "white",
                                      transition: "all 0.15s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected)
                                        e.currentTarget.style.background =
                                          "#fafafa";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        isSelected
                                          ? isAutoPicked
                                            ? "linear-gradient(135deg, #fbf7ff 0%, #ffffff 100%)"
                                            : "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)"
                                          : "white";
                                    }}
                                  >
                                    {/* Checkbox */}
                                    <div
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        borderRadius: "5px",
                                        flexShrink: 0,
                                        marginTop: "2px",
                                        border: isSelected
                                          ? "2px solid #8b5cf6"
                                          : "2px solid #cbd5e1",
                                        background: isSelected
                                          ? "#8b5cf6"
                                          : "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "all 0.15s ease",
                                      }}
                                    >
                                      {isSelected && (
                                        <i
                                          className="fa-solid fa-check"
                                          style={{
                                            color: "white",
                                            fontSize: "0.65rem",
                                          }}
                                        />
                                      )}
                                    </div>

                                    {/* Question Content */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontSize: "0.88rem",
                                          color: "#1e293b",
                                          lineHeight: "1.45",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        <LazyMarkdown>
                                          {formatLatexText(questionText)}
                                        </LazyMarkdown>
                                      </div>

                                      {(q.diagramFileURL || q.svgCode || q.imageURL) && (
                                        <div
                                          className="diagram-viewer-container"
                                          style={{
                                            marginTop: "0.5rem",
                                            marginBottom: "0.75rem",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "0.25rem",
                                          }}
                                        >
                                          <div
                                            style={{
                                              position: "relative",
                                              border: "1px solid #e2e8f0",
                                              borderRadius: "8px",
                                              overflow: "hidden",
                                              backgroundColor: "#f8fafc",
                                              maxWidth: "100%",
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                                              cursor: "pointer",
                                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (!q.svgCode && (q.diagramFileURL || q.imageURL))
                                                window.open(
                                                  q.diagramFileSignedURL || q.diagramFileURL || q.imageURL,
                                                  "_blank",
                                                );
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = "scale(1.02)";
                                              e.currentTarget.style.boxShadow =
                                                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = "scale(1)";
                                              e.currentTarget.style.boxShadow =
                                                "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
                                            }}
                                            title="Click to view full size image"
                                          >
                                            {q.svgCode ? (
                                              <>
                                                <style>{`
                                                  .diagram-viewer-container svg {
                                                    max-width: 100%;
                                                    height: auto;
                                                    max-height: 220px;
                                                  }
                                                `}</style>
                                                <div
                                                  dangerouslySetInnerHTML={{
                                                    __html: q.svgCode,
                                                  }}
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    maxWidth: "100%",
                                                    maxHeight: "220px",
                                                  }}
                                                />
                                              </>
                                            ) : (
                                              <img
                                                src={q.diagramFileSignedURL || q.diagramFileURL || q.imageURL}
                                                alt={`Diagram for Question`}
                                                style={{
                                                  maxHeight: "220px",
                                                  maxWidth: "100%",
                                                  objectFit: "contain",
                                                  display: "block",
                                                }}
                                              />
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {q.options &&
                                        q.options.length > 0 && (
                                          <div
                                            style={{
                                              marginTop: "1rem",
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "0.5rem",
                                            }}
                                          >
                                            {q.options.map((opt, i) => {
                                              const optText =
                                                typeof opt === "string"
                                                  ? opt
                                                  : opt?.text || "";
                                              return (
                                                <div
                                                  key={i}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: "0.5rem",
                                                    padding: "0.5rem",
                                                    background: "#f8fafc",
                                                    borderRadius: "6px",
                                                    border:
                                                      q.correctOptions?.includes(
                                                        i.toString(),
                                                      ) ||
                                                        q.correctAnswer ===
                                                        i.toString()
                                                        ? "1px solid #86efac"
                                                        : "1px solid #e2e8f0",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      fontWeight: 600,
                                                      color: "#64748b",
                                                      minWidth: "20px",
                                                    }}
                                                  >
                                                    {String.fromCharCode(
                                                      65 + i,
                                                    )}
                                                    .
                                                  </span>
                                                  <div
                                                    style={{
                                                      fontSize: "0.85rem",
                                                      flex: 1,
                                                    }}
                                                  >
                                                    <LazyMarkdown>
                                                      {formatLatexText(optText)}
                                                    </LazyMarkdown>
                                                  </div>
                                                  {(q.correctOptions?.includes(
                                                    i.toString(),
                                                  ) ||
                                                    q.correctAnswer ===
                                                    i.toString()) && (
                                                      <i
                                                        className="fa-solid fa-check"
                                                        style={{
                                                          color: "#22c55e",
                                                          fontSize: "0.8rem",
                                                          marginTop: "2px",
                                                        }}
                                                      />
                                                    )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}

                                      {/* Tags */}
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "0.4rem",
                                          marginTop: "0.45rem",
                                        }}
                                      >
                                        {q.topic && (
                                          <span
                                            style={{
                                              fontSize: "0.7rem",
                                              padding: "2px 8px",
                                              borderRadius: "6px",
                                              background: "#eff6ff",
                                              color: "#3b82f6",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {q.topic}
                                          </span>
                                        )}
                                        {q.subTopic && (
                                          <span
                                            style={{
                                              fontSize: "0.7rem",
                                              padding: "2px 8px",
                                              borderRadius: "6px",
                                              background: "#f3e8ff",
                                              color: "#9333ea",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {q.subTopic}
                                          </span>
                                        )}
                                        {q.type && (
                                          <span
                                            style={{
                                              fontSize: "0.7rem",
                                              padding: "2px 8px",
                                              borderRadius: "6px",
                                              background: "#fef3c7",
                                              color: "#d97706",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {q.type}
                                          </span>
                                        )}
                                        {q.difficulty && (
                                          <span
                                            style={{
                                              fontSize: "0.7rem",
                                              padding: "2px 8px",
                                              borderRadius: "6px",
                                              background:
                                                q.difficulty.toLowerCase() ===
                                                  "easy"
                                                  ? "#dcfce7"
                                                  : q.difficulty.toLowerCase() ===
                                                    "hard"
                                                    ? "#fee2e2"
                                                    : "#fef9c3",
                                              color:
                                                q.difficulty.toLowerCase() ===
                                                  "easy"
                                                  ? "#16a34a"
                                                  : q.difficulty.toLowerCase() ===
                                                    "hard"
                                                    ? "#dc2626"
                                                    : "#ca8a04",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {q.difficulty}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Right side actions */}
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-end",
                                        gap: "0.5rem",
                                      }}
                                    >
                                      {isAutoPicked ? (
                                        <span
                                          className="lqp-autopicked-tag"
                                          title="Randomly selected via Auto-Pick"
                                        >
                                          <i className="fa-solid fa-dice" /> Auto-Picked
                                        </span>
                                      ) : isSelected ? (
                                        <div
                                          style={{
                                            fontSize: "0.7rem",
                                            color: "#7c3aed",
                                            fontWeight: 700,
                                            whiteSpace: "nowrap",
                                            marginTop: "2px",
                                          }}
                                        >
                                          <i
                                            className="fa-solid fa-circle-check"
                                            style={{ marginRight: "3px" }}
                                          />
                                          Selected
                                        </div>
                                      ) : null}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setLibrarySolutionModalData(q);
                                        }}
                                        style={{
                                          background: "#f1f5f9",
                                          border: "1px solid #cbd5e1",
                                          cursor: "pointer",
                                          color: "#475569",
                                          padding: "0.3rem 0.6rem",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          borderRadius: "6px",
                                          transition: "all 0.2s",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = "#e2e8f0";
                                          e.currentTarget.style.color = "#1e293b";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = "#f1f5f9";
                                          e.currentTarget.style.color = "#475569";
                                        }}
                                      >
                                        <i className="fa-solid fa-eye"></i> View Solution
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Pagination UI */}
                          {libraryTopicTotalPages > 1 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: "1rem",
                                padding: "0 0.5rem",
                              }}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setLibraryTopicPage((p) => Math.max(1, p - 1))
                                }
                                disabled={libraryTopicPage === 1}
                                style={{
                                  padding: "0.5rem 1rem",
                                  borderRadius: "8px",
                                  border: "1px solid #cbd5e1",
                                  background:
                                    libraryTopicPage === 1
                                      ? "#f1f5f9"
                                      : "white",
                                  color:
                                    libraryTopicPage === 1
                                      ? "#94a3b8"
                                      : "#475569",
                                  cursor:
                                    libraryTopicPage === 1
                                      ? "not-allowed"
                                      : "pointer",
                                  fontSize: "0.85rem",
                                  fontWeight: 600,
                                  transition: "all 0.2s",
                                }}
                              >
                                Previous
                              </button>
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                Page {libraryTopicPage} of{" "}
                                {libraryTopicTotalPages}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setLibraryTopicPage((p) =>
                                    Math.min(libraryTopicTotalPages, p + 1),
                                  )
                                }
                                disabled={
                                  libraryTopicPage === libraryTopicTotalPages
                                }
                                style={{
                                  padding: "0.5rem 1rem",
                                  borderRadius: "8px",
                                  border: "1px solid #cbd5e1",
                                  background:
                                    libraryTopicPage === libraryTopicTotalPages
                                      ? "#f1f5f9"
                                      : "white",
                                  color:
                                    libraryTopicPage === libraryTopicTotalPages
                                      ? "#94a3b8"
                                      : "#475569",
                                  cursor:
                                    libraryTopicPage === libraryTopicTotalPages
                                      ? "not-allowed"
                                      : "pointer",
                                  fontSize: "0.85rem",
                                  fontWeight: 600,
                                  transition: "all 0.2s",
                                }}
                              >
                                Next
                              </button>
                            </div>
                          )}

                          {/* ── Selection Summary & Footer ── */}
                          <div
                            style={{
                              marginTop: "1.25rem",
                              padding: "1rem 1.25rem",
                              background:
                                librarySelectedQuestions.size > 0
                                  ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)"
                                  : "#f8fafc",
                              borderRadius: "12px",
                              border: `1px solid ${librarySelectedQuestions.size > 0 ? "#ddd6fe" : "#e2e8f0"}`,
                            }}
                          >
                            {/* Per-subject breakdown */}
                            {librarySelectedQuestions.size > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                  marginBottom: "0.85rem",
                                }}
                              >
                                {Object.entries(selectedBySubject)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([sub, count]) => (
                                    <span
                                      key={sub}
                                      style={{
                                        fontSize: "0.78rem",
                                        padding: "3px 10px",
                                        borderRadius: "8px",
                                        background: "white",
                                        border: "1px solid #ddd6fe",
                                        color: "#6d28d9",
                                        fontWeight: 600,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.3rem",
                                      }}
                                    >
                                      {sub}
                                      <span
                                        style={{
                                          background: "#8b5cf6",
                                          color: "white",
                                          borderRadius: "50%",
                                          width: "18px",
                                          height: "18px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "0.68rem",
                                          fontWeight: 700,
                                        }}
                                      >
                                        {count}
                                      </span>
                                    </span>
                                  ))}
                              </div>
                            )}

                            {/* Action buttons */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 700,
                                  fontSize: "0.95rem",
                                  color:
                                    librarySelectedQuestions.size > 0
                                      ? "#6d28d9"
                                      : "#64748b",
                                }}
                              >
                                {librarySelectedQuestions.size > 0
                                  ? `${librarySelectedQuestions.size} question${librarySelectedQuestions.size > 1 ? "s" : ""} selected`
                                  : "No questions selected"}
                              </span>
                              <div style={{ display: "flex", gap: "0.75rem" }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsLibraryBankModalOpen(false);
                                    setLibraryQuestionsLoaded(false);
                                    setAllLibraries([]);
                                    setLibraryMetadataCache({});
                                    setFetchingMetadataIds(new Set());
                                    setModalStandardizedTestId(null);
                                    setLibrarySelectedQuestions(new Map());
                                    setLibraryActiveSubject(null);
                                    setLibraryFilters({
                                      topic: [],
                                      subTopic: [],
                                      type: [],
                                      difficulty: [],
                                    });
                                    setLibrarySearchTerm("");
                                    setLibraryRandomCount("");
                                    setLastPickedRandomCount(null);
                                    setLibraryRandomSortedPool(null);
                                  }}
                                  style={{
                                    padding: "0.5rem 1.5rem",
                                    background: "#f1f5f9",
                                    border: "1px solid #cbd5e1",
                                    color: "#475569",
                                    borderRadius: "8px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    fontSize: "0.88rem",
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn-submit"
                                  onClick={handleSaveLibrarySelections}
                                  disabled={librarySelectedQuestions.size === 0}
                                  style={{
                                    padding: "0.5rem 1.5rem",
                                    background:
                                      librarySelectedQuestions.size === 0
                                        ? "#94a3b8"
                                        : "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                                    border: "none",
                                    color: "white",
                                    borderRadius: "8px",
                                    fontWeight: "bold",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor:
                                      librarySelectedQuestions.size === 0
                                        ? "not-allowed"
                                        : "pointer",
                                    fontSize: "0.88rem",
                                    boxShadow:
                                      librarySelectedQuestions.size > 0
                                        ? "0 4px 12px rgba(109,40,217,0.3)"
                                        : "none",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <i className="fa-solid fa-plus-circle" />
                                  Save Questions
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "4rem 2rem",
                        color: "#64748b",
                      }}
                    >
                      <div
                        style={{
                          width: "72px",
                          height: "72px",
                          borderRadius: "20px",
                          background:
                            "linear-gradient(135deg, #f5f3ff, #ede9fe)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 1.25rem",
                          border: "1px solid #ddd6fe",
                        }}
                      >
                        <i
                          className="fa-solid fa-book-open"
                          style={{ fontSize: "2rem", color: "#8b5cf6" }}
                        />
                      </div>
                      <p
                        style={{
                          fontSize: "1.05rem",
                          fontWeight: 600,
                          color: "#334155",
                          margin: "0 0 0.4rem",
                        }}
                      >
                        Select an Exam Template to begin
                      </p>
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: "#94a3b8",
                          margin: 0,
                        }}
                      >
                        Choose a template above to load its associated question
                        libraries.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </Modal>

          {/* Solution Modal for Library Questions */}
          {librarySolutionModalData && (
            <Modal
              isOpen={true}
              onClose={() => setLibrarySolutionModalData(null)}
              title="View Solution"
              maxWidth="800px"
            >
              <div style={{ padding: "1.5rem", fontSize: "0.95rem", lineHeight: "1.6", color: "#334155" }}>
                <LazyMarkdown>
                  {formatLatexText(librarySolutionModalData._sourceSolution || librarySolutionModalData.solution || "No solution provided.")}
                </LazyMarkdown>
              </div>
            </Modal>
          )}
        </>
      )}

      {isDownloadModalOpen && (
        <Modal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            if (downloadModalAction === "navigate" && downloadModalTestId) {
              navigate(`/instructor/test-grading/${downloadModalTestId}`);
            }
          }}
          closeButton={false}
          title="Test Saved Successfully"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "1.5rem",
              gap: "1.5rem",
            }}
          >
            <div
              style={{
                width: "4rem",
                height: "4rem",
                backgroundColor: "#dcfce7",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#16a34a",
              }}
            >
              <svg
                style={{ width: "2rem", height: "2rem" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div style={{ textAlign: "center" }}>
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  color: "#1e293b",
                  marginBottom: "0.5rem",
                }}
              >
                Your Test Paper is Ready
              </h3>
              <p style={{ color: "#475569", fontSize: "0.875rem" }}>
                Your test has been saved and the test paper PDF is ready for
                download.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
                width: "100%",
                marginTop: "1.5rem",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "1rem",
                  width: "100%",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    if (generatedTestPaperBlob) {
                      const url = URL.createObjectURL(generatedTestPaperBlob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${downloadModalTestName || "Question_Paper"}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }
                  }}
                  style={{
                    flex: 1,
                    minWidth: "130px",
                    backgroundColor: "var(--color-1)",
                    color: "white",
                    fontWeight: "600",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.75rem",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <i className="fa-solid fa-download"></i> Test Paper
                </button>
                <button
                  onClick={() => {
                    if (generatedAnswerKeyBlob) {
                      const url = URL.createObjectURL(generatedAnswerKeyBlob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${downloadModalTestName || "Answer_Key"}_Answer_Key.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }
                  }}
                  style={{
                    flex: 1,
                    minWidth: "130px",
                    backgroundColor: "var(--color-2)",
                    color: "white",
                    fontWeight: "600",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.75rem",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <i className="fa-solid fa-download"></i> Answer Key
                </button>
                <button
                  onClick={() => {
                    if (generatedSolutionsBlob) {
                      const url = URL.createObjectURL(generatedSolutionsBlob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${downloadModalTestName || "Solutions"}_Solutions.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }
                  }}
                  style={{
                    flex: 1,
                    minWidth: "130px",
                    backgroundColor: "var(--color-3)",
                    color: "white",
                    fontWeight: "600",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.75rem",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <i className="fa-solid fa-download"></i> Solutions
                </button>
              </div>
              <button
                onClick={() => {
                  setIsDownloadModalOpen(false);
                  if (
                    downloadModalAction === "navigate" &&
                    downloadModalTestId
                  ) {
                    navigate(`/instructor/test-grading/${downloadModalTestId}`);
                  }
                }}
                style={{
                  minWidth: "150px",
                  backgroundColor: "var(--color-5)",
                  color: "#000",
                  fontWeight: "600",
                  padding: "0.75rem 2rem",
                  borderRadius: "0.75rem",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Continue <i className="fa-solid fa-arrow-right" style={{ marginLeft: "0.5rem" }}></i>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
});

ManageTestUI.propTypes = {
  classId: PropTypes.string.isRequired,
  classDetails: PropTypes.node,
};

export default ManageTestUI;
